// src/pages/Clients.js - Optimized version
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import BusinessIcon from '@mui/icons-material/Business';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import BarChartIcon from '@mui/icons-material/BarChart';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DateRangeIcon from '@mui/icons-material/DateRange';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoneyIcon from '@mui/icons-material/Money';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WarningIcon from '@mui/icons-material/Warning';
import FilterIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import RegionIcon from '@mui/icons-material/Public';
import StorageIcon from '@mui/icons-material/Storage';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import GetAppIcon from '@mui/icons-material/GetApp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import DevicesIcon from '@mui/icons-material/Devices';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import BugReportIcon from '@mui/icons-material/BugReport';

// Restore required MUI components
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputBase from '@mui/material/InputBase';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

// Fix component imports
import SearchFilterBar from '../components/common/SearchFilterBar';
import { Pie, Bar } from 'react-chartjs-2';

// Import required Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// Import custom hooks and components
import { useAppData, ACTIONS } from '../contexts/AppDataContext';
import PageHeader from '../components/common/PageHeader';
import LoadingOverlay from '../components/common/LoadingOverlay';
import ChartCard from '../components/common/ChartCard';
import api from '../services/api';
import { getFullClientName, dedupeClientsByCanonicalKey, isExcludedInternalClient, getCanonicalClientKey } from '../utils/clientNameMappings';
import { formatDataSize } from '../utils/formatDataSize';
import DateRangePicker from '../components/DateRangePicker';

// Import the PermissionWidget component
import { PermissionWidget, WIDGET_PERMISSIONS, useHasCostPermission } from '../utils/widgetPermissions';



// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

// Helper: returns true when a product entry should be hidden from
// product lists / counts (e.g. "UNKNOWNPRODUCT" / "unknown" / empty product names).
const isExcludedProduct = (product) => {
  const name = (product?.product || '').trim().toLowerCase();
  return name === '' || name === 'unknown' || name === 'unknownproduct';
};

const clientNamesMatchCanonical = (a, b) => {
  if (a == null || b == null) return false;
  return getCanonicalClientKey(a) === getCanonicalClientKey(b);
};

const sumByClientSizeForCanonicalName = (byClient, clientName) => {
  if (!byClient?.length || !clientName) return 0;
  const key = getCanonicalClientKey(clientName);
  return byClient
    .filter((c) => getCanonicalClientKey(c.client_name) === key)
    .reduce((s, c) => s + (Number(c.size_gb) || 0), 0);
};

const findFirstByClientForCanonical = (byClient, clientName) => {
  if (!byClient?.length || !clientName) return undefined;
  const key = getCanonicalClientKey(clientName);
  return byClient.find((c) => getCanonicalClientKey(c.client_name) === key);
};

// EmptyState component (inline definition if not imported)
const EmptyState = ({ title, message, icon: Icon = BusinessIcon, height = 200 }) => {
  const theme = useTheme();

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height={height}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '12px',
        boxShadow: `0 8px 16px ${alpha('#000', 0.05)}`,
        p: 3,
      }}
    >
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: alpha(theme.palette.primary.main, 0.7),
          mb: 2,
        }}
      >
        <Icon fontSize="large" />
      </Box>
      <Typography variant="h6" color="text.primary" fontWeight="medium" align="center">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, maxWidth: 400 }}>
        {message}
      </Typography>
    </Box>
  );
};

// Update date formatting function
const formatDateTime = (dateString, includeTime = false) => {
  if (!dateString) return "-";
  const date = new Date(dateString);

  // Format date in DD-MM-YYYY format
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  // Base format (DD-MM-YYYY)
  let formattedDate = `${day}-${month}-${year}`;

  // Add time if includeTime is true
  if (includeTime) {
    // Format time in HH:MM AM/PM
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');

    formattedDate += ` ${formattedHours}:${minutes} ${ampm}`;
  }

  return formattedDate;
};

export default function Clients() {
  // Define common parent patterns for consistent reference throughout the component
  const PARENT_PATTERNS = useMemo(() => ['DICT',], []);

  // Theme for styling
  const theme = useTheme();

  const location = useLocation();
  const navigate = useNavigate();

  // Import cost permission hook
  const hasCostPermission = useHasCostPermission();


  const getDatesInRange = useCallback((startDate, endDate) => {
    const dates = [];
    let currentDate = new Date(startDate);
    const lastDate = new Date(endDate);

    while (currentDate <= lastDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }, []);

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [region, setRegion] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');  // Add this line
  const [error, setError] = useState('');  // Add this line
  const [groupFilter, setGroupFilter] = useState('all'); // 'all', 'trend_micro', 'blue_ocean', 'tm_direct', 'tm_indirect', 'bo_direct', 'bo_indirect'
  const [engagementModelFilter, setEngagementModelFilter] = useState('all'); // 'all', 'MXDR', 'Third party', 'Both'
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [openLicenseDialog, setOpenLicenseDialog] = useState(false);
  const [openViewDetailsDialog, setOpenViewDetailsDialog] = useState(false);
  const [licenseData, setLicenseData] = useState({
    startDate: '',
    endDate: '',
    associatedLogData: '',
    licensePerDay: '',
    onlineRetention: '',
    offlineRetention: '',
    devicesToIntegrate: '',
    engagementType: '',
    engagementModel: ''
  });
  const [savingLicense, setSavingLicense] = useState(false);
  const [loadingLicense, setLoadingLicense] = useState(false);
  const [clientCostData, setClientCostData] = useState({
    rsyslog_size_gb: 0,
    cost_per_gb: 0,
    total_cost: 0,
    total_client_data_gb: 0,
    production_cost: 0,
    cost_details: {
      compute_cost: 0,
      boot_volume_cost: 0,
      block_volume_cost: 0
    },
    date: new Date().toISOString().split('T')[0]
  });
  const [loadingCostData, setLoadingCostData] = useState(false);
  const [selectedCostDate, setSelectedCostDate] = useState(
    new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
  );

  // Create state for date filter
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [loadingDateFilter, setLoadingDateFilter] = useState(false);
  // Date filter dialog has been removed in favor of inline date inputs

  const [loadingDownload, setLoadingDownload] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [clientLicenses, setClientLicenses] = useState({});
  const [loadingLicenses, setLoadingLicenses] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  // Add state for total RSYSLOG data and production cost
  const [totalRsyslogData, setTotalRsyslogData] = useState(0);
  const [totalProductionCost, setTotalProductionCost] = useState(0);
  // Add this near the top of the component, with other state variables
  const [totalProductionCostData, setTotalProductionCostData] = useState({
    production_cost: 0,
    rsyslog_size: 0,
    cost_per_gb: 0,
    cost_details: {
      compute_cost: 0,
      boot_volume_cost: 0,
      block_volume_cost: 0
    },
    region: "All Regions",
    date_range: {
      from: "",
      to: ""
    }
  });
  const [loadingTotalCosts, setLoadingTotalCosts] = useState(false);
  // Add state for overutilized filter
  const [showOverutilizedOnly, setShowOverutilizedOnly] = useState(false);
  const [showParentClientsOnly, setShowParentClientsOnly] = useState(true); // Default to parent view
  const [parentClientName, setParentClientName] = useState('');
  // Add state for license expiration filter
  const [showExpiringLicensesOnly, setShowExpiringLicensesOnly] = useState(false);
  // Add state for download confirmation dialog
  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);
  const [downloadType, setDownloadType] = useState('product'); // 'product' or 'client'
  const [downloadTarget, setDownloadTarget] = useState(null); // client name or null for all
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedLicenseRows, setExpandedLicenseRows] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);
  const [productDevicePopup, setProductDevicePopup] = useState({
    open: false,
    clientName: '',
    productName: '',
    deviceData: [],
    loading: false
  });

  const [dateRange, setDateRange] = useState({
    from: fromDate ? new Date(fromDate) : new Date(),
    to: toDate ? new Date(toDate) : new Date()
  });

  // Add debounce timer ref
  const debounceTimer = useRef(null);
  // Add a ref to track if license data has been loaded already
  const licensesLoadedRef = useRef(false);

  // Get data from context with destructuring for better performance
  const {
    clients,
    regions,
    clientLogs,
    loading,
    fetchRegions,
    fetchClients,
    fetchClientLogs,
    fetchClientProducts,
    refreshAllData,
    fetchProductDeviceIds
  } = useAppData();

  // Define fetchAllClientLicenses before it's used
  const fetchAllClientLicenses = useCallback(async () => {
    setLoadingLicenses(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (region) params.set('region', region);

      // Add date range if available
      if (fromDate && toDate) {
        params.set('from_date', fromDate);
        params.set('to_date', toDate);
      }

      // Fetch license data for all clients
      const response = await api.getWithAuth(`/api/clients/licenses?${params}`);

      if (response.data && response.data.client_licenses) {
        // Convert array to map for faster lookup
        const licenseMap = {};
        response.data.client_licenses.forEach(license => {
          licenseMap[license.client_name] = license;
        });

        setClientLicenses(licenseMap);
        return licenseMap; // Return data for promise chaining
      }
      return {}; // Return empty object if no data
    } catch (error) {
      // //console.error('Error fetching client licenses:', error);
      setErrorMessage('Failed to fetch license information for clients');
      setShowError(true);
      throw error; // Rethrow to allow catching in the caller
    } finally {
      setLoadingLicenses(false);
    }
  }, [region, fromDate, toDate, api, setErrorMessage, setShowError]);

  // Define fetchLicenseData before it's used
  const fetchLicenseData = useCallback(async (clientName) => {
    if (!clientName) return;

    setLoadingLicense(true);

    try {
      // Get API base URL from env or use default
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

      // Create the URL for license data
      const url = `/api/clients/${clientName}/license`;

      // Fetch license data using the authenticated API method
      const response = await api.getWithAuth(url);

      // Update license data if found
      // The backend returns the license data directly
      if (response.data) {
        setLicenseData({
          startDate: response.data.start_date || '',
          endDate: response.data.end_date || '',
          associatedLogData: response.data.associated_log_data || '',
          licensePerDay: response.data.license_per_day || '',
          onlineRetention: response.data.online_retention || '',
          offlineRetention: response.data.offline_retention || '',
          devicesToIntegrate: response.data.devices_to_integrate || '',
          engagementType: response.data.engagement_type || '',
          engagementModel: response.data.engagement_model || ''
        });
      } else {
        // Reset license data if not found
        setLicenseData({
          startDate: '',
          endDate: '',
          associatedLogData: '',
          licensePerDay: '',
          onlineRetention: '',
          offlineRetention: '',
          devicesToIntegrate: '',
          engagementType: '',
          engagementModel: ''
        });
      }
    } catch (error) {
      //console.error('Error fetching license data:', error);
      // Reset license data on error
      setLicenseData({
        startDate: '',
        endDate: '',
        associatedLogData: '',
        licensePerDay: '',
        onlineRetention: '',
        offlineRetention: '',
        devicesToIntegrate: '',
        engagementType: ''
      });
    } finally {
      setLoadingLicense(false);
    }
  }, []);

  // Move calculateTotals function before fetchClientCostData to fix reference error
  // Fix the calculateTotals function to properly handle backend data for production costs and RSYSLOG sizes
  const calculateTotals = useCallback(() => {
    ////console.log('Calculating totals from available data...');

    // Method 1: Check if we have client logs data with the total_rsyslog_size in the debug object
    if (clientLogs && clientLogs.debug && 'total_rsyslog_size' in clientLogs.debug) {
      const totalSize = clientLogs.debug.total_rsyslog_size;
      ////console.log('Found total_rsyslog_size in debug:', totalSize);
      setTotalRsyslogData(totalSize);
    }
    // Method 2: Calculate from clientLogs.by_client if available
    else if (clientLogs && clientLogs.by_client && clientLogs.by_client.length > 0) {
      const totalSize = clientLogs.by_client.reduce((sum, client) => sum + (parseFloat(client.size_gb) || 0), 0);
      ////console.log('Calculated total RSYSLOG size from clientLogs.by_client:', totalSize);
      setTotalRsyslogData(totalSize);
    }
    // If no data available, leave at default (0)
    else {
      ////console.log('No data available to calculate total RSYSLOG size');
    }

    // Method 1: Check if we have the total_production_cost directly from the backend
    if (clientLogs && clientLogs.debug && 'total_production_cost' in clientLogs.debug) {
      const totalProductionCost = clientLogs.debug.total_production_cost;
      ////console.log('Found total_production_cost in debug:', totalProductionCost);
      setTotalProductionCost(totalProductionCost);
    }
    // Method 2: Calculate the sum of all cost components from all clients
    else if (Object.keys(clientCostData).length > 0) {
      // We need to be careful here - the clientCostData.total_cost for individual clients
      // is their portion of the total production cost, not the full production cost.
      // Try to extract the total production cost from any client's debug data
      let totalProdCost = 0;

      // Find a client that has debug data with total_production_cost
      const clientWithDebugData = Object.values(clientCostData).find(client =>
        client && client.debug && client.debug.total_production_cost);

      if (clientWithDebugData) {
        totalProdCost = clientWithDebugData.debug.total_production_cost;
        ////console.log('Found total_production_cost from client debug data:', totalProdCost);
      }
      // If no debug data available, use a fallback calculation
      else {
        // Compute total OCI costs across all clients (this might not be accurate but is a fallback)
        const ociFallbackCosts = Object.values(clientCostData).reduce((sum, client) => {
          if (!client || !client.cost_details) return sum;

          const computeCost = client.cost_details.compute_cost || 0;
          const bootVolumeCost = client.cost_details.boot_volume_cost || 0;
          const blockVolumeCost = client.cost_details.block_volume_cost || 0;

          // Take the highest calculated total production cost
          const clientProdCost = computeCost + bootVolumeCost + blockVolumeCost;
          return clientProdCost > sum ? clientProdCost : sum;
        }, 0);

        totalProdCost = ociFallbackCosts;
        ////console.log('Calculated fallback total production cost:', totalProdCost);
      }

      setTotalProductionCost(totalProdCost);
    }
    // If no cost data available, leave at default (0)
    else {
      ////console.log('No data available to calculate total production cost');
    }
  }, [clientLogs, clientCostData]);

  // Update the useEffect to log debug information
  // useEffect(() => {
  //   console.log('Running calculateTotals with data:', { 
  //     clientLogsAvailable: !!clientLogs,
  //     clientCostDataAvailable: Object.keys(clientCostData).length > 0
  //   });

  //   // Log important data for debugging
  //   if (clientLogs && clientLogs.debug) {
  //     ////console.log('clientLogs.debug:', clientLogs.debug);
  //   }

  //   calculateTotals();
  // }, [calculateTotals, clientLogs, clientCostData]);

  // Define fetchClientCostData after calculateTotals has been defined
  const fetchClientCostData = useCallback(async (clientName, specificDate = null, optimizationParams = {}) => {
    if (!clientName) return;

    setLoadingCostData(true);

    try {
      // Get API base URL from env
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

      // Create the URL for cost data with appropriate parameters based on filters
      let url = `${API_BASE_URL}/api/clients/${clientName}/costs${region ? `?region=${region}` : ''}`;

      // Build query parameters
      const params = new URLSearchParams();

      // If region is specified, add it
      if (region) {
        params.set('region', region);
      }

      // If a specific date is provided, use it
      if (specificDate) {
        params.set('date', specificDate);
      }
      // If date filter is active, pass the date range
      else if (fromDate && toDate) {
        params.set('from_date', fromDate);
        params.set('to_date', toDate);
        ////console.log(`Fetching cost data with date range: ${fromDate} to ${toDate}`);
      }
      // Otherwise use today's date as default
      else {
        const today = new Date().toISOString().split('T')[0];
        params.set('date', today);
      }

      // Add optimization parameters to improve query performance
      if (optimizationParams) {
        Object.entries(optimizationParams).forEach(([key, value]) => {
          params.set(key, value);
        });
      }

      // For large date ranges, request data sampling
      const fromDateObj = new Date(fromDate);
      const toDateObj = new Date(toDate);
      const daysDiff = Math.ceil(Math.abs(toDateObj - fromDateObj) / (1000 * 60 * 60 * 24)) + 1;

      if (daysDiff > 30) {
        params.set('use_sampling', 'true');
        params.set('sample_points', '30');
      }

      // Always request query optimization for large date ranges
      if (daysDiff > 7) {
        params.set('optimize_query', 'true');
      }

      // Append all parameters to the URL
      url = `${url}${url.includes('?') ? '&' : '?'}${params.toString()}`;

      ////console.log(`Fetching cost data from: ${url}`);

      // Fetch cost data with only essential headers to avoid CORS issues
      console.log(`Fetching cost data from: ${url}`);

      let data;

      try {
        const fetchResponse = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            // Remove problematic headers that cause CORS issues
          },
          credentials: 'include'
        });

        if (fetchResponse.ok) {
          data = await fetchResponse.json();
          console.log('Successfully fetched cost data');
        } else {
          throw new Error(`HTTP error ${fetchResponse.status}`);
        }
      } catch (fetchError) {
        console.error('Error fetching cost data with fetch API:', fetchError);

        // Fall back to axios if fetch fails
        console.log('Falling back to axios for cost data request');
        const axiosResponse = await api.get(`/api/clients/${clientName}/costs`, {
          params: Object.fromEntries(params)
        });
        data = axiosResponse.data;
      }

      if (!data) {
        console.error(`Error fetching cost data: No data returned`);
        setClientCostData(prevState => ({
          ...prevState,
          [clientName]: {
            rsyslog_size_gb: 0,
            cost_per_gb: 0,
            total_cost: 0,
            total_client_data_gb: 0,
            production_cost: 0,
            cost_details: {
              compute_cost: 0,
              boot_volume_cost: 0,
              block_volume_cost: 0
            },
            daily_data: {},
            date_range: {
              from: fromDate || new Date().toISOString().split('T')[0],
              to: toDate || new Date().toISOString().split('T')[0]
            },
            currency: "INR"
          }
        }));
        setLoadingCostData(false);
        return;
      }
      ////console.log('Cost data received:', data);

      // Extract debug information for total production cost and total RSYSLOG size
      if (data.debug) {
        console.log('Debug data available:', data.debug);

        // Set total production cost if available
        if (data.debug.total_production_cost) {
          console.log('Setting total production cost from API:', data.debug.total_production_cost);
          setTotalProductionCost(data.debug.total_production_cost);
        }

        // Set total RSYSLOG size if available
        if (data.debug.total_rsyslog_size) {
          console.log('Setting total RSYSLOG size from API:', data.debug.total_rsyslog_size);
          setTotalRsyslogData(data.debug.total_rsyslog_size);
        }
      }

      // Get the client total size - handle both direct value and debug aggregated data
      let totalClientSize = data.rsyslog_size_gb || 0;

      // If we have debug daily data, it means we have a date range response
      // Sum up the daily sizes to get the total over the period
      if (data.debug && data.debug.daily_data) {
        const dailyData = data.debug.daily_data;
        console.log('Daily data available:', Object.keys(dailyData).length, 'days');

        // Calculate sum of client sizes across all days
        totalClientSize = Object.values(dailyData)
          .reduce((total, day) => total + (day.client_rsyslog_size || 0), 0);

        console.log('Calculated total client size from daily data:', totalClientSize);
      }

      // For consolidated clients like DICT, ensure child clients are properly loaded
      if (PARENT_PATTERNS.includes(clientName)) {
        console.log(`Processing cost data for parent client ${clientName}`);

        // Get all child clients
        const childClients = clients.filter(client => client && typeof client === 'string' ? client.startsWith(clientName) : client && client.name && client.name.startsWith(clientName));
        if (childClients.length > 0) {
          console.log(`Found ${childClients.length} child clients for ${clientName}`);
        }
      }

      // Update cost data with client name as key
      setClientCostData(prevState => ({
        ...prevState,
        [clientName]: {
          rsyslog_size_gb: totalClientSize, // Use aggregated size
          cost_per_gb: data.cost_per_gb || 0,
          total_cost: data.total_cost || 0,
          daily_data: data.debug?.daily_data || {},
          cost_details: data.cost_details || {
            compute_cost: 0,
            boot_volume_cost: 0,
            block_volume_cost: 0
          },
          date_range: {
            from: data.date_range?.from || fromDate || new Date().toISOString().split('T')[0],
            to: data.date_range?.to || toDate || new Date().toISOString().split('T')[0]
          },
          daily_data: data.debug?.daily_data || null, // Store daily data for potential display
          currency: data.currency || "INR",
          debug: data.debug // Store debug data for later reference
        }
      }));

      // After updating cost data, recalculate totals
      setTimeout(() => calculateTotals(), 0);

    } catch (error) {
      //console.error('Error fetching cost data:', error);
      setClientCostData(prevState => ({
        ...prevState,
        [clientName]: {
          rsyslog_size_gb: 0,
          cost_per_gb: 0,
          total_cost: 0,
          total_client_data_gb: 0,
          production_cost: 0,
          cost_details: {
            compute_cost: 0,
            boot_volume_cost: 0,
            block_volume_cost: 0
          },
          date_range: {
            from: fromDate || new Date().toISOString().split('T')[0],
            to: toDate || new Date().toISOString().split('T')[0]
          },
          currency: "INR"
        }
      }));
    } finally {
      setLoadingCostData(false);
    }
  }, [region, fromDate, toDate, calculateTotals, setTotalProductionCost, setTotalRsyslogData]);
  // Function to fetch total production costs
  const fetchTotalProductionCosts = useCallback(async (params) => {
    setLoadingTotalCosts(true);

    try {
      // Build params object for API call
      const apiParams = {};
      if (params?.region) {
        apiParams.region = params.region;
      }
      if (params?.from_date && params?.to_date) {
        apiParams.from_date = params.from_date;
        apiParams.to_date = params.to_date;
      }

      // Use authenticated API helper so X-User-Name / X-User-Role headers are sent
      const response = await api.getWithAuth('/api/total_production_costs', {
        params: apiParams
      });

      const data = response.data || {};

      // Update the total production cost data
      setTotalProductionCostData(data);

      // Update the global total values (used in other parts of the UI)
      setTotalRsyslogData(data.rsyslog_size || 0);
      setTotalProductionCost(data.production_cost || 0);
    } catch (error) {
      //console.error('Error fetching total production costs:', error);
      setErrorMessage('Failed to fetch total production costs');
      setShowError(true);
    } finally {
      setLoadingTotalCosts(false);
    }
  }, [api, setErrorMessage, setShowError, setTotalRsyslogData, setTotalProductionCost]);

  const initialRender = useRef(true);


  // Completely replace the debounceFetchData function with applyDateFilter function
  // that only gets called from the button click handler
  // Track last applied filter to prevent redundant API calls
  const lastAppliedFilterRef = useRef({
    fromDate: null,
    toDate: null
  });

  const applyDateFilter = useCallback((fromDateVal, toDateVal, skipUrlUpdate = false) => {
    // Log the date values for debugging
    //console.log("Applying date filter with:", { fromDateVal, toDateVal });

    if (!fromDateVal || !toDateVal) {
      setErrorMessage("Please select both From and To dates");
      setShowError(true);
      return;
    }

    // Create a function to handle timezone issues properly
    const getUTCDateString = (date) => {
      const d = new Date(date);
      return new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
      ).toISOString().split('T')[0];
    };

    // Validate date range using UTC dates to avoid timezone issues
    const fromDateObj = new Date(fromDateVal);
    // Ensure fromDate is at the start of the day
    fromDateObj.setHours(0, 0, 0, 0);

    const toDateObj = new Date(toDateVal);
    // Ensure toDate is at the end of the day for inclusive filtering
    toDateObj.setHours(23, 59, 59, 999);

    if (fromDateObj > toDateObj) {
      setErrorMessage("From date cannot be after To date");
      setShowError(true);
      return;
    }

    // Get formatted date strings for API and URL - use UTC to avoid timezone issues
    const fromDateString = getUTCDateString(fromDateObj);
    const toDateString = getUTCDateString(toDateObj);

    // Check if this is the same filter we just applied to prevent redundant API calls
    if (
      fromDateString === lastAppliedFilterRef.current.fromDate &&
      toDateString === lastAppliedFilterRef.current.toDate
    ) {
      //console.log("Skipping duplicate filter application");
      return;
    }

    // Update the last applied filter reference
    lastAppliedFilterRef.current = {
      fromDate: fromDateString,
      toDate: toDateString
    };

    // Set loading state
    setLoadingDateFilter(true);

    try {

      // Update URL with the new date filter - but only if not skipping URL update
      if (!skipUrlUpdate) {
        const params = new URLSearchParams(location.search);

        params.set('from_date', fromDateString);
        params.set('to_date', toDateString);
        if (region) params.set('region', region);

        // Log the URL update
        //console.log("Updating URL with date params:", { from: fromDateString, to: toDateString });

        // Update URL - this does NOT trigger a navigation
        window.history.replaceState(
          {},
          '',
          `${location.pathname}?${params.toString()}`
        );
      }

      // Calculate date range size in days
      const daysDiff = Math.ceil(Math.abs(toDateObj - fromDateObj) / (1000 * 60 * 60 * 24)) + 1;

      // Define optimization parameters based on date range size
      const optimizationParams = {
        // Set optimization level based on date range
        optimization_level: daysDiff > 14 ? 'high' : daysDiff > 7 ? 'medium' : 'none',

        // Request data aggregation for large date ranges
        aggregate_daily: daysDiff > 7,

        // For very large date ranges, use sampling
        use_sampling: daysDiff > 30,

        // When using sampling, specify the number of data points to return
        sample_points: daysDiff > 30 ? 30 : daysDiff,

        // Use backend query optimization
        optimize_query: true,

        // Only return necessary fields to reduce payload size
        fields: 'essential',

        // Skip license fetching since we already have it and it doesn't change with date
        skip_license_fetch: true
      };

      // For large queries, show a more descriptive loading message
      if (daysDiff > 14) {
        setSuccessMessage(`Loading data for ${daysDiff} days. This may take a moment...`);
        setShowSuccess(true);
      }

      // Create API params with optimization parameters
      const apiParams = {
        region,
        from_date: fromDateString,
        to_date: toDateString,
        ...optimizationParams
      };

      // Ensure we update state with the properly formatted dates
      setFromDate(fromDateString);
      setToDate(toDateString);

      // Highly optimized approach - only fetch what's really needed
      // 1. Fetch total production costs (faster summary data)
      // 2. Then fetch client list
      // 3. Then fetch client logs with optimization flags
      // DO NOT refetch license data which doesn't change with date filtering

      Promise.all([
        fetchTotalProductionCosts(apiParams),
        fetchClients(region, apiParams),
        fetchClientLogs({
          region,
          from_date: fromDateString,
          to_date: toDateString,
          forceRefresh: true,
          ...optimizationParams
        })
      ])
        .then(() => {
          // If a client is expanded, refresh its cost data
          if (expandedClient) {
            return fetchClientCostData(expandedClient, null, optimizationParams);
          }
        })
        .then(() => {
          // Calculate new totals
          calculateTotals();
          setLoadingDateFilter(false);

          // Clear success message after a delay
          setTimeout(() => {
            if (showSuccess) {
              setShowSuccess(false);
            }
          }, 2000);
        })
        .catch(error => {
          console.error('Error applying date filter:', error);
          setErrorMessage('Failed to apply date filter: ' + (error.message || 'Unknown error'));
          setShowError(true);
          setLoadingDateFilter(false);
        });
    } catch (error) {
      console.error('Error applying date filter:', error);
      setErrorMessage('Failed to apply date filter: ' + (error.message || 'Unknown error'));
      setShowError(true);
      setLoadingDateFilter(false);
    }
  }, [
    region,
    expandedClient,
    fetchClients,
    fetchClientLogs,
    fetchClientCostData,
    calculateTotals,
    fetchTotalProductionCosts,
    location.search,
    location.pathname,
    setErrorMessage,
    setShowError,
    setSuccessMessage,
    setShowSuccess,
    showSuccess
  ]);

  // Super simple handlers with no side effects
  const handleFromDateChange = useCallback((event) => {
    setFromDate(event.target.value);
  }, []);

  const handleToDateChange = useCallback((event) => {
    setToDate(event.target.value);
  }, []);

  // Update the clear filter handler to use the applyDateFilter function
  const handleClearDateFilter = useCallback(() => {
    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    // Reset to today's date instead of empty strings
    setFromDate(todayStr);
    setToDate(todayStr);

    // Apply the filter with today's date
    applyDateFilter(todayStr, todayStr);
  }, [applyDateFilter]);

  // Update the handleApplyDateFilter function to use the applyDateFilter function
  const handleApplyDateFilter = useCallback(() => {
    applyDateFilter(fromDate, toDate);

    // If a client is currently expanded, refresh its data with the new date range
    if (expandedClient) {
      console.log(`Refreshing expanded client ${expandedClient} with new date range`);
      // Re-fetch client products with the new date range
      fetchClientProducts(expandedClient, region, { from_date: fromDate, to_date: toDate })
        .then(details => {
          if (details) {
            setClientDetails(prev => ({
              ...prev,
              [expandedClient]: details,
            }));
          }
        })
        .catch(error => {
          console.error(`Error refreshing expanded client ${expandedClient}:`, error);
        });
    }
  }, [fromDate, toDate, applyDateFilter, expandedClient, region, fetchClientProducts]);

  // Remove any useEffects that watch fromDate or toDate and trigger data loading

  // Modified version of the URL parameters handler
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const regionParam = params.get('region');
    const searchParam = params.get('search');
    const fromDateParam = params.get('from_date');
    const toDateParam = params.get('to_date');

    // First render only: just set the state values without triggering data fetching
    if (initialRender.current) {
      initialRender.current = false;

      if (regionParam) {
        setRegion(regionParam);
      }

      if (searchParam) {
        setSearchTerm(searchParam);
      }

      if (fromDateParam) {
        setFromDate(fromDateParam);
      }

      if (toDateParam) {
        setToDate(toDateParam);
      }
      return;
    }

    // Handle region change from URL
    if (regionParam && regionParam !== region) {
      setRegion(regionParam);
    }

    // Handle search change from URL
    if (searchParam && searchParam !== searchTerm) {
      setSearchTerm(searchParam);
    }

    // Just update the state, don't trigger data loading
    if (fromDateParam && fromDateParam !== fromDate) {
      setFromDate(fromDateParam);
    }

    if (toDateParam && toDateParam !== toDate) {
      setToDate(toDateParam);
    }
  }, [location.search]);

  // Simplified initial data loading logic
  useEffect(() => {
    let isMounted = true;
    const loadTimer = setTimeout(() => {
      const loadData = async () => {
        try {
          // Only load data once at initial mount
          if (isMounted) {
            // Get today's date
            const today = new Date();
            const todayStr = today.toISOString().split('T')[0];

            // If no explicit dates are set in URL, use today's date
            const params = new URLSearchParams(location.search);
            const fromDateParam = params.get('from_date');
            const toDateParam = params.get('to_date');

            // Optimization flags for initial load
            const optimizationParams = {
              optimization_level: 'high',
              aggregate_daily: true,
              optimize_query: true,
              fields: 'essential',
              priority: 'speed'
            };

            // First load clients and regions (these are small and fast)
            await Promise.all([
              fetchRegions(),
              fetchClients(region)
            ]);

            // Then conditionally load the remaining data
            if (!fromDateParam && !toDateParam) {
              // Set default date to today
              setFromDate(todayStr);
              setToDate(todayStr);

              // Load today's data with high optimization
              await Promise.all([
                fetchClientLogs({
                  region,
                  from_date: todayStr,
                  to_date: todayStr,
                  ...optimizationParams
                }),
                fetchTotalProductionCosts({
                  region,
                  from_date: todayStr,
                  to_date: todayStr,
                  ...optimizationParams
                })
              ]);
            } else if (fromDateParam && toDateParam) {
              // Use dates from URL
              setFromDate(fromDateParam);
              setToDate(toDateParam);

              // Load data with URL dates
              await Promise.all([
                fetchClientLogs({
                  region,
                  from_date: fromDateParam,
                  to_date: toDateParam,
                  ...optimizationParams
                }),
                fetchTotalProductionCosts({
                  region,
                  from_date: fromDateParam,
                  to_date: toDateParam,
                  ...optimizationParams
                })
              ]);
            }

            // Calculate totals
            calculateTotals();
          }
        } catch (error) {
          console.error('Error loading client data:', error);
          setLoadingDateFilter(false);
        }
      };

      if (isMounted) {
        loadData();
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(loadTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on component mount

  // Clear client details when region or date range changes
  useEffect(() => {
    setClientDetails({});
    setExpandedClient(null);
  }, [region, fromDate, toDate]);

  // Handle refresh with debounce
  const handleRefresh = useCallback(() => {
    // Set loading state
    const startLoading = () => {
      setLoadingData(true);
    };

    // Show success message function
    const showSuccessMessage = (message) => {
      setSuccessMessage(message);
      setShowSuccess(true);
    };

    // Show error message function
    const showErrorMessage = (message) => {
      setErrorMessage(message);
      setShowError(true);
    };

    // Refresh all data
    const doRefresh = async () => {
      try {
        startLoading();

        // Build fetch parameters
        let fetchParams = {};

        // If date filter is active, use the selected date range
        if (fromDate && toDate) {
          fetchParams = { from_date: fromDate, to_date: toDate };
        } else {
          // Otherwise use today's date range as default
          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];

          // Create start and end date for today (start of day to end of day)
          const startOfDay = todayStr;
          const endOfDay = todayStr;

          fetchParams = { from_date: startOfDay, to_date: endOfDay };
          //console.log(`Using default today date range for refresh: ${startOfDay} to ${endOfDay}`);
        }

        // Fetch all data in parallel with appropriate date parameters
        await Promise.all([
          fetchRegions(),
          fetchClients(region, fetchParams),
          fetchClientLogs({ region, ...fetchParams, forceRefresh: true }),
          fetchAllClientLicenses(), // Also refresh license data
          fetchTotalProductionCosts({
            region,
            from_date: fetchParams.from_date,
            to_date: fetchParams.to_date
          }) // Fetch total production costs
        ]);

        // Show success message
        showSuccessMessage("Data refreshed successfully");
      } catch (error) {
        //console.error('Error refreshing data:', error);
        showErrorMessage("Failed to refresh data. Please try again.");
      } finally {
        setLoadingData(false);
      }
    };

    // Execute refresh
    doRefresh();
  }, [region, fromDate, toDate, fetchRegions, fetchClients, fetchClientLogs, fetchAllClientLicenses, fetchTotalProductionCosts]);

  // Navigate back to dashboard
  const handleBackToDashboard = useCallback(() => {
    navigate(region ? `/?region=${region}` : '/');
  }, [navigate, region]);

  // Handle region change
  const handleRegionChange = useCallback((event) => {
    const newRegion = event.target.value;
    setRegion(newRegion);
    setSelectedRegion(newRegion);  // Add this line

    // Build the URL with all current parameters
    const params = new URLSearchParams();
    if (newRegion) params.set('region', newRegion);
    if (searchTerm) params.set('search', searchTerm);
    if (fromDate && toDate) {
      params.set('from_date', fromDate);
      params.set('to_date', toDate);
    }

    navigate(`/clients${params.toString() ? `?${params.toString()}` : ''}`);
  }, [navigate, searchTerm, fromDate, toDate]);

  // Handle search
  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  // Toggle overutilized filter
  const toggleOverutilizedFilter = useCallback(() => {
    setShowOverutilizedOnly(prev => !prev);
    // Reset expiring licenses filter if enabled
    if (showExpiringLicensesOnly) {
      setShowExpiringLicensesOnly(false);
    }
  }, [showExpiringLicensesOnly]);

  // Handle group filter change
  const handleGroupFilterChange = useCallback((event) => {
    setGroupFilter(event.target.value);
  }, []);

  const handleEngagementModelFilterChange = useCallback((event) => {
    setEngagementModelFilter(event.target.value);
  }, []);

  // Add toggle function for license expiration filter
  const toggleExpiringLicensesFilter = useCallback((value = null) => {
    setShowExpiringLicensesOnly(prev => value !== null ? value : !prev);
    // Reset overutilized filter if enabled
    if (showOverutilizedOnly) {
      setShowOverutilizedOnly(false);
    }
  }, [showOverutilizedOnly]);

  // Handle client expansion with async loading and caching
  const handleClientExpand = useCallback(async (clientName) => {
    if (expandedClient === clientName) {
      setExpandedClient(null);
      return;
    }

    setExpandedClient(clientName);

    // Log the client expansion action
    try {
      await api.post('/api/log_client_action', {
        client_name: clientName,
        action_type: 'EXPAND',
        details: {
          region: region || 'All Regions'
        }
      });
    } catch (error) {
      console.error('Error logging client expansion:', error);
    }

    // Check if we already have details for this client
    if (!clientDetails[clientName]) {
      setLoadingDetails(true);

      try {
        // Pass the current region and date range to fetchClientProducts
        const params = {};
        if (fromDate && toDate) {
          params.from_date = fromDate;
          params.to_date = toDate;
        }

        // Pass the current region to fetchClientProducts along with date params
        const details = await fetchClientProducts(clientName, region, params);

        if (details) {
          setClientDetails(prev => ({
            ...prev,
            [clientName]: details,
          }));

          // Also fetch cost data for the client when expanded with date params
          await fetchClientCostData(clientName, null, {
            from_date: fromDate,
            to_date: toDate
          });

          // Check if this is a parent client from PARENT_PATTERNS
          const isParent = PARENT_PATTERNS.includes(clientName);

          // If it's a parent client, also fetch cost data for all child clients
          if (isParent && clients && clients.length) {
            // Find child clients directly without using getChildClients
            const childClients = clients.filter(client =>
              client !== clientName &&
              client.toLowerCase().startsWith(clientName.toLowerCase())
            );

            console.log(`Fetching cost data for ${childClients.length} child clients of ${clientName}`);

            // Create an array of promises for fetching child client data
            const childPromises = childClients.map(childName =>
              fetchClientCostData(childName, null, {
                from_date: fromDate,
                to_date: toDate
              })
            );

            // Wait for all child client data to be fetched
            await Promise.all(childPromises);
            console.log(`Completed fetching cost data for all child clients of ${clientName}`);

            // Force a re-render after all child data is loaded
            setClientDetails(prev => ({ ...prev }));
          }
        }
      } catch (error) {
        console.error(`Error fetching details for client ${clientName}:`, error);
      } finally {
        setLoadingDetails(false);
      }
    } else {
      // If we already have details, still fetch the latest cost data with date params
      try {
        // Fetch parent client data first
        await fetchClientCostData(clientName, null, {
          from_date: fromDate,
          to_date: toDate
        });

        // Check if this is a parent client from PARENT_PATTERNS
        const isParent = PARENT_PATTERNS.includes(clientName);

        // If it's a parent client, also fetch cost data for all child clients
        if (isParent && clients && clients.length) {
          // Find child clients directly without using getChildClients
          const childClients = clients.filter(client =>
            client !== clientName &&
            client.toLowerCase().startsWith(clientName.toLowerCase())
          );

          console.log(`Fetching cost data for ${childClients.length} child clients of ${clientName}`);

          // Create an array of promises for fetching child client data
          const childPromises = childClients.map(childName =>
            fetchClientCostData(childName, null, {
              from_date: fromDate,
              to_date: toDate
            })
          );

          // Wait for all child client data to be fetched
          await Promise.all(childPromises);
          console.log(`Completed fetching cost data for all child clients of ${clientName}`);

          // Force a re-render after all child data is loaded
          setClientDetails(prev => ({ ...prev }));
        }
      } catch (error) {
        console.error(`Error fetching cost data for ${clientName} and its children:`, error);
      }
    }
  }, [expandedClient, clientDetails, fetchClientProducts, region, fetchClientCostData, api, fromDate, toDate, PARENT_PATTERNS, clients]);

  // Handle download client data with proper error handling
  const handleDownload = useCallback(async (clientName) => {
    if (!clientName) return;

    try {
      setLoadingDownload(true);

      // Log the download action
      try {
        // Make sure to always use 'DOWNLOAD' action type, especially for ALQASSIM
        const actionType = 'DOWNLOAD';

        await api.post('/api/log_client_action', {
          client_name: clientName,
          action_type: actionType,
          details: {
            region: region || 'All Regions',
            from_date: fromDate || '',
            to_date: toDate || ''
          }
        });


      } catch (error) {
        console.error('Error logging client download:', error);
      }

      // Check if we have a date range
      const hasDateRange = !!(fromDate && toDate);

      // First, fetch the cost data for this client
      const params = new URLSearchParams({
        region: region || '',
        from_date: fromDate || '',
        to_date: toDate || ''
      });

      const costResponse = await api.get(`/api/clients/${clientName}/costs?${params}`);
      const costData = costResponse.data;

      // Get all products for this client
      let deviceData = [];
      const clientProducts = clientLogs?.by_product?.filter(product => clientNamesMatchCanonical(product.client_name, clientName) && !isExcludedProduct(product)) || [];

      // Directly fetch all device IDs for all products
      //console.log(`Fetching device IDs for all products of client ${clientName}`);

      // Process each product one by one
      for (const product of clientProducts) {
        const productName = product.product;
        if (!productName) continue;

        try {
          //console.log(`Fetching device IDs for ${clientName}/${productName}`);
          const result = await fetchProductDeviceIds(clientName, productName, region, { from_date: fromDate, to_date: toDate });

          if (result && result.device_data && result.device_data.length > 0) {
            //console.log(`Found ${result.device_data.length} device IDs for ${productName}`);
            //console.log("Raw device data:", JSON.stringify(result.device_data));

            // Add product name to each device record and ensure size is properly formatted
            const productDevices = result.device_data.map(device => {
              // Make sure size is a number - check both size_gb and total_size_gb fields
              const sizeGB = typeof device.total_size_gb === 'number' ?
                device.total_size_gb :
                parseFloat(device.total_size_gb || device.size_gb || '0');

              //console.log(`Device ${device.device_id}: Using size ${sizeGB} from fields:`, 
              //  {total_size_gb: device.total_size_gb, size_gb: device.size_gb});

              return {
                ...device,
                product_name: productName,
                size_gb: sizeGB
              };
            });

            // Add to our collection
            deviceData = [...deviceData, ...productDevices];
          }
        } catch (error) {
          console.error(`Error fetching device IDs for ${clientName}/${productName}:`, error);
        }
      }

      // Get daily data if available
      const dailyData = costData.debug?.daily_data || {};

      // Check if this is a parent client
      const isParent = PARENT_PATTERNS.includes(clientName);

      // Create aggregated daily data object
      const aggregatedDailyData = { ...dailyData };

      // For parent clients, add data from all child clients
      if (isParent && clients && clients.length) {
        console.log(`Adding child client data for CSV download for parent ${clientName}`);

        // Find child clients
        const childClients = clients.filter(client =>
          client !== clientName &&
          client.toLowerCase().startsWith(clientName.toLowerCase())
        );

        console.log(`Found ${childClients.length} child clients for CSV download`);

        // Process each child client
        for (const childName of childClients) {
          try {
            // Fetch cost data for child client
            const childParams = new URLSearchParams({
              region: region || '',
              from_date: fromDate || '',
              to_date: toDate || ''
            });

            console.log(`Fetching cost data for child client ${childName} for CSV download`);
            const childCostResponse = await api.get(`/api/clients/${childName}/costs?${childParams}`);
            const childCostData = childCostResponse.data;

            // Get child daily data
            const childDailyData = childCostData.debug?.daily_data || {};

            // Add child's daily data (size + per-product breakdown) to aggregated data
            Object.entries(childDailyData).forEach(([date, data]) => {
              const childSize = data.client_rsyslog_size || 0;
              const childProducts = data.products || [];

              if (!aggregatedDailyData[date]) {
                // If this date doesn't exist yet, just clone the whole object
                aggregatedDailyData[date] = {
                  ...data,
                  products: childProducts.map(p => ({
                    ...p,
                    size_gb: p.size_gb || 0,
                  })),
                };
              } else {
                // Add child's size to parent's size
                if (!aggregatedDailyData[date].client_rsyslog_size) {
                  aggregatedDailyData[date].client_rsyslog_size = 0;
                }
                aggregatedDailyData[date].client_rsyslog_size += childSize;

                // Merge per-product sizes for this date
                const existingProducts = aggregatedDailyData[date].products || [];
                const productMapForDay = new Map();

                // Seed with existing products
                existingProducts.forEach(p => {
                  const name = p.product || 'Unknown';
                  const current = productMapForDay.get(name) || {
                    ...p,
                    size_gb: 0,
                  };
                  current.size_gb += p.size_gb || 0;
                  productMapForDay.set(name, current);
                });

                // Add child products
                childProducts.forEach(p => {
                  const name = p.product || 'Unknown';
                  const current = productMapForDay.get(name) || {
                    ...p,
                    size_gb: 0,
                  };
                  current.size_gb += p.size_gb || 0;
                  productMapForDay.set(name, current);
                });

                aggregatedDailyData[date].products = Array.from(productMapForDay.values());

                console.log(`Added ${childSize.toFixed(2)} GB from ${childName} for date ${date} to CSV data (including per-product breakdown)`);
              }
            });
          } catch (error) {
            console.error(`Error fetching cost data for child client ${childName}:`, error);
          }
        }
      }

      // Calculate total client size from aggregated daily data
      let totalClientSize = 0;
      if (Object.keys(aggregatedDailyData).length > 0) {
        Object.values(aggregatedDailyData).forEach(day => {
          if (day.client_rsyslog_size) {
            totalClientSize += day.client_rsyslog_size;
          }
        });
      } else {
        // Fallback to regular size if no daily data
        totalClientSize = costData.rsyslog_size_gb || 0;
      }

      console.log(`Total client size for ${clientName} (including children): ${totalClientSize.toFixed(2)} GB`);

      // Get client data and product data
      const clientData = findFirstByClientForCanonical(clientLogs?.by_client, clientName);
      const productData = clientLogs?.by_product?.filter(product => clientNamesMatchCanonical(product.client_name, clientName) && !isExcludedProduct(product)) || [];

      // For parent clients, also include products from child clients
      let allProductData = [...productData];
      if (isParent) {
        const childClients = clients.filter(client =>
          client !== clientName &&
          client.toLowerCase().startsWith(clientName.toLowerCase())
        );

        childClients.forEach(childName => {
          const childProducts = clientLogs?.by_product?.filter(product => clientNamesMatchCanonical(product.client_name, childName) && !isExcludedProduct(product)) || [];
          allProductData = [...allProductData, ...childProducts];
        });
      }

      // Calculate total count (number of products)
      const totalCount = allProductData.length;

      if (!clientData && allProductData.length === 0) {
        setErrorMessage(`No data available for client "${clientName}"${region ? ` in region "${region}"` : ''}.`);
        setShowError(true);
        setLoadingDownload(false);
        return;
      }

      // Get the display region (use "All Regions" when empty)
      const displayRegion = region || "All Regions";

      // Function to escape CSV values properly
      const escapeCSV = (value) => {
        if (value === null || value === undefined) {
          return '';
        }

        const stringValue = String(value);
        if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('\r')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      // Fetch license data for this client to get start/end dates and threshold
      let licenseInfo = clientLicenses[clientName] || {};
      if (!licenseInfo.startDate && !licenseInfo.license_data) {
        try {
          const licenseResponse = await api.getWithAuth(`/api/clients/${clientName}/license`);
          if (licenseResponse.data && licenseResponse.data.license_data) {
            licenseInfo = licenseResponse.data.license_data;
            // Also update the licenses cache
            setClientLicenses(prev => ({
              ...prev,
              [clientName]: licenseInfo
            }));
          }
        } catch (err) {
          console.error(`Error fetching license for ${clientName}`, err);
        }
      }

      // Extract license information, checking both new API format and old formats
      const startDate = licenseInfo.start_date || licenseInfo.startDate || 'Not set';
      const endDate = licenseInfo.end_date || licenseInfo.endDate || 'Not set';
      const threshold = licenseInfo.license_per_day || licenseInfo.licensePerDay || 'Not set';
      const onlineRetention = licenseInfo.online_retention || licenseInfo.onlineRetention || 'Not set';
      const offlineRetention = licenseInfo.offline_retention || licenseInfo.offlineRetention || 'Not set';

      // Get full client name from mapping
      const fullClientName = getFullClientName(clientName);

      // Array to hold rows
      const rows = [];

      // Add summary section
      rows.push(['Client Cost Analysis Summary']);
      rows.push(['Client ID:', clientName]);
      rows.push(['Client Name:', fullClientName]);
      rows.push(['Date Range:', hasDateRange ? `${fromDate} to ${toDate}` : 'All Time']);
      rows.push(['License Start Date:', startDate]);
      rows.push(['License End Date:', endDate]);
      rows.push(['License Threshold (GB/day):', threshold]);
      rows.push(['Online Retention (Months):', onlineRetention]);
      rows.push(['Offline Retention (Months):', offlineRetention]);
      rows.push(['Region:', displayRegion]);
      rows.push(['Total Products:', totalCount]);
      rows.push(['']);

      // Add cost analysis data only if user has permission
      if (hasCostPermission) {
        rows.push(['Cost Analysis']);
        rows.push(['Total Cost (₹)', 'Total Client Data', 'Cost Per GB (₹)']);
        rows.push([
          Number(costData.total_cost || 0).toFixed(2),
          formatDataSize(totalClientSize || 0),
          Number(costData.cost_per_gb || 0).toFixed(2)
        ].map(val => escapeCSV(val)));
        rows.push(['']);

        // Add cost breakdown
        rows.push(['Cost Breakdown']);
        rows.push(['Compute Cost (₹)', 'Boot Volume Cost (₹)', 'Block Volume Cost (₹)']);
        rows.push([
          Number(costData.cost_details?.compute_cost || 0).toFixed(2),
          Number(costData.cost_details?.boot_volume_cost || 0).toFixed(2),
          Number(costData.cost_details?.block_volume_cost || 0).toFixed(2)
        ].map(val => escapeCSV(val)));
        rows.push(['']);
      } else {
        // Only add data size information
        rows.push(['Data Size Analysis']);
        rows.push(['Total Client Data']);
        rows.push([
          formatDataSize(totalClientSize || 0)
        ].map(val => escapeCSV(val)));
        rows.push(['']);
      }

      // Add daily breakdown if we have a date range and daily data
      if (hasDateRange && Object.keys(aggregatedDailyData).length > 0) {
        rows.push(['Daily Data Breakdown']);

        // Adjust headers based on permission
        if (hasCostPermission) {
          rows.push(['Date', 'Size', 'Cost (₹)', 'Cost Per GB (₹)']);
        } else {
          rows.push(['Date', 'Size']);
        }

        // Sort dates for consistent output
        const sortedDates = Object.keys(aggregatedDailyData).sort();

        sortedDates.forEach(date => {
          const dayData = aggregatedDailyData[date];
          const daySize = dayData.client_rsyslog_size || 0;

          // Calculate day cost based on size and cost per GB
          const dayCostPerGB = (dayData.oci_costs?.cost_per_gb) ||
            (dayData.total_rsyslog_size > 0 ?
              (dayData.total_cost || 0) / dayData.total_rsyslog_size : 0);

          const dayCost = daySize * dayCostPerGB;

          if (hasCostPermission) {
            rows.push([
              date,
              formatDataSize(daySize),
              Number(dayCost).toFixed(2),
              Number(dayCostPerGB).toFixed(2)
            ].map(val => escapeCSV(val)));
          } else {
            rows.push([
              date,
              formatDataSize(daySize)
            ].map(val => escapeCSV(val)));
          }
        });
        rows.push(['']);
      }

      // Only add product details if not in client-wise mode
      if (downloadType !== 'client') {
        // Add product details section
        rows.push(['Product Details']);

        // Different header based on whether we have daily data
        if (hasDateRange && Object.keys(aggregatedDailyData).length > 0) {
          const dates = Object.keys(aggregatedDailyData).sort();
          rows.push(['Client Name', 'Product', 'Region', ...dates.map(d => d), 'Total Size', 'Total Cost (₹)'].map(escapeCSV));

          const productDailyMap = new Map();

          // For single-day ranges, fall through to the costs API path below.
          // hosts.py now returns correctly aggregated daily totals, so no
          // normalization is needed — just use the raw per-product sizes.
          if (false) {
            // (kept as placeholder; single-day now handled by the costs API path)
          } else {
            // Default: use actual per-day per-product sizes from cost API
            dates.forEach(date => {
              const dayData = aggregatedDailyData[date] || {};
              const dayProducts = dayData.products || [];

              dayProducts.forEach(p => {
                const productName = p.product || 'Unknown';
                const sizeGB = p.size_gb || 0;

                if (!productDailyMap.has(productName)) {
                  productDailyMap.set(productName, {
                    totalSize: 0,
                    daily: {}
                  });
                }

                const entry = productDailyMap.get(productName);
                entry.totalSize += sizeGB;
                entry.daily[date] = (entry.daily[date] || 0) + sizeGB;
              });
            });

            // Fallback if we somehow have no per-day product info
            if (productDailyMap.size === 0) {
              allProductData.forEach(product => {
                const productName = product.product || 'Unknown';
                const sizeGB = product.size_gb || 0;

                if (!productDailyMap.has(productName)) {
                  productDailyMap.set(productName, {
                    totalSize: 0,
                    daily: {}
                  });
                }

                const entry = productDailyMap.get(productName);
                entry.totalSize += sizeGB;
              });
            }

            // If still nothing, add a single "All Products" row
            if (productDailyMap.size === 0 && clientData) {
              const allProductsEntry = {
                totalSize: totalClientSize || 0,
                daily: {}
              };

              dates.forEach(date => {
                const dayData = aggregatedDailyData[date];
                const daySize = dayData?.client_rsyslog_size || 0;
                allProductsEntry.daily[date] = daySize;
              });

              productDailyMap.set('All Products', allProductsEntry);
            }
          }

          // Add rows for each product
          productDailyMap.forEach((entry, productName) => {
            const row = [
              clientName,
              productName,
              displayRegion
            ];

            // Add size for each date
            dates.forEach(date => {
              row.push(formatDataSize(entry.daily[date] || 0));
            });

            // Add total size and cost
            row.push(formatDataSize(entry.totalSize));
            row.push(Number(entry.totalSize * costData.cost_per_gb || 0).toFixed(2));

            rows.push(row.map(escapeCSV));
          });
        } else {
          // Simple product details without daily breakdown
          rows.push(['Client Name', 'Product', 'Size', 'Cost (₹)', 'Region', 'Date Exported'].map(escapeCSV));

          // Get current date for export timestamp
          const exportDate = new Date().toISOString().slice(0, 10);

          // Add data rows
          if (allProductData.length > 0) {
            allProductData.forEach(product => {
              const productSize = product.size_gb || 0;
              const productCost = productSize * costData.cost_per_gb || 0;

              rows.push([
                clientName,
                product.product || 'Unknown',
                formatDataSize(productSize),
                Number(productCost).toFixed(2),
                displayRegion,
                exportDate
              ].map(val => escapeCSV(val)));
            });
          } else if (clientData) {
            // If no individual products, just add the client as a single row
            rows.push([
              clientName,
              'All Products',
              formatDataSize(totalClientSize || 0),
              Number((totalClientSize || 0) * costData.cost_per_gb || 0).toFixed(2),
              displayRegion,
              exportDate
            ].map(val => escapeCSV(val)));
          }
        }
      }

      // Create a second sheet for device IDs
      const deviceIdRows = [];
      let hasDeviceData = deviceData.length > 0;

      // Add device ID data as a second sheet
      if (hasDeviceData) {
        deviceIdRows.push(['Device ID Data']);
        deviceIdRows.push(['']);
        deviceIdRows.push(['Client', 'Product', 'Device ID', 'Size']);
        deviceIdRows.push(['']);

        //console.log("Device data for CSV:", deviceData);

        // Group device data by product
        const devicesByProduct = {};
        deviceData.forEach(device => {
          const productName = device.product_name || 'Unknown';
          if (!devicesByProduct[productName]) {
            devicesByProduct[productName] = [];
          }
          devicesByProduct[productName].push(device);
        });

        // Add data for each product
        Object.entries(devicesByProduct).forEach(([productName, devices]) => {
          // Calculate total size for this product
          let productTotalSize = 0;
          devices.forEach(device => {
            const deviceSize = parseFloat(device.size_gb || 0);
            productTotalSize += deviceSize;
            //console.log(`Device ${device.device_id}: size = ${deviceSize}, product total now = ${productTotalSize}`);
          });

          // Add product header
          deviceIdRows.push([
            clientName,
            productName,
            `${devices.length} devices`,
            ''
          ].map(val => escapeCSV(val)));

          // Add product total
          deviceIdRows.push([
            '',
            'Total Size:',
            '',
            formatDataSize(productTotalSize)
          ].map(val => escapeCSV(val)));

          deviceIdRows.push(['']);

          // Add individual device rows
          devices.forEach(device => {
            // Ensure we get a valid size value
            const deviceSize = typeof device.size_gb === 'number' ?
              device.size_gb : parseFloat(device.size_gb || '0');

            // //console.log(`CSV row for device ${device.device_id}: size = ${deviceSize}`);

            deviceIdRows.push([
              '',
              '',
              device.device_id || 'Unknown',
              formatDataSize(deviceSize)
            ].map(val => escapeCSV(val)));
          });

          deviceIdRows.push(['']);
        });
      }

      // Create an Excel workbook with multiple sheets
      const workbook = XLSX.utils.book_new();

      // Create the main sheet
      const mainSheetData = rows.map(row => {
        // Convert each row to proper format for XLSX
        return row.map(cell => {
          // If cell is a string that starts with a number and has commas, convert to number
          if (typeof cell === 'string' && cell.match(/^[\d,.]+$/) && cell.includes('.')) {
            return parseFloat(cell.replace(/,/g, ''));
          }
          return cell;
        });
      });

      // Add the main sheet to the workbook
      const mainWorksheet = XLSX.utils.aoa_to_sheet(mainSheetData);
      XLSX.utils.book_append_sheet(workbook, mainWorksheet, "Client Analysis");

      // Add device ID data as a second sheet if available
      if (hasDeviceData) {
        //console.log("Adding device data to Excel:", deviceData);

        // Create the device ID sheet
        const deviceSheetData = deviceIdRows.map(row => {
          // Convert each row to proper format for XLSX
          return row.map(cell => {
            // If cell is a string that starts with a number and has commas, convert to number
            if (typeof cell === 'string' && cell.match(/^[\d,.]+$/) && cell.includes('.')) {
              return parseFloat(cell.replace(/,/g, ''));
            }
            return cell;
          });
        });

        // Add the device ID sheet to the workbook
        const deviceWorksheet = XLSX.utils.aoa_to_sheet(deviceSheetData);
        XLSX.utils.book_append_sheet(workbook, deviceWorksheet, "Device IDs");
      }

      // Generate the Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      // Create a Blob with the Excel data
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Create a link element
      const link = document.createElement('a');

      // Set the download attribute with the client name
      link.setAttribute('download', `${clientName}_${displayRegion.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);

      // Create a URL for the Blob
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      // Append to body, click programmatically, then remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccessMessage(`Successfully downloaded data for ${clientName}.`);
      setShowSuccess(true);
      setLoadingDownload(false);

    } catch (error) {
      console.error(`Error downloading client data:`, error);
      setErrorMessage(`Failed to download data for client "${clientName}": ${error.message || 'Unknown error'}`);
      setShowError(true);
      setLoadingDownload(false);
    }
  }, [api, region, fromDate, toDate, clientLogs, downloadType, clientLicenses, getFullClientName, setClientLicenses, setErrorMessage, setLoadingDownload, setShowError, setShowSuccess, setSuccessMessage, fetchProductDeviceIds, hasCostPermission, clients, PARENT_PATTERNS]);

  // Filter options for regions - memoized to prevent re-renders
  const regionOptions = useMemo(() => {
    const options = [{ value: '', label: 'All Regions' }];
    if (regions && regions.length) {
      regions.forEach(region => options.push({ value: region, label: region }));
    }
    return options;
  }, [regions]);

  // Get all child clients for a parent client
  const getChildClients = useCallback((parentName) => {
    if (!clients || !clients.length) return [];

    let childClients = [];

    // For PARENT_PATTERNS (special known parents), check if client starts with parent name
    if (PARENT_PATTERNS.includes(parentName)) {
      childClients = clients.filter(clientName =>
        clientName && typeof clientName === 'string' &&
        clientName !== parentName &&
        clientName.toLowerCase().startsWith(parentName.toLowerCase())
      );
    } else {
      // For other clients, detect children by prefix match (same rules as getParentClientName)
      childClients = clients.filter(clientName => {
        if (!clientName || typeof clientName !== 'string' ||
          clientName === parentName || PARENT_PATTERNS.includes(clientName)) {
          return false;
        }
        const parentLower = parentName.toLowerCase();
        const childLower = clientName.toLowerCase();
        return parentName.length >= 4 &&
          childLower.startsWith(parentLower) &&
          clientName.length > parentName.length &&
          getCanonicalClientKey(clientName) !== getCanonicalClientKey(parentName);
      });
    }

    // Add detailed logging to help troubleshoot server issues
    console.log(`Found ${childClients.length} child clients for parent ${parentName}:`, childClients);
    return childClients;
  }, [clients, PARENT_PATTERNS]);

  // Function to get parent client name from a child client
  const getParentClientName = useCallback((clientName) => {
    if (!clientName || typeof clientName !== 'string') {
      return clientName;
    }

    // Check for parent patterns
    for (const pattern of PARENT_PATTERNS) {
      if (clientName !== pattern && clientName.toLowerCase().startsWith(pattern.toLowerCase())) {
        return pattern;
      }
    }

    // Check for other parent-child relationships by looking for other clients
    // that could serve as parents of this one (minimum 4-char prefix, different canonical id)
    for (const potentialParent of clients) {
      // Skip if it's not a string, the same client, or a known parent pattern (already checked above)
      if (!potentialParent || typeof potentialParent !== 'string' ||
        potentialParent === clientName || PARENT_PATTERNS.includes(potentialParent)) {
        continue;
      }

      const parentLower = potentialParent.toLowerCase();
      const clientLower = clientName.toLowerCase();

      // Avoid false positives like BAN → Bandhan Life, BAL → BALMERLAWRIE, or
      // BANDHAN ↔ Bandhan Life (same customer, different spellings in DB).
      if (potentialParent.length >= 4 &&
        clientLower.startsWith(parentLower) &&
        clientName.length > potentialParent.length &&
        getCanonicalClientKey(clientName) !== getCanonicalClientKey(potentialParent)) {
        return potentialParent;
      }
    }

    // If no parent pattern or other client matches, the client is standalone
    return clientName;
  }, [PARENT_PATTERNS, clients]);

  // Check if a client is a parent client (has real child clients, not alias spellings)
  const isParentClient = useCallback((clientName) => {
    if (PARENT_PATTERNS.includes(clientName)) return true;

    return clients.some(name => {
      if (!name || typeof name !== 'string' || name === clientName) return false;
      const parentLower = clientName.toLowerCase();
      const childLower = name.toLowerCase();
      return clientName.length >= 4 &&
        childLower.startsWith(parentLower) &&
        name.length > clientName.length &&
        getCanonicalClientKey(name) !== getCanonicalClientKey(clientName);
    });
  }, [clients, PARENT_PATTERNS]);

  // Get client's region - memoized
  const getClientRegion = useCallback((clientName) => {
    if (!clientName || !clientLogs || !clientLogs.by_client) {
      console.log(`No client logs data available for ${clientName}`);
      return { regions: [], displayRegions: ["Unknown"] };
    }

    const clientData = findFirstByClientForCanonical(clientLogs.by_client, clientName);

    if (!clientData) {
      console.log(`No client data found for ${clientName} in clientLogs.by_client`);
      return { regions: [], displayRegions: ["Unknown"] };
    }

    if (!clientData.region) {
      console.log(`No region data found for ${clientName}`, clientData);

      // Check if we can find the region in another part of the data
      // For example, try to get it from by_product if available
      let regionFromProduct = null;
      if (clientLogs.by_product) {
        const productEntry = clientLogs.by_product.find(p => clientNamesMatchCanonical(p.client_name, clientName));
        if (productEntry && productEntry.region) {
          regionFromProduct = productEntry.region;
          console.log(`Found region ${regionFromProduct} for ${clientName} in by_product data`);
        }
      }

      // If we found a region in by_product, use that
      if (regionFromProduct) {
        return {
          regions: [regionFromProduct],
          displayRegions: [regionFromProduct],
          isGlobal: false
        };
      }

      // Check engagement type from license data
      const licenseInfo = clientLicenses[clientName];
      const engagementType = licenseInfo?.engagement_type || '';
      
      // BO Direct / BO Indirect = Blue Ocean
      if (engagementType === 'BO Direct' || engagementType === 'BO Indirect') {
        return {
          regions: ["Blue Ocean"],
          displayRegions: ["Blue Ocean"],
          isGlobal: false
        };
      }

      // Trend Micro Direct / Trend Micro Indirect = Trend Micro (default if engagement type is set)
      if (engagementType === 'Trend Micro Direct' || engagementType === 'Trend Micro Indirect' || (!engagementType && groupFilter === 'trend_micro')) {
        return {
          regions: ["Trend Micro"],
          displayRegions: ["Trend Micro"],
          isGlobal: false
        };
      }

      return { regions: [], displayRegions: ["Unknown"] };
    }

    // If we have a region, return it in the format expected by the UI
    const region = clientData.region;
    console.log(`Found region ${region} for ${clientName}`);
    return {
      regions: [region],
      displayRegions: [region],
      isGlobal: false
    };
  }, [clientLogs, clientLicenses, groupFilter]);

  // Get client log size from clientLogs data - memoized
  const getClientLogSize = useCallback((clientName) => {
    if (!clientName || !clientLogs || !clientLogs.by_client) {
      return { size: 0, products: 0 };
    }

    const matchProductRows = (name) => (p) =>
      !isExcludedProduct(p) && clientNamesMatchCanonical(p.client_name, name);

    const isParentPattern = PARENT_PATTERNS.includes(clientName);
    const shouldAggregateChildData = isParentPattern ||
      (showParentClientsOnly && isParentClient(clientName));

    if (shouldAggregateChildData) {
      const childClients = getChildClients(clientName);

      if (childClients.length === 0) {
        let productCount = 0;
        if (clientLogs.by_product) {
          productCount = new Set(
            clientLogs.by_product.filter(matchProductRows(clientName)).map(product => product.product)
          ).size;
        }

        return {
          size: sumByClientSizeForCanonicalName(clientLogs.by_client, clientName),
          products: productCount
        };
      }

      let totalSize = sumByClientSizeForCanonicalName(clientLogs.by_client, clientName);
      const uniqueProducts = new Set();

      if (clientLogs.by_product) {
        clientLogs.by_product
          .filter(matchProductRows(clientName))
          .forEach(product => {
            if (product.product) uniqueProducts.add(product.product);
          });
      }

      childClients.forEach(childName => {
        totalSize += sumByClientSizeForCanonicalName(clientLogs.by_client, childName);

        if (clientLogs.by_product) {
          clientLogs.by_product
            .filter(matchProductRows(childName))
            .forEach(product => {
              if (product.product) uniqueProducts.add(product.product);
            });
        }
      });

      console.log(`Aggregated data for ${clientName}: ${totalSize.toFixed(2)} GB from ${childClients.length} children`);
      console.log(`Child clients of ${clientName}:`, childClients);

      return {
        size: totalSize,
        products: uniqueProducts.size
      };
    }

    let productCount = 0;
    if (clientLogs.by_product) {
      productCount = new Set(
        clientLogs.by_product.filter(matchProductRows(clientName)).map(product => product.product)
      ).size;
    }

    return {
      size: sumByClientSizeForCanonicalName(clientLogs.by_client, clientName),
      products: productCount
    };
  }, [clientLogs, showParentClientsOnly, isParentClient, getChildClients, PARENT_PATTERNS]);

  // Count overutilized clients for reporting
  const overutilizedCount = useMemo(() => {
    if (!clients || !clients.length) return 0;

    return clients.filter(clientName => {
      // Get client size and license threshold 
      const { size } = getClientLogSize(clientName);
      const licenseThreshold = clientLicenses[clientName]?.license_per_day
        ? parseFloat(clientLicenses[clientName].license_per_day)
        : null;

      // Count if the client exceeds their threshold and has a threshold set
      return licenseThreshold !== null && size > licenseThreshold;
    }).length;
  }, [clients, getClientLogSize, clientLicenses]);

  // Check if a client's license is expiring soon (within 30 days) or already expired
  const checkLicenseExpiration = (clientName) => {
    if (!clientLicenses[clientName] || !clientLicenses[clientName].end_date) {
      return { isExpiring: false, isExpired: false, daysRemaining: 0 };
    }

    const endDate = new Date(clientLicenses[clientName].end_date);
    const today = new Date();

    // Set both dates to start of day for accurate comparison
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      isExpiring: diffDays > 0 && diffDays <= 30,
      isExpired: diffDays <= 0,
      daysRemaining: diffDays
    };
  };

  // Count clients with expired or expiring licenses - memoized
  const licenseExpirationStats = useMemo(() => {
    if (!clients || !clients.length) return { expired: 0, expiring: 0, total: 0 };

    let expired = 0;
    let expiring = 0;

    clients.forEach(clientName => {
      const { isExpired, isExpiring } = checkLicenseExpiration(clientName);
      if (isExpired) expired++;
      else if (isExpiring) expiring++;
    });

    return {
      expired,
      expiring,
      total: expired + expiring
    };
  }, [clients, checkLicenseExpiration]);

  // Filter clients based on search term and overutilized state - memoized for performance
  const filteredClients = useMemo(() => {
    if (!clients || !clients.length) return [];

    const searchLower = searchTerm.toLowerCase();
    // First filter by search and exclusions
    const searchFiltered = clients.filter(clientName => {
      // Match abbreviated id or mapped display name (e.g. LYRANET ↔ Lyra Network)
      const displayName = getFullClientName(clientName);
      const matchesSearch =
        searchTerm === '' ||
        clientName.toLowerCase().includes(searchLower) ||
        displayName.toLowerCase().includes(searchLower);
      // Basic name filtering and exclusions
      return matchesSearch &&
        !isExcludedInternalClient(clientName) &&
        !clientName.toLowerCase().includes('listener') &&
        clientName !== 'EXTRA' &&
        clientName !== 'log_size_monitor' &&
        clientName !== 'rsyslog_size_history' &&
        clientName !== 'disk_usage_monitor' &&
        clientName !== 'Advisory' &&
        clientName !== 'rsyslog1' &&
        clientName !== 'EventusBOM' &&
        clientName !== 'eventus_demo' &&
        clientName !== 'errorlogs'
    });

    // Apply parent/child filter if the parent-only view is active
    let viewFiltered = searchFiltered;
    if (showParentClientsOnly) {
      const parentsList = [];
      const childClientsList = new Set();

      // First identify all child clients
      clients.forEach(clientName => {
        // Check if this client has a parent
        const parentName = getParentClientName(clientName);
        if (parentName !== clientName) {
          // This is a child client
          childClientsList.add(clientName);
        }
      });

      // Add known parent patterns that have children in the client list
      PARENT_PATTERNS.forEach(pattern => {
        // Check if this pattern has any children
        const hasChildren = clients.some(clientName =>
          clientName !== pattern &&
          clientName.toLowerCase().startsWith(pattern.toLowerCase())
        );

        // Only include if it has children and matches search term (if any)
        if (hasChildren &&
          (searchTerm === '' || pattern.toLowerCase().includes(searchTerm.toLowerCase()))) {
          parentsList.push(pattern);
        }
      });

      // Add all other clients that are either parents or standalone (not children)
      searchFiltered.forEach(clientName => {
        // Skip if already in parentsList
        if (parentsList.includes(clientName)) return;

        // Skip if this is a child client — unless the user is searching; then show direct matches
        if (childClientsList.has(clientName) && searchTerm === '') return;

        // Add to parent list (this is either a parent or standalone client)
        parentsList.push(clientName);
      });

      viewFiltered = parentsList;
    }

    // Apply group filter if active
    if (groupFilter !== 'all') {
      viewFiltered = viewFiltered.filter(clientName => {
        // Get engagement type from license data
        const licenseInfo = clientLicenses[clientName];
        const engagementType = licenseInfo?.engagement_type || '';
        
        if (groupFilter === 'blue_ocean') {
          return engagementType === 'BO Direct' || engagementType === 'BO Indirect';
        } else if (groupFilter === 'trend_micro') {
          return engagementType === 'Trend Micro Direct' || engagementType === 'Trend Micro Indirect';
        } else if (groupFilter === 'tm_direct') {
          return engagementType === 'Trend Micro Direct';
        } else if (groupFilter === 'tm_indirect') {
          return engagementType === 'Trend Micro Indirect';
        } else if (groupFilter === 'bo_direct') {
          return engagementType === 'BO Direct';
        } else if (groupFilter === 'bo_indirect') {
          return engagementType === 'BO Indirect';
        }
        return true;
      });
    }

    // Apply engagement model filter if active
    if (engagementModelFilter !== 'all') {
      viewFiltered = viewFiltered.filter(clientName => {
        const licenseInfo = clientLicenses[clientName];
        const engagementModel = licenseInfo?.engagement_model || '';
        return engagementModel === engagementModelFilter;
      });
    }

    // Apply overutilized filter if active
    if (showOverutilizedOnly) {
      viewFiltered = viewFiltered.filter(clientName => {
        // Special handling for parent patterns like DICT
        if (PARENT_PATTERNS.includes(clientName)) {
          // Always check child clients for overutilization for parent patterns
          const childClients = getChildClients(clientName);
          return childClients.some(childName => {
            const { size } = getClientLogSize(childName);
            const licenseThreshold = clientLicenses[childName]?.license_per_day
              ? parseFloat(clientLicenses[childName].license_per_day)
              : null;

            return licenseThreshold !== null && size > licenseThreshold;
          });
        }

        // Regular overutilized check for normal clients
        const { size } = getClientLogSize(clientName);
        const licenseThreshold = clientLicenses[clientName]?.license_per_day
          ? parseFloat(clientLicenses[clientName].license_per_day)
          : null;

        // Only include if the client exceeds their threshold and has a threshold set
        return licenseThreshold !== null && size > licenseThreshold;
      });
    }

    // Apply license expiration filter if active
    if (showExpiringLicensesOnly) {
      viewFiltered = viewFiltered.filter(clientName => {
        const { isExpired, isExpiring } = checkLicenseExpiration(clientName);
        return isExpired || isExpiring;
      });
    }

    // One row per logical client (DB often has alias + abbrev + casing variants)
    return dedupeClientsByCanonicalKey(viewFiltered, clientLicenses, clientLogs);
  }, [clients, searchTerm, showOverutilizedOnly, getClientLogSize, clientLicenses, showParentClientsOnly, getParentClientName, isParentClient, getChildClients, PARENT_PATTERNS, clientLogs, checkLicenseExpiration, showExpiringLicensesOnly, groupFilter, engagementModelFilter, getFullClientName]);

  // Prepare chart data for products - memoized
  const getProductChartData = useCallback((clientName) => {
    if (!clientName) return null;

    // Check if we have client details
    if (!clientDetails[clientName]) {
      return {
        labels: ['No Data'],
        datasets: [{
          label: 'Log Size (GB)',
          data: [0],
          backgroundColor: getColor(0, 0.7),
          borderColor: getColor(0),
          borderWidth: 1
        }]
      };
    }

    // Get logs data with fallbacks
    const logs = clientDetails[clientName].logs || { by_product: [] };

    // Filter products for this client and ensure required properties
    const products = (logs.by_product || [])
      .filter(product => clientNamesMatchCanonical(product.client_name, clientName) && !isExcludedProduct(product))
      .map(product => ({
        ...product,
        product: product.product || 'Unknown Product',
        size_gb: typeof product.size_gb === 'number' ? product.size_gb : 0
      }));

    // Return fallback data if no products
    if (!products.length) {
      return {
        labels: ['No Products'],
        datasets: [{
          label: 'Log Size (GB)',
          data: [0],
          backgroundColor: getColor(0, 0.7),
          borderColor: getColor(0),
          borderWidth: 1
        }]
      };
    }

    // Sort products by size for better visualization
    products.sort((a, b) => b.size_gb - a.size_gb);

    return {
      labels: products.map(product => product.product),
      datasets: [{
        label: 'Log Size (GB)',
        data: products.map(product => product.size_gb),
        backgroundColor: products.map((_, i) => getColor(i, 0.7)),
        borderColor: products.map((_, i) => getColor(i)),
        borderWidth: 1
      }]
    };
  }, [clientDetails]);

  // Helper function to get colors
  function getColor(index, alpha = 1) {
    const colors = [
      `rgba(255, 91, 141, ${alpha})`,   // pink - #ff5b8d
      `rgba(121, 82, 246, ${alpha})`,   // purple - #7952f6
      `rgba(91, 229, 255, ${alpha})`,   // blue - #5be5ff
      `rgba(255, 158, 94, ${alpha})`,   // orange - #ff9e5e
      `rgba(76, 175, 80, ${alpha})`,    // green
      `rgba(255, 152, 0, ${alpha})`,    // orange
      `rgba(244, 67, 54, ${alpha})`,    // red
      `rgba(156, 39, 176, ${alpha})`,   // purple
      `rgba(3, 169, 244, ${alpha})`,    // light blue
      `rgba(0, 150, 136, ${alpha})`,    // teal
    ];
    return colors[index % colors.length];
  }

  // Create filter actions element for the header - memoized
  const filterActions = useMemo(() => (
    <Box>
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterOptions={regionOptions}
        filterValue={region}
        onFilterChange={setRegion}
        onRefresh={handleRefresh}
        placeholder="Search clients..."
        filterLabel="Region"
        filterWidth={150}
      />

      <Box display="flex" alignItems="center" mt={2} mb={1} gap={1.5}>
        {/* Standard date filter boxes directly in the UI */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: '8px',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <DateRangeIcon sx={{ color: theme.palette.primary.main, ml: 0.5 }} />

          <TextField
            type="date"
            size="small"
            label="From"
            value={fromDate}
            onChange={handleFromDateChange}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 150,
              '& .MuiOutlinedInput-root': {
                height: '35px',
              }
            }}
          />

          <TextField
            type="date"
            size="small"
            label="To"
            value={toDate}
            onChange={handleToDateChange}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 150,
              '& .MuiOutlinedInput-root': {
                height: '35px',
              }
            }}
          />

          <Button
            size="small"
            variant="contained"
            onClick={handleApplyDateFilter}
            disabled={!fromDate || !toDate || loadingDateFilter}
            sx={{
              height: '35px',
              minWidth: '90px',
              borderRadius: '6px',
              textTransform: 'none',
            }}
          >
            {loadingDateFilter ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              'Apply'
            )}
          </Button>

          {(fromDate && toDate) && (
            <IconButton
              size="small"
              onClick={handleClearDateFilter}
              disabled={loadingDateFilter}
              sx={{
                backgroundColor: alpha(theme.palette.error.light, 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.light, 0.2),
                }
              }}
            >
              <CloseIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
            </IconButton>
          )}
        </Box>

        {/* Overutilized Filter Button */}
        <Tooltip title={showOverutilizedOnly
          ? "Currently showing only clients exceeding their license threshold"
          : "Filter to show only clients exceeding their license threshold"
        }>
          <Button
            variant={showOverutilizedOnly ? "contained" : "outlined"}
            color="error"
            startIcon={<WarningIcon sx={{ fontSize: 16 }} />}
            onClick={toggleOverutilizedFilter}
            size="small"
            endIcon={overutilizedCount > 0 &&
              <Chip
                label={overutilizedCount}
                size="small"
                sx={{
                  height: 20,
                  minWidth: 20,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  backgroundColor: showOverutilizedOnly ? alpha('#fff', 0.3) : theme.palette.error.main,
                  color: showOverutilizedOnly ? '#fff' : '#fff',
                }}
              />
            }
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 'medium',
              boxShadow: 'none',
              backgroundColor: showOverutilizedOnly ? theme.palette.error.main : alpha(theme.palette.error.main, 0.1),
              color: showOverutilizedOnly ? 'white' : theme.palette.error.main,
              border: showOverutilizedOnly ? 'none' : `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              '&:hover': {
                backgroundColor: showOverutilizedOnly ? alpha(theme.palette.error.main, 0.9) : alpha(theme.palette.error.main, 0.2),
                boxShadow: 'none',
              },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {showOverutilizedOnly ? 'Showing Overutilized' : 'Filter Overutilized'}
          </Button>
        </Tooltip>

        {/* Add expiring licenses filter button here - alongside other filter buttons */}
        {licenseExpirationStats.total > 0 && (
          <Tooltip title={showExpiringLicensesOnly
            ? "Currently showing only clients with expiring or expired licenses"
            : "Filter to show only clients with expiring or expired licenses"
          }>
            <Button
              variant={showExpiringLicensesOnly ? "contained" : "outlined"}
              color="warning"
              startIcon={<EventBusyIcon sx={{ fontSize: 16 }} />}
              onClick={() => toggleExpiringLicensesFilter()}
              size="small"
              endIcon={licenseExpirationStats.total > 0 &&
                <Chip
                  label={licenseExpirationStats.total}
                  size="small"
                  sx={{
                    height: 20,
                    minWidth: 20,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    backgroundColor: showExpiringLicensesOnly ? alpha('#fff', 0.3) : theme.palette.warning.main,
                    color: showExpiringLicensesOnly ? '#fff' : '#fff',
                  }}
                />
              }
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 'medium',
                boxShadow: 'none',
                backgroundColor: showExpiringLicensesOnly ? theme.palette.warning.main : alpha(theme.palette.warning.main, 0.1),
                color: showExpiringLicensesOnly ? 'white' : theme.palette.warning.main,
                border: showExpiringLicensesOnly ? 'none' : `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                '&:hover': {
                  backgroundColor: showExpiringLicensesOnly ? alpha(theme.palette.warning.main, 0.9) : alpha(theme.palette.warning.main, 0.2),
                  boxShadow: 'none',
                },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {showExpiringLicensesOnly ? 'Showing Expiring' : 'Filter Expiring'}
            </Button>
          </Tooltip>
        )}


        {/* Add license expiration filter button here - alongside other filter buttons */}
        {(fromDate && toDate) && (
          <Chip
            icon={loadingDateFilter ? <CircularProgress size={16} /> : <DateRangeIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />}
            label={loadingDateFilter ? "Loading..." : `${fromDate} to ${toDate}`}
            size="small"
            onDelete={!loadingDateFilter ? handleClearDateFilter : undefined}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              fontWeight: 'medium',
              borderRadius: '10px',
              px: 1,
              py: 0.6,
              height: 30,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              '& .MuiChip-label': {
                px: 0.8,
              },
              '& .MuiChip-icon': {
                ml: 0.5,
              },
              '& .MuiChip-deleteIcon': {
                color: theme.palette.primary.main,
                '&:hover': {
                  color: theme.palette.primary.dark,
                },
              },
            }}
          />
        )}
      </Box>
    </Box>
  ), [
    searchTerm,
    region,
    regionOptions,
    handleRefresh,
    // Remove showDateFilter
    fromDate,
    toDate,
    // Remove toggleDateFilter
    handleFromDateChange,
    handleToDateChange,
    handleApplyDateFilter,
    handleClearDateFilter,
    loadingDateFilter,
    theme.palette.primary.main,
    showOverutilizedOnly,
    toggleOverutilizedFilter,
    theme.palette.error.main,
    overutilizedCount,
    groupFilter,
    handleGroupFilterChange,
    engagementModelFilter,
    handleEngagementModelFilterChange
  ]);

  // Handle closing error snackbar
  const handleCloseError = useCallback(() => {
    setShowError(false);
  }, []);

  // Handle opening of more actions menu
  const handleMenuOpen = useCallback((event, clientName) => {
    setAnchorEl(event.currentTarget);
    setSelectedClient(clientName);
  }, []);

  // Handle closing of more actions menu
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Fetch license data for a client is now moved to the top of the file

  // Handle opening view details dialog
  const handleOpenViewDetailsDialog = useCallback(() => {
    handleMenuClose();

    // Log the view details action
    try {
      api.post('/api/log_client_action', {
        client_name: selectedClient,
        action_type: 'VIEW_DETAILS',
        details: {
          region: region || 'All Regions'
        }
      });
    } catch (error) {
      console.error('Error logging view details action:', error);
    }

    // Fetch current license data
    fetchLicenseData(selectedClient);

    // Fetch cost data with selected date
    fetchClientCostData(selectedClient, selectedCostDate);

    // Open dialog
    setOpenViewDetailsDialog(true);
  }, [handleMenuClose, selectedClient, fetchLicenseData, fetchClientCostData, selectedCostDate, api, region]);

  // Handle closing view details dialog
  const handleCloseViewDetailsDialog = useCallback(() => {
    setOpenViewDetailsDialog(false);
  }, []);

  // Handle saving license data
  const handleSaveLicenseData = useCallback(async () => {
    if (!selectedClient) return;

    setSavingLicense(true);

    try {
      // Log the license save action
      try {
        await api.post('/api/log_client_action', {
          client_name: selectedClient,
          action_type: 'SAVE_LICENSE',
          details: {
            region: region || 'All Regions'
          }
        });
      } catch (error) {
        console.error('Error logging license save action:', error);
      }

      // Get API base URL from env or use default
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

      // Create the URL for saving license data
      const url = `${API_BASE_URL}/api/clients/${selectedClient}/license`;

      // Get user info from localStorage
      const userStr = localStorage.getItem('user');
      let username = 'unknown';
      let role = 'user';

      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          username = user.username || 'unknown';
          role = user.role || 'user';
          //console.log('Using username and role from localStorage:', username, role);
        } catch (e) {
          console.warn('Failed to parse user data from localStorage', e);
        }
      }

      // Log the license data being sent
      //console.log('Sending license data:', JSON.stringify(licenseData));

      // Send POST request to save license data
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Name': username,
          'X-User-Role': role
        },
        body: JSON.stringify(licenseData),
      });

      const data = await response.json();
      //console.log('License save response:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save license data');
      }

      // Close dialog
      setOpenLicenseDialog(false);

      // Show success message
      setSuccessMessage(`License details for ${selectedClient} saved successfully!`);
      setShowSuccess(true);

      // Refresh the license data to verify it was saved
      //console.log('Refreshing license data after save...');
      setTimeout(async () => {
        try {
          // Fetch the updated license data
          await fetchLicenseData(selectedClient);
          //console.log('License data refreshed successfully');

          // Also refresh all client licenses to update the UI
          await fetchAllClientLicenses();
          //console.log('All client licenses refreshed');
        } catch (refreshError) {
          console.error('Error refreshing license data:', refreshError);
        }
      }, 1000);
    } catch (error) {
      //console.error('Error saving license data:', error);
      setErrorMessage(`Error saving license data: ${error.message}`);
      setShowError(true);
    } finally {
      setSavingLicense(false);
    }
  }, [selectedClient, licenseData, fetchLicenseData, fetchAllClientLicenses, api, region]);

  // Handle license data changes
  const handleLicenseDataChange = useCallback((field, value) => {
    setLicenseData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Calculate client cost based on log size
  const calculateClientCost = useCallback((clientName) => {
    const costPerGBPerMonth = 5;
    const sizeGb = sumByClientSizeForCanonicalName(clientLogs.by_client, clientName);
    const monthlyPrice = sizeGb * costPerGBPerMonth;
    const yearlyPrice = monthlyPrice * 12;

    return {
      monthly: monthlyPrice.toFixed(2),
      yearly: yearlyPrice.toFixed(2)
    };
  }, [clientLogs]);

  // Handle closing license dialog
  const handleCloseLicenseDialog = useCallback(() => {
    setOpenLicenseDialog(false);
  }, []);

  // Handle opening license dialog
  const handleOpenLicenseDialog = useCallback(() => {
    handleMenuClose();

    // Log the license view action
    try {
      api.post('/api/log_client_action', {
        client_name: selectedClient,
        action_type: 'VIEW_LICENSE',
        details: {
          region: region || 'All Regions'
        }
      });
    } catch (error) {
      console.error('Error logging license view action:', error);
    }

    // Fetch current license data
    fetchLicenseData(selectedClient);

    // Open dialog
    setOpenLicenseDialog(true);
  }, [handleMenuClose, selectedClient, fetchLicenseData, api, region]);

  // Handle downloading all clients
  const handleDownloadAll = useCallback(async () => {
    // Open dialog to ask for download options instead of immediately downloading
    setDownloadTarget(null); // No specific client, downloading all
    setDownloadType('client'); // Default to client-wise download
    setDownloadConfirmOpen(true);
  }, [setDownloadConfirmOpen, setDownloadTarget, setDownloadType]);

  // Process download after confirmation
  const processDownload = useCallback(async () => {
    setDownloadConfirmOpen(false);

    if (downloadTarget) {
      // Individual client download
      handleDownload(downloadTarget);
    } else {
      // All clients download - use filtered clients from UI (includes groupFilter, search, etc.)
      try {
        //console.log('Download all clients requested');
        setLoadingDownload(true);

        // Use filteredClients from UI which already includes groupFilter (Trend Micro/Blue Ocean), search, and other filters
        if (!filteredClients || filteredClients.length === 0) {
          setErrorMessage('No clients found');
          setShowError(true);
          setLoadingDownload(false);
          return;
        }

        // Function to escape CSV values properly
        const escapeCSV = (value) => {
          if (value === null || value === undefined) {
            return '';
          }

          const stringValue = String(value);
          if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('\r')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        };

        // Build header information
        const rows = [];
        const displayRegion = region || "All Regions";
        const exportDate = new Date().toISOString().slice(0, 10);
        const hasDateRange = !!(fromDate && toDate);

        // Add summary section
        rows.push(['Consolidated Client Data with Cost Analysis']);
        rows.push(['Region:', displayRegion]);
        rows.push(['Date Range:', hasDateRange ? `${fromDate} to ${toDate}` : 'All Time']);
        rows.push(['Total Clients:', filteredClients.length]);
        rows.push(['Export Date:', exportDate]);
        rows.push(['']);

        // Create main data header row based on download type
        if (downloadType === 'client') {
          // Client-wise download with license info
          const headerRow = [
            'Client ID',
            'Client Name',
            'Parent Client',
            'License Start Date',
            'License End Date',
            'Threshold (GB/day)',
            'Online Retention (Months)',
            'Offline Retention (Months)',
            'Engagement Type',
            'Engagement Model',
            'Total Size'
          ];

          // Add cost columns only if user has permission
          if (hasCostPermission) {
            headerRow.push('Total Cost (₹)');
            headerRow.push('Cost/GB (₹)');
          }

          // Only add Average column if we have a date range
          if (fromDate && toDate && fromDate !== toDate) {
            headerRow.push('Average/Day');
          }

          headerRow.push('Region');

          rows.push(headerRow.map(escapeCSV));
        } else if (hasDateRange) {
          // Product-wise with date range, organize by date with size columns only (matching single client format)
          const dates = getDatesInRange(fromDate, toDate);

          // Create headers for each date (size only - matching single client download format)
          const dateHeaders = [];
          dates.forEach(date => {
            dateHeaders.push(date);
          });

          const headerRow = [
            'Client ID',
            'Client Name',
            'Parent Client',
            'Product',
            'Region',
            'License Start Date',
            'License End Date',
            'Threshold (GB/day)',
            'Online Retention (Months)',
            'Offline Retention (Months)',
            'Engagement Type',
            'Engagement Model',
            ...dateHeaders,
            'Total Size'
          ];

          // Add cost columns only if user has permission
          if (hasCostPermission) {
            headerRow.push('Total Cost (₹)');
            headerRow.push('Cost/GB (₹)');
          }

          rows.push(headerRow.map(escapeCSV));
        }

        // First identify all parent-child relationships
        const parentChildMap = {};

        filteredClients.forEach(clientName => {
          // For PARENT_PATTERNS like DICT
          for (const pattern of PARENT_PATTERNS) {
            if (clientName !== pattern && clientName.toLowerCase().startsWith(pattern.toLowerCase())) {
              if (!parentChildMap[pattern]) {
                parentChildMap[pattern] = [];
              }
              parentChildMap[pattern].push(clientName);
            }
          }

          // For other parent-child relationships
          filteredClients.forEach(potentialParent => {
            if (potentialParent === clientName || PARENT_PATTERNS.includes(potentialParent)) {
              return;
            }

            if (clientName !== potentialParent &&
              clientName.toLowerCase().startsWith(potentialParent.toLowerCase()) &&
              !parentChildMap[clientName]) {
              if (!parentChildMap[potentialParent]) {
                parentChildMap[potentialParent] = [];
              }
              parentChildMap[potentialParent].push(clientName);
            }
          });
        });

        // Process clients in batches to improve performance
        const processedClientIds = new Set(); // Track which clients have been processed
        const batchSize = 5;

        for (let i = 0; i < filteredClients.length; i += batchSize) {
          const batch = filteredClients.slice(i, i + batchSize);

          // Create a loading message
          if (i > 0) {
            setLoadingDownload(true);
            setSuccessMessage(`Processing clients ${i + 1}-${Math.min(i + batchSize, filteredClients.length)} of ${filteredClients.length}...`);
            setShowSuccess(true);
          }

          // Process batch in parallel
          await Promise.all(batch.map(async (clientName) => {
            try {
              // Skip if this client has already been processed
              if (processedClientIds.has(clientName)) {
                return;
              }

              // Skip child clients in client-wise mode as they'll be included with parents
              if (downloadType === 'client') {
                let isChild = false;
                for (const parentId in parentChildMap) {
                  if (parentChildMap[parentId].includes(clientName)) {
                    isChild = true;
                    break;
                  }
                }

                if (isChild) {
                  // Mark as processed
                  processedClientIds.add(clientName);
                  return;
                }
              }

              // Fetch cost data using the same API as individual download
              const params = new URLSearchParams({
                region: region || '',
                from_date: fromDate || '',
                to_date: toDate || ''
              });

              const costResponse = await api.getWithAuth(`/api/clients/${clientName}/costs?${params}`);
              const costData = costResponse.data;

              // Get daily data if available
              const dailyData = costData.debug?.daily_data || {};

              // Calculate total client size from daily data
              let totalClientSize = 0;
              if (dailyData && Object.keys(dailyData).length > 0) {
                Object.values(dailyData).forEach(day => {
                  if (day.client_rsyslog_size) {
                    totalClientSize += day.client_rsyslog_size;
                  }
                });
              } else {
                // Fallback to regular size if no daily data
                totalClientSize = costData.rsyslog_size_gb || 0;
              }

              // Get client data and product data
              const clientData = findFirstByClientForCanonical(clientLogs?.by_client, clientName);
              const productData = clientLogs?.by_product?.filter(product => clientNamesMatchCanonical(product.client_name, clientName) && !isExcludedProduct(product)) || [];

              // If no client data or products, skip
              if (!clientData && productData.length === 0) {
                processedClientIds.add(clientName);
                return;
              }

              // Fetch license data
              let licenseInfo = clientLicenses[clientName] || {};
              if (!licenseInfo.startDate && !licenseInfo.license_data) {
                try {
                  const licenseResponse = await api.getWithAuth(`/api/clients/${clientName}/license`);
                  if (licenseResponse.data && licenseResponse.data.license_data) {
                    licenseInfo = licenseResponse.data.license_data;
                  }
                } catch (err) {
                  console.error(`Error fetching license for ${clientName}`, err);
                }
              }

              // Extract license information, checking both formats
              const startDate = licenseInfo.start_date || licenseInfo.startDate || '';
              const endDate = licenseInfo.end_date || licenseInfo.endDate || '';
              const threshold = licenseInfo.license_per_day || licenseInfo.licensePerDay || '';
              const onlineRetention = licenseInfo.online_retention || licenseInfo.onlineRetention || '';
              const offlineRetention = licenseInfo.offline_retention || licenseInfo.offlineRetention || '';
              const engagementType = licenseInfo.engagement_type || '';
              const engagementModel = licenseInfo.engagement_model || '';

              // Check if this is a parent with children
              const isParent = !!parentChildMap[clientName];
              const childClients = parentChildMap[clientName] || [];

              // Calculate consolidated size for parent clients in client-wise mode
              let consolidatedSize = totalClientSize;
              if (downloadType === 'client' && isParent) {
                // First process all child clients
                for (const childName of childClients) {
                  // Mark child as processed
                  processedClientIds.add(childName);

                  // Get child client size
                  try {
                    const childCostResponse = await api.getWithAuth(`/api/clients/${childName}/costs?${params}`);
                    const childCostData = childCostResponse.data;

                    // Calculate child size the same way as the parent
                    let childSize = 0;
                    const childDailyData = childCostData.debug?.daily_data || {};

                    if (childDailyData && Object.keys(childDailyData).length > 0) {
                      Object.values(childDailyData).forEach(day => {
                        if (day.client_rsyslog_size) {
                          childSize += day.client_rsyslog_size;
                        }
                      });
                    } else {
                      childSize = childCostData.rsyslog_size_gb || 0;
                    }

                    consolidatedSize += childSize;
                  } catch (error) {
                    console.error(`Error fetching data for child client ${childName}:`, error);
                  }
                }
              }

              // Add row based on download type
              if (downloadType === 'client') {
                const dataRow = [
                  clientName,
                  getFullClientName(clientName) || clientName,
                  isParent ? '' : (Object.keys(parentChildMap).find(parent =>
                    parentChildMap[parent].includes(clientName)) || ''),
                  startDate,
                  endDate,
                  threshold,
                  onlineRetention,
                  offlineRetention,
                  engagementType || '',
                  engagementModel || '',
                  formatDataSize(consolidatedSize)
                ];

                // Add cost data only if user has permission
                if (hasCostPermission) {
                  dataRow.push(Number(costData.total_cost || 0).toFixed(2));
                  dataRow.push(Number(costData.cost_per_gb || 0).toFixed(2));
                }

                // Add average data if we have a date range
                if (fromDate && toDate && fromDate !== toDate) {
                  // Calculate the date range in days
                  const from = new Date(fromDate);
                  const to = new Date(toDate);
                  const diffTime = Math.abs(to - from);
                  const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates

                  dataRow.push(formatDataSize(consolidatedSize / days));
                }

                dataRow.push(displayRegion);

                rows.push(dataRow.map(escapeCSV));
              } else {
                // Product-wise download - iterate through products
                productData.forEach(product => {
                  if (hasDateRange) {
                    // Handle product-wise with dates
                    const dates = getDatesInRange(fromDate, toDate);
                    const row = [
                      clientName,
                      getFullClientName(clientName),
                      isParent ? `Parent (${childClients.length} child clients)` :
                        (Object.keys(parentChildMap).find(parent =>
                          parentChildMap[parent].includes(clientName)) || ''),
                      product.product || 'Unknown',
                      displayRegion,
                      startDate,
                      endDate,
                      threshold,
                      onlineRetention,
                      offlineRetention,
                      engagementType || '',
                      engagementModel || ''
                    ];

                    // Calculate product's proportion of total size
                    const proportion = totalClientSize > 0 ? (product.size_gb || 0) / totalClientSize : 0;

                    // Add size for each date (matching single client download format - no per-date costs)
                    dates.forEach(date => {
                      const dayData = dailyData[date];
                      const daySize = dayData ? (dayData.client_rsyslog_size || 0) : 0;
                      const productDaySize = daySize * proportion;

                      row.push(formatDataSize(productDaySize));
                    });

                    // Add totals
                    row.push(formatDataSize(product.size_gb || 0));
                    // Only add cost columns if user has permission (must match header structure)
                    if (hasCostPermission) {
                      row.push(((product.size_gb || 0) * costData.cost_per_gb).toFixed(2));
                      row.push(costData.cost_per_gb.toFixed(2));
                    }

                    rows.push(row.map(escapeCSV));
                  } else {
                    // Simple product-wise without dates
                    rows.push([
                      clientName,
                      getFullClientName(clientName),
                      isParent ? `Parent (${childClients.length} child clients)` :
                        (Object.keys(parentChildMap).find(parent =>
                          parentChildMap[parent].includes(clientName)) || ''),
                      product.product || 'Unknown',
                      formatDataSize(product.size_gb || 0),
                      ((product.size_gb || 0) * costData.cost_per_gb).toFixed(2),
                      costData.cost_per_gb.toFixed(2),
                      startDate,
                      endDate,
                      threshold,
                      onlineRetention,
                      offlineRetention,
                      engagementType || '',
                      engagementModel || '',
                      displayRegion
                    ].map(escapeCSV));
                  }
                });
              }

              // Mark client as processed
              processedClientIds.add(clientName);
            } catch (error) {
              console.error(`Error fetching data for client ${clientName}:`, error);
            }
          }));
        }

        // Create an Excel workbook
        const workbook = XLSX.utils.book_new();

        // Convert rows to proper format for XLSX
        const worksheetData = rows.map(row => {
          return row.map(cell => {
            // If cell is a string that starts with a number and has commas, convert to number
            if (typeof cell === 'string' && cell.match(/^[\d,.]+$/) && cell.includes('.')) {
              return parseFloat(cell.replace(/,/g, ''));
            }
            return cell;
          });
        });

        // Add the worksheet to the workbook
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "All Clients");

        // Generate the Excel file
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        // Create download link
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `all_clients_${downloadType}_${exportDate}.xlsx`);
        link.style.display = 'none';
        document.body.appendChild(link);

        // Trigger download
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setSuccessMessage('Download complete');
        setShowSuccess(true);
      } catch (error) {
        console.error('Error downloading all clients:', error);
        setErrorMessage(`Failed to download: ${error.message || 'Unknown error'}`);
        setShowError(true);
      } finally {
        setLoadingDownload(false);
      }
    }
  }, [
    downloadTarget,
    handleDownload,
    setDownloadConfirmOpen,
    setLoadingDownload,
    fromDate,
    toDate,
    region,
    downloadType,
    fetchClients,
    getDatesInRange,
    getFullClientName,
    setErrorMessage,
    setShowError,
    setSuccessMessage,
    setShowSuccess,
    PARENT_PATTERNS,
    clientLicenses,
    api,
    clientLogs,
    hasCostPermission,
    filteredClients
  ]);

  // Update the loadData function to include client licenses
  const loadData = async () => {
    try {
      setLoadingData(true);

      // Get today's date in YYYY-MM-DD format
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      // Create start and end date for today (start of day to end of day)
      const startOfDay = todayStr;
      const endOfDay = todayStr;

      // Build fetch parameters
      const fetchParams = {};

      // If date filter is active, use that
      if (fromDate && toDate) {
        // Get UTC date strings to handle timezone issues consistently
        const getUTCDateString = (dateStr) => {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(
            Date.UTC(year, month - 1, day)
          ).toISOString().split('T')[0];
        };

        // Make sure we're using consistent date formats for API
        const fromDateUTC = getUTCDateString(fromDate);
        const toDateUTC = getUTCDateString(toDate);

        // Use ISO formatted date strings for the API
        fetchParams.from_date = fromDateUTC;
        fetchParams.to_date = toDateUTC;
        //console.log(`Using date filter: ${fromDateUTC} to ${toDateUTC} (inclusive of the entire last day)`);
      } else {
        // Otherwise use today's date as default
        fetchParams.from_date = startOfDay;
        fetchParams.to_date = endOfDay;
        //console.log(`Using default today date range for loadData: ${startOfDay} to ${endOfDay}`);
      }

      // Use Promise.all to execute API calls in parallel for better performance
      const results = await Promise.all([
        // First batch of requests
        Promise.all([
          // Fetch total production costs first (independent of others)
          fetchTotalProductionCosts({
            region,
            from_date: fetchParams.from_date,
            to_date: fetchParams.to_date
          }),

          // Fetch clients data
          fetchClients(region, fetchParams)
        ]),

        // Second batch - depends on first batch but can run in parallel
        Promise.all([
          // Fetch client logs with the same parameters
          fetchClientLogs({
            region,
            ...fetchParams,
            forceRefresh: false // Use cache when available
          }),

          // Fetch license data (independent of the others)
          fetchAllClientLicenses()
        ])
      ]);

      // If a client is expanded, refresh its data
      if (expandedClient) {
        await fetchClientCostData(expandedClient);
      }

      calculateTotals();
    } catch (error) {
      console.error('Error loading data:', error);
      setErrorMessage('Failed to load data: ' + (error.message || 'Unknown error'));
      setShowError(true);
    } finally {
      setLoadingData(false);
    }
  };

  // Update dependency arrays for useEffect that react to region or date changes
  // Update production costs when region changes
  useEffect(() => {
    if (region) {
      // We don't want to auto-fetch when dates change, only when region changes
      // and only on initial load of a new region
      const currentRegion = new URLSearchParams(location.search).get('region');
      if (currentRegion === region) {
        //console.log(`Region matched URL parameter, not re-fetching data`);
        return;
      }

      //console.log(`Region changed to ${region}, updating URL only`);

      // Just update the URL, don't fetch data
      const params = new URLSearchParams(location.search);
      params.set('region', region);

      // Only update URL without triggering data load
      window.history.replaceState(
        {},
        '',
        `${location.pathname}?${params.toString()}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  // Always ensure all parent patterns are included in the clients list - memoized
  const ensureParentClients = useCallback(() => {
    if (!clients) return [];

    // Create a copy of clients to prevent direct state mutation
    const updatedClients = [...clients];

    // For each parent pattern, check if it exists in clients
    PARENT_PATTERNS.forEach(pattern => {
      if (!updatedClients.includes(pattern)) {
        // Check if any clients start with this pattern (are children)
        const hasChildren = clients.some(clientName =>
          clientName !== pattern && clientName.toLowerCase().startsWith(pattern.toLowerCase())
        );

        // Always include parent patterns if they have any children
        if (hasChildren) {
          updatedClients.push(pattern);
        }
      }
    });

    return updatedClients;
  }, [clients, PARENT_PATTERNS]);

  // Apply the ensureParentClients function when component mounts or clients change
  useEffect(() => {
    if (clients) {
      // Get an updated list with parent patterns included
      const updatedClients = ensureParentClients();

      // Check if we've added any new clients
      if (updatedClients.length > clients.length) {
        // If using fetchClients from context, you might need to call that
        // or use a different mechanism to update the clients list
        fetchClients(region);
      }
    }
  }, [clients, ensureParentClients, fetchClients, region]);

  // Inside the Clients component, modify where client names are displayed
  const renderClientName = (clientName) => {
    return getFullClientName(clientName);
  };

  // Inside the Clients component, update the chart data preparation
  const prepareChartData = (clientLogs) => {
    if (!clientLogs?.by_client) return null;

    return {
      labels: clientLogs.by_client.slice(0, 10).map(client => getFullClientName(client.client_name)),
      datasets: [{
        label: "Log Size (GB)",
        data: clientLogs.by_client.slice(0, 10).map(client => client.size_gb),
        backgroundColor: Array(10).fill().map((_, i) => alpha(getColor(i), 0.7)),
        borderColor: Array(10).fill().map((_, i) => getColor(i)),
        borderWidth: 1
      }]
    };
  };

  // Update the product chart data preparation
  const prepareProductChartData = (clientLogs) => {
    if (!clientLogs?.by_product) return null;

    const visibleProducts = clientLogs.by_product.filter(product => !isExcludedProduct(product));

    // For aggregated data (parent clients with children), we need to group by product
    if (clientLogs.is_aggregated) {
      console.log(`Preparing chart data for aggregated client with ${clientLogs.child_count} children`);

      // Group products by name and sum their sizes
      const productGroups = {};
      visibleProducts.forEach(product => {
        const productName = product.product;
        if (!productGroups[productName]) {
          productGroups[productName] = {
            product: productName,
            size_gb: 0,
            client_count: new Set(),
            clients: []
          };
        }
        productGroups[productName].size_gb += (product.size_gb || 0);
        productGroups[productName].client_count.add(product.client_name);
        // Store client name for display
        if (!productGroups[productName].clients.includes(product.client_name)) {
          productGroups[productName].clients.push(product.client_name);
        }
      });

      // Convert to array and sort by size
      const groupedProducts = Object.values(productGroups)
        .sort((a, b) => b.size_gb - a.size_gb)
        .slice(0, 10);

      return {
        labels: groupedProducts.map(product =>
          `${product.product} (${product.client_count.size} clients)`
        ),
        datasets: [{
          label: "Log Size (GB)",
          data: groupedProducts.map(product => product.size_gb),
          backgroundColor: Array(10).fill().map((_, i) => alpha(getColor(i), 0.7)),
          borderColor: Array(10).fill().map((_, i) => getColor(i)),
          borderWidth: 1
        }],
        // Add clients information for each product
        clientInfo: groupedProducts.map(product => ({
          product: product.product,
          clients: product.clients
        }))
      };
    }

    // Regular case - not aggregated
    return {
      labels: visibleProducts.slice(0, 10).map(product =>
        `${product.product} (${getFullClientName(product.client_name)})`
      ),
      datasets: [{
        label: "Log Size (GB)",
        data: visibleProducts.slice(0, 10).map(product => product.size_gb),
        backgroundColor: Array(10).fill().map((_, i) => alpha(getColor(i), 0.7)),
        borderColor: Array(10).fill().map((_, i) => getColor(i)),
        borderWidth: 1
      }]
    };
  };

  // Parse URL parameters on initial load for date filters
  const initialUrlParseRef = useRef(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromDateParam = params.get('from_date');
    const toDateParam = params.get('to_date');

    // Only process if both date parameters exist and it's either the initial load
    // or the URL has actually changed (preventing infinite loops)
    if (fromDateParam && toDateParam && initialUrlParseRef.current) {
      // Mark that we've processed the initial URL parameters
      initialUrlParseRef.current = false;

      //console.log(`Found date parameters in URL: ${fromDateParam} to ${toDateParam}`);

      try {
        // Function to create a date in local time from a YYYY-MM-DD string
        const createLocalDate = (dateStr) => {
          const [year, month, day] = dateStr.split('-').map(Number);
          // Create date with local year/month/day values (not UTC)
          return new Date(year, month - 1, day);
        };

        // Create date objects from the parameters - use local time
        const fromDateObj = createLocalDate(fromDateParam);
        const toDateObj = createLocalDate(toDateParam);

        // Skip invalid dates
        if (isNaN(fromDateObj.getTime()) || isNaN(toDateObj.getTime())) {
          console.error('Invalid date parameters in URL');
          return;
        }

        // Set start of day for from date
        fromDateObj.setHours(0, 0, 0, 0);

        // Set end of day for to date to make it inclusive
        toDateObj.setHours(23, 59, 59, 999);

        //console.log(`Parsed date objects from URL:`, {
        //   from: fromDateObj.toString(),
        //   to: toDateObj.toString()
        // });

        // Set the date range for the picker
        setDateRange({
          from: fromDateObj,
          to: toDateObj
        });

        // Update the state values
        const fromDateStr = fromDateParam;
        const toDateStr = toDateParam;

        setFromDate(fromDateStr);
        setToDate(toDateStr);

        // Load data directly instead of using applyDateFilter to avoid potential loops
        setLoadingDateFilter(true);

        Promise.all([
          fetchTotalProductionCosts({
            region,
            from_date: fromDateStr,
            to_date: toDateStr
          }),
          fetchClients(region, {
            from_date: fromDateStr,
            to_date: toDateStr
          }),
          fetchClientLogs({
            region,
            from_date: fromDateStr,
            to_date: toDateStr,
            forceRefresh: true
          })
        ])
          .then(() => {
            calculateTotals();
            setLoadingDateFilter(false);
          })
          .catch(error => {
            console.error('Error loading data from URL parameters:', error);
            setLoadingDateFilter(false);
            setErrorMessage("Error loading data with URL parameters");
            setShowError(true);
          });
      } catch (error) {
        console.error('Error parsing date parameters from URL:', error);
        setErrorMessage("Invalid date format in URL parameters");
        setShowError(true);
      }
    }
  }, [location.search, fetchTotalProductionCosts, fetchClients, fetchClientLogs, calculateTotals, region]);

  // Open download confirmation dialog
  const handleOpenDownloadConfirm = useCallback((clientName = null) => {
    setDownloadTarget(clientName);
    setDownloadConfirmOpen(true);
  }, []);

  // Close download confirmation dialog
  const handleCloseDownloadConfirm = useCallback(() => {
    setDownloadConfirmOpen(false);
  }, []);

  // Add this new useEffect hook after the other useEffect hooks
  // This will fetch license data when clients are loaded
  useEffect(() => {
    // Only fetch licenses if we have clients data and we're not already loading
    // and we haven't already loaded licenses in this session
    if (clients && clients.length > 0 && !loadingLicenses && !licensesLoadedRef.current) {
      // Set the ref to true immediately to prevent duplicate calls
      licensesLoadedRef.current = true;

      // Fetch all client licenses
      fetchAllClientLicenses()
        .then(() => {
          //console.log("License data loaded on initial mount");
        })
        .catch(error => {
          console.error("Error loading license data on initial mount:", error);
          // Don't reset the ref on error - we still don't want to retry automatically
        });
    }
  }, [clients, fetchAllClientLicenses, loadingLicenses]);

  // Function to handle opening the product device ID popup
  const handleOpenProductDevicePopup = async (clientName, productName) => {
    //console.log(`Opening device ID popup for client: ${clientName}, product: ${productName}`);

    setProductDevicePopup({
      open: true,
      clientName,
      productName,
      deviceData: [],
      loading: true
    });

    try {
      // Fetch device IDs for the product - use uppercase client name for consistency with backend
      //console.log(`Fetching device IDs with params - client: ${clientName}, product: ${productName}, region: ${region}`);
      const result = await fetchProductDeviceIds(clientName, productName, region, { from_date: fromDate, to_date: toDate });
      //console.log('Device IDs result:', result);

      if (result && result.device_data && result.device_data.length > 0) {
        //console.log(`Found ${result.device_data.length} device IDs`);

        // Store the device data in the popup state
        setProductDevicePopup(prev => ({
          ...prev,
          deviceData: result.device_data,
          loading: false
        }));

        // Also store in client details for CSV export
        setClientDetails(prev => {
          const clientDetail = prev[clientName] || {};
          const products = clientDetail.products || {};

          return {
            ...prev,
            [clientName]: {
              ...clientDetail,
              products: {
                ...products,
                [productName]: {
                  ...(products[productName] || {}),
                  device_data: result.device_data
                }
              }
            }
          };
        });
      } else {
        //console.log('No device IDs found');
        setProductDevicePopup(prev => ({
          ...prev,
          deviceData: [],
          loading: false
        }));
      }
    } catch (error) {
      console.error('Error fetching product device IDs:', error);
      setProductDevicePopup(prev => ({
        ...prev,
        deviceData: [],
        loading: false
      }));
      showErrorMessage('Failed to fetch device IDs for this product');
    }
  };

  // Function to handle closing the product device ID popup
  const handleCloseProductDevicePopup = () => {
    setProductDevicePopup({
      open: false,
      clientName: '',
      productName: '',
      deviceData: [],
      loading: false
    });
  };

  // Show error message function
  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setShowError(true);
  };

  // Handle date range change from the new picker
  const handleDateRangeChange = useCallback((newDateRange) => {
    if (!newDateRange?.from || !newDateRange?.to) return;

    // Get current formatted dates for comparison
    const getFormattedDate = (date) => {
      return date.toISOString().split('T')[0];
    };

    const newFromDateStr = getFormattedDate(newDateRange.from);
    const newToDateStr = getFormattedDate(newDateRange.to);

    // Check if the dates have actually changed to avoid unnecessary updates
    if (newFromDateStr === fromDate && newToDateStr === toDate) {
      //console.log("Date range unchanged, skipping update");
      return;
    }

    //console.log(`Date range changed: ${newFromDateStr} to ${newToDateStr}`);

    // Update the dateRange state
    setDateRange(newDateRange);

    // Apply the date filter directly with Date objects
    // This ensures proper handling of start/end of day
    applyDateFilter(newDateRange.from, newDateRange.to);

  }, [applyDateFilter, fromDate, toDate]);

  // Track whether date range update is from internal state change or URL
  const dateRangeUpdateRef = useRef({
    lastFromDate: null,
    lastToDate: null
  });

  // Update the date range when fromDate or toDate changes from URL params
  useEffect(() => {
    // Skip if values haven't changed to prevent loops
    if (fromDate === dateRangeUpdateRef.current.lastFromDate &&
      toDate === dateRangeUpdateRef.current.lastToDate) {
      return;
    }

    // Update last seen values
    dateRangeUpdateRef.current.lastFromDate = fromDate;
    dateRangeUpdateRef.current.lastToDate = toDate;

    if (fromDate && toDate) {
      // Function to create a date in local time from a YYYY-MM-DD string
      const createLocalDate = (dateStr) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        // Create date with local year/month/day values (not UTC)
        return new Date(year, month - 1, day);
      };

      // Create date objects and ensure proper time handling
      const fromDateObj = createLocalDate(fromDate);
      fromDateObj.setHours(0, 0, 0, 0); // Start of day

      const toDateObj = createLocalDate(toDate);
      toDateObj.setHours(23, 59, 59, 999); // End of day for inclusive filtering

      //console.log("Setting date range from state values:", {
      //   from: fromDateObj.toString(),
      //   to: toDateObj.toString()
      // });

      setDateRange({
        from: fromDateObj,
        to: toDateObj
      });
    }
  }, [fromDate, toDate]);

  // Add state for RSYSLOG data and Production Cost detail popups
  const [rsyslogDetailPopup, setRsyslogDetailPopup] = useState({
    open: false,
    loading: false
  });
  const [productionCostDetailPopup, setProductionCostDetailPopup] = useState({
    open: false,
    loading: false
  });

  // Handlers for RSYSLOG data detail popup
  const handleOpenRsyslogDetailPopup = () => {
    setRsyslogDetailPopup({
      open: true,
      loading: false
    });
  };

  const handleCloseRsyslogDetailPopup = () => {
    setRsyslogDetailPopup({
      open: false,
      loading: false
    });
  };

  // Handlers for Production Cost detail popup
  const handleOpenProductionCostDetailPopup = () => {
    setProductionCostDetailPopup({
      open: true,
      loading: false
    });
  };

  const handleCloseProductionCostDetailPopup = () => {
    setProductionCostDetailPopup({
      open: false,
      loading: false
    });
  };

  return (
    <LoadingOverlay loading={loadingData || loadingLicenses} text="Loading client data...">
      <Box sx={{ width: '100%', position: 'relative' }}>
        {/* Header with gradient background */}
        <Box
          sx={{
            background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
            borderRadius: '16px',
            mb: 3,
            p: 3,
            boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.05)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToDashboard}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      boxShadow: 'none',
                    },
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 'medium',
                    px: 2,
                  }}
                >
                  Back to Dashboard
                </Button>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                gutterBottom
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.5px',
                }}
              >
                {region ? `Clients in ${region} Region` : 'All Clients'}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mr: 2 }}>
                Monitor and manage client logs and details
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {/* <Typography variant="subtitle1" color="text.secondary" sx={{ mr: 2 }}>
                  Monitor and manage client logs and details
                </Typography> */}
                <DateRangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  isLoading={loadingDateFilter}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  height: '100%',
                  mt: { xs: 2, md: 0 }
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '10px',
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                    pl: 1.5,
                    flexGrow: 1,
                    transition: 'all 0.2s',
                    minWidth: '250px',
                    maxWidth: '400px',
                    '&:hover': {
                      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                  }}
                >
                  <SearchIcon sx={{ color: alpha(theme.palette.text.secondary, 0.7), fontSize: 18, mr: 1 }} />
                  <InputBase
                    placeholder="Search clients..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{
                      flexGrow: 1,
                      py: 1.2,
                      px: 1,
                      fontSize: '0.9rem',
                      width: '100%',
                      color: theme.palette.text.primary,
                      '& ::placeholder': {
                        color: alpha(theme.palette.text.secondary, 0.6),
                        opacity: 1,
                      },
                    }}
                  />
                </Paper>

                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: 150,
                  }}
                >
                  <Select
                    value={region}
                    onChange={handleRegionChange}
                    displayEmpty
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RegionIcon fontSize="small" sx={{ color: alpha(theme.palette.text.secondary, 0.8) }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selected || 'All Regions'}
                        </Typography>
                      </Box>
                    )}
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: alpha(theme.palette.background.paper, 0.8),
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                      height: '40px',
                      fontSize: '0.875rem',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                      '&.Mui-focused': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                      '& .MuiSelect-select': {
                        paddingRight: 3,
                        paddingLeft: 2,
                      },
                    }}
                    IconComponent={(props) => (
                      <Box
                        component="div"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'absolute',
                          right: 7,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: alpha(theme.palette.text.primary, 0.5),
                        }}
                      >
                        <ArrowDropDownIcon {...props} />
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        elevation: 3,
                        sx: {
                          mt: 0.5,
                          borderRadius: '10px',
                          border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                          backgroundColor: theme.palette.background.paper,
                          maxHeight: 300,
                          '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            padding: '8px 16px',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            },
                            '&.Mui-selected': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.12),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.18),
                              },
                            }
                          }
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RegionIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>All Regions</Typography>
                      </Box>
                    </MenuItem>
                    {regionOptions
                      .filter((opt) => opt.value !== '')
                      .map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RegionIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                          <Typography>{opt.label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Group By filter */}
                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: 150,
                  }}
                >
                  <Select
                    value={groupFilter}
                    onChange={handleGroupFilterChange}
                    displayEmpty
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeviceHubIcon fontSize="small" sx={{ color: alpha(theme.palette.text.secondary, 0.8) }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selected === 'all' ? 'All Clients' :
                           selected === 'blue_ocean' ? 'Blue Ocean' :
                           selected === 'trend_micro' ? 'Trend Micro' :
                           selected === 'tm_direct' ? 'TM Direct' :
                           selected === 'tm_indirect' ? 'TM Indirect' :
                           selected === 'bo_direct' ? 'BO Direct' :
                           selected === 'bo_indirect' ? 'BO Indirect' : selected}
                        </Typography>
                      </Box>
                    )}
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: alpha(theme.palette.background.paper, 0.8),
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                      height: '40px',
                      fontSize: '0.875rem',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                      '&.Mui-focused': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                      '& .MuiSelect-select': {
                        paddingRight: 3,
                        paddingLeft: 2,
                      },
                    }}
                    IconComponent={(props) => (
                      <Box
                        component="div"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'absolute',
                          right: 7,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: alpha(theme.palette.text.primary, 0.5),
                        }}
                      >
                        <ArrowDropDownIcon {...props} />
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        elevation: 3,
                        sx: {
                          mt: 0.5,
                          borderRadius: '10px',
                          border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                          backgroundColor: theme.palette.background.paper,
                          maxHeight: 300,
                          '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            padding: '8px 16px',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            },
                            '&.Mui-selected': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.12),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.18),
                              },
                            }
                          }
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeviceHubIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>All Clients</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="tm_direct">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeviceHubIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>TM Direct</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="tm_indirect">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeviceHubIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>TM Indirect</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="bo_direct">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeviceHubIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>BO Direct</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="bo_indirect">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeviceHubIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>BO Indirect</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Engagement Model filter */}
                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: 160,
                  }}
                >
                  <Select
                    value={engagementModelFilter}
                    onChange={handleEngagementModelFilterChange}
                    displayEmpty
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon fontSize="small" sx={{ color: alpha(theme.palette.text.secondary, 0.8) }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selected === 'all' ? 'All Models' : selected}
                        </Typography>
                      </Box>
                    )}
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: alpha(theme.palette.background.paper, 0.8),
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                      height: '40px',
                      fontSize: '0.875rem',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                      '&.Mui-focused': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                      '& .MuiSelect-select': {
                        paddingRight: 3,
                        paddingLeft: 2,
                      },
                    }}
                    IconComponent={(props) => (
                      <Box
                        component="div"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'absolute',
                          right: 7,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: alpha(theme.palette.text.primary, 0.5),
                        }}
                      >
                        <ArrowDropDownIcon {...props} />
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        elevation: 3,
                        sx: {
                          mt: 0.5,
                          borderRadius: '10px',
                          border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                          backgroundColor: theme.palette.background.paper,
                          maxHeight: 300,
                          '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            padding: '8px 16px',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            },
                            '&.Mui-selected': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.12),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.18),
                              },
                            }
                          }
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>All Models</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="MXDR">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>MXDR</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Third party">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>Third party</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Both">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>Both</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* DateRangePicker moved to header section */}

                <Tooltip title="Download All">
                  <IconButton
                    onClick={loadingDownload ? null : handleDownloadAll}
                    disabled={loadingDownload}
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2)
                      },
                      position: 'relative'
                    }}
                  >
                    {loadingDownload ? (
                      <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
                    ) : (
                      <GetAppIcon />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      color: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: '10px',
                      width: '40px',
                      height: '40px',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        transform: 'rotate(30deg)',
                      },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Region Summary Cards */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            gap: 2,
          }}
        >
          {/* Total RSYSLOG Data Card - Now wrapped with permission check */}
          <PermissionWidget permission={WIDGET_PERMISSIONS['rsyslog-data']} fallback={null}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '16px',
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 24px ${alpha(theme.palette.common.black, 0.08)}`,
                }
              }}
              onClick={handleOpenRsyslogDetailPopup}
            >
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StorageIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                  <Typography variant="h6" fontWeight="medium">Total RSYSLOG Data</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {loadingTotalCosts && (
                    <CircularProgress size={20} sx={{ color: theme.palette.primary.main, mr: 1 }} />
                  )}
                  <VisibilityIcon fontSize="small" sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                </Box>
              </Box>
              <Box sx={{ p: 3, pt: 0, display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'center' }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'baseline',
                  }}
                >
                  {formatDataSize(totalProductionCostData.rsyslog_size)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total RSYSLOG data size for {totalProductionCostData.region || (region ? `${region} region` : 'all regions')}
                  {fromDate && toDate ?
                    ` from ${fromDate} to ${toDate}` :
                    " (today only)"}
                </Typography>

                {/* Daily RSYSLOG Size Bar Chart - Only show for multiple days - Removed since we have popup for details */}

                {/* Cost per GB information */}
                {totalProductionCostData.cost_per_gb > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      size="small"
                      label={`Cost Per GB: ₹${totalProductionCostData.cost_per_gb.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 'medium',
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Card>
          </PermissionWidget>

          {/* Production Cost Card - Now wrapped with permission check */}
          <PermissionWidget permission={WIDGET_PERMISSIONS['production-cost']} fallback={null}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '16px',
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 24px ${alpha(theme.palette.common.black, 0.08)}`,
                }
              }}
              onClick={handleOpenProductionCostDetailPopup}
            >
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MoneyIcon sx={{ color: theme.palette.success.main, mr: 1 }} />
                  <Typography variant="h6" fontWeight="medium">Production Cost</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {loadingTotalCosts && (
                    <CircularProgress size={20} sx={{ color: theme.palette.success.main, mr: 1 }} />
                  )}
                  <VisibilityIcon fontSize="small" sx={{ color: theme.palette.success.main, opacity: 0.7 }} />
                </Box>
              </Box>
              <Box sx={{ p: 3, pt: 0, display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'center' }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: theme.palette.success.main,
                    display: 'flex',
                    alignItems: 'baseline',
                  }}
                >
                  ₹{totalProductionCostData.production_cost.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total production cost for {totalProductionCostData.region || (region ? `${region} region` : 'all regions')}
                  {fromDate && toDate ?
                    ` from ${fromDate} to ${toDate}` :
                    " (today only)"}
                </Typography>

                {/* Daily Production Cost Bar Chart - Only show for multiple days - Removed since we have popup for details */}

                {/* Cost breakdown information */}
                {totalProductionCostData.cost_details && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={`Compute: ₹${totalProductionCostData.cost_details.compute_cost.toLocaleString('en-IN')}`}
                      sx={{
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                        fontWeight: 'medium',
                        fontSize: '0.7rem',
                      }}
                    />
                    <Chip
                      size="small"
                      label={`Boot Vol: ₹${totalProductionCostData.cost_details.boot_volume_cost.toLocaleString('en-IN')}`}
                      sx={{
                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                        color: theme.palette.warning.main,
                        fontWeight: 'medium',
                        fontSize: '0.7rem',
                      }}
                    />
                    <Chip
                      size="small"
                      label={`Block Vol: ₹${totalProductionCostData.cost_details.block_volume_cost.toLocaleString('en-IN')}`}
                      sx={{
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        color: theme.palette.error.main,
                        fontWeight: 'medium',
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Card>
          </PermissionWidget>
        </Box>

        {/* Clients Table with updated styling */}
        <Card
          elevation={0}
          sx={{
            borderRadius: '16px',
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
            position: 'relative',
          }}
        >
          {/* Table Header */}
          <Box
            sx={{
              p: 2.5,
              background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              {region ? `Clients in ${region} Region` : 'All Clients'}
              {groupFilter !== 'all' && (
                <Typography component="span" variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                  ({groupFilter === 'blue_ocean' ? 'Blue Ocean' :
                    groupFilter === 'trend_micro' ? 'Trend Micro' :
                    groupFilter === 'tm_direct' ? 'TM Direct' :
                    groupFilter === 'tm_indirect' ? 'TM Indirect' :
                    groupFilter === 'bo_direct' ? 'BO Direct' :
                    groupFilter === 'bo_indirect' ? 'BO Indirect' : groupFilter})
                </Typography>
              )}
              {engagementModelFilter !== 'all' && (
                <Typography component="span" variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                  · {engagementModelFilter}
                </Typography>
              )}
              <Chip
                label={filteredClients.length}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                  ml: 1,
                  borderRadius: '6px',
                  height: 22,
                }}
              />
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {fromDate && toDate && (
                <Chip
                  icon={loadingDateFilter ? <CircularProgress size={16} /> : <DateRangeIcon sx={{ fontSize: 16 }} />}
                  label={loadingDateFilter ? "Loading..." : `${fromDate} to ${toDate}`}
                  size="small"
                  onDelete={!loadingDateFilter ? handleClearDateFilter : undefined}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    fontWeight: 'medium',
                    borderRadius: '10px',
                    px: 1,
                    py: 0.5,
                    height: 28,
                    '& .MuiChip-label': {
                      px: 0.5,
                    },
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.primary.main,
                      '&:hover': {
                        color: theme.palette.primary.dark,
                      },
                    },
                  }}
                />
              )}
              <Chip
                icon={<RegionIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />}
                label={region || 'All Regions'}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  fontWeight: 'medium',
                  borderRadius: '10px',
                  px: 1,
                  py: 0.6,
                  height: 30,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  '& .MuiChip-label': {
                    px: 0.8,
                  },
                  '& .MuiChip-icon': {
                    ml: 0.5,
                  },
                }}
              />

              {/* Parent/Child view toggle switch */}
              <Button
                variant={showParentClientsOnly ? "contained" : "outlined"}
                size="small"
                onClick={() => setShowParentClientsOnly(!showParentClientsOnly)}
                startIcon={<FolderIcon sx={{ fontSize: 16 }} />}
                sx={{
                  ml: 1.5,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 'medium',
                  boxShadow: 'none',
                  backgroundColor: showParentClientsOnly ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.1),
                  color: showParentClientsOnly ? 'white' : theme.palette.primary.main,
                  border: showParentClientsOnly ? 'none' : `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    backgroundColor: showParentClientsOnly ? alpha(theme.palette.primary.main, 0.9) : alpha(theme.palette.primary.main, 0.2),
                    boxShadow: 'none',
                  },
                }}
              >
                {showParentClientsOnly ? 'All Clients' : 'Show Parent Clients'}
              </Button>
            </Box>
          </Box>

          {/* License Expiration Alert - Compact Notification Above Table */}
          {licenseExpirationStats.total > 0 && (
            <Box
              sx={{
                mb: 1.5,
                py: 1,
                px: 1.5,
                borderRadius: '4px',
                border: `1px solid ${licenseExpirationStats.expired > 0
                  ? alpha(theme.palette.error.main, 0.6)
                  : alpha(theme.palette.warning.main, 0.6)}`,
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <WarningIcon
                  fontSize="small"
                  sx={{
                    color: theme.palette.error.main,
                  }}
                />
                <Typography
                  variant="body2"
                  color="error"
                  fontWeight="medium"
                >
                  {licenseExpirationStats.expired > 0
                    ? `${licenseExpirationStats.expired} client${licenseExpirationStats.expired !== 1 ? 's' : ''} with expired licenses${licenseExpirationStats.expiring > 0 ? `, and ${licenseExpirationStats.expiring} client${licenseExpirationStats.expiring !== 1 ? 's' : ''} with licenses expiring soon` : ''}`
                    : `${licenseExpirationStats.expiring} client${licenseExpirationStats.expiring !== 1 ? 's' : ''} with licenses expiring soon`
                  }
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => toggleExpiringLicensesFilter(true)}
                sx={{
                  borderRadius: '4px',
                  textTransform: 'none',
                  px: 1.5,
                  fontSize: '0.8125rem',
                  height: 28,
                  fontWeight: 500,
                  boxShadow: 'none',
                  ml: 2,
                }}
              >
                View Details
              </Button>
            </Box>
          )}

          <TableContainer>
            <Table>
              <TableHead sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.03),
                '& .MuiTableCell-head': {
                  fontWeight: 'medium',
                  color: theme.palette.text.secondary,
                  py: 1.5,
                }
              }}>
                <TableRow>
                  <TableCell>Client Name</TableCell>
                  <TableCell align="right">Region</TableCell>
                  <TableCell align="right">Products</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Threshold (GB/Day)</TableCell>
                  {fromDate && toDate && fromDate !== toDate && (
                    <TableCell align="right">Average/Day</TableCell>
                  )}
                  <TableCell align="right">Expiration Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={(fromDate && toDate && fromDate !== toDate) ? 8 : 7} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center', p: 3 }}>
                        <InfoIcon sx={{ color: alpha(theme.palette.text.secondary, 0.5), fontSize: 48, mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No clients found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try adjusting your search or filters
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {/* Show message when overutilized filter is active */}
                {showOverutilizedOnly && filteredClients.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={(fromDate && toDate && fromDate !== toDate) ? 8 : 7} sx={{ p: 2, backgroundColor: alpha(theme.palette.error.light, 0.1) }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <WarningIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                          <Typography variant="body2" color="error" fontWeight="medium">
                            Showing only clients exceeding their license threshold ({filteredClients.length} of {overutilizedCount} overutilized clients)
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon fontSize="small" />}
                          onClick={toggleOverutilizedFilter}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 'medium',
                          }}
                        >
                          Clear Filter
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {/* Display count when not filtering but overutilized clients exist */}
                {!showOverutilizedOnly && overutilizedCount > 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ p: 2, backgroundColor: alpha(theme.palette.warning.light, 0.1) }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <WarningIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                          <Typography variant="body2" color="warning.dark" fontWeight="medium">
                            {overutilizedCount} client{overutilizedCount !== 1 ? 's' : ''} exceeding license threshold
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<FilterIcon />}
                          onClick={toggleOverutilizedFilter}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 'medium',
                          }}
                        >
                          Show only overutilized
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {/* Display banner when expiring licenses filter is active */}
                {showExpiringLicensesOnly && filteredClients.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={(fromDate && toDate && fromDate !== toDate) ? 8 : 7} sx={{ p: 2, backgroundColor: alpha(theme.palette.warning.light, 0.1) }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <EventBusyIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                          <Typography variant="body2" color="warning.dark" fontWeight="medium">
                            Showing only clients with expiring or expired licenses ({filteredClients.length} of {licenseExpirationStats.total} clients)
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<CloseIcon fontSize="small" />}
                          onClick={() => toggleExpiringLicensesFilter(false)}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 'medium',
                          }}
                        >
                          Clear Filter
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {filteredClients.map(clientName => {
                  const { size, products } = getClientLogSize(clientName);
                  const open = Boolean(anchorEl) && selectedClient === clientName;

                  return (
                    <React.Fragment key={clientName}>
                      <TableRow
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                          },
                          '& td': {
                            borderBottom: expandedClient === clientName ?
                              'none' : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            py: 1.75
                          },
                          transition: 'background-color 0.2s ease',
                          // Add a subtle highlight for expired or expiring licenses
                          ...(checkLicenseExpiration(clientName).isExpired && {
                            backgroundColor: alpha(theme.palette.error.light, 0.05),
                          }),
                          ...(checkLicenseExpiration(clientName).isExpiring && !checkLicenseExpiration(clientName).isExpired && {
                            backgroundColor: alpha(theme.palette.warning.light, 0.05),
                          }),
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => handleClientExpand(clientName)}
                              sx={{
                                mr: 1,
                                transform: expandedClient === clientName ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s',
                                color: expandedClient === clientName ?
                                  theme.palette.primary.main : theme.palette.text.secondary,
                                backgroundColor: expandedClient === clientName ?
                                  alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                }
                              }}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                            <Typography
                              variant="body1"
                              sx={{
                                color: expandedClient === clientName ?
                                  theme.palette.primary.main : theme.palette.text.primary,
                                fontWeight: expandedClient === clientName ? 'medium' : 'regular',
                              }}
                            >
                              {renderClientName(clientName)}
                            </Typography>

                            {/* Add engagement type tag */}
                            {(() => {
                              const licenseInfo = clientLicenses[clientName];
                              const engagementType = licenseInfo?.engagement_type || '';
                              if (engagementType === 'Trend Micro Direct' || engagementType === 'Trend Micro Indirect') {
                                return (
                                  <Chip
                                    size="small"
                                    label={engagementType}
                                    sx={{
                                      ml: 1.5,
                                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                      color: theme.palette.secondary.main,
                                      fontWeight: 'medium',
                                      borderRadius: '8px',
                                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                );
                              } else if (engagementType === 'BO Direct' || engagementType === 'BO Indirect') {
                                return (
                                  <Chip
                                    size="small"
                                    label={engagementType}
                                    sx={{
                                      ml: 1.5,
                                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                                      color: theme.palette.info.main,
                                      fontWeight: 'medium',
                                      borderRadius: '8px',
                                      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                );
                              }
                              return null;
                            })()}

                            {/* Add license expiration indicator */}
                            {(() => {
                              const { isExpiring, isExpired, daysRemaining } = checkLicenseExpiration(clientName);
                              if (isExpired) {
                                return (
                                  <Tooltip title={`License expired ${Math.abs(daysRemaining)} days ago`} arrow>
                                    <Chip
                                      size="small"
                                      label="Expired"
                                      icon={<WarningIcon fontSize="small" />}
                                      sx={{
                                        ml: 1.5,
                                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                                        color: theme.palette.error.main,
                                        fontWeight: 'medium',
                                        borderRadius: '8px',
                                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                        animation: 'pulse 1.5s infinite',
                                        '@keyframes pulse': {
                                          '0%': { opacity: 0.8 },
                                          '50%': { opacity: 1 },
                                          '100%': { opacity: 0.8 },
                                        },
                                      }}
                                    />
                                  </Tooltip>
                                );
                              } else if (isExpiring) {
                                return (
                                  <Tooltip title={`License expires in ${daysRemaining} days`} arrow>
                                    <Chip
                                      size="small"
                                      label={`${daysRemaining} days left`}
                                      sx={{
                                        ml: 1.5,
                                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                        color: theme.palette.warning.main,
                                        fontWeight: 'medium',
                                        borderRadius: '8px',
                                        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                                      }}
                                    />
                                  </Tooltip>
                                );
                              }
                              return null;
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {getClientRegion(clientName).displayRegions.map((regionName, idx) => (
                                <Chip
                                  key={idx}
                                  label={regionName}
                                  size="small"
                                  sx={{
                                    backgroundColor: regionName === 'Unknown'
                                      ? alpha(theme.palette.grey[500], 0.1)
                                      : alpha(theme.palette.secondary.main, 0.1),
                                    color: regionName === 'Unknown'
                                      ? theme.palette.grey[500]
                                      : theme.palette.secondary.main,
                                    fontSize: '0.7rem',
                                    height: '22px',
                                    cursor: regionName === 'Unknown' ? 'default' : 'pointer',
                                    position: 'relative',
                                    '&:hover': regionName === 'Unknown' ? {} : {
                                      backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                                      boxShadow: `0 2px 4px ${alpha(theme.palette.secondary.main, 0.2)}`,
                                      transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s ease',
                                    '&::after': regionName === 'Unknown' ? {} : {
                                      content: '"↗"',
                                      position: 'absolute',
                                      top: '-2px',
                                      right: '-2px',
                                      fontSize: '0.6rem',
                                      color: theme.palette.secondary.main,
                                      opacity: 0.7,
                                    },
                                  }}
                                  onClick={regionName === 'Unknown' ? undefined : () => {
                                    // Navigate to clients page with region filter
                                    const params = new URLSearchParams();
                                    params.set('region', regionName);
                                    if (fromDate) params.set('from_date', fromDate);
                                    if (toDate) params.set('to_date', toDate);
                                    navigate(`/clients?${params.toString()}`);
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={products || '0'}
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 'medium',
                              borderRadius: '8px',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            // When date filter is active, don't apply color coding to total size
                            // Only the Average (GB/Day) column should be color-coded
                            const hasDateFilter = fromDate && toDate && fromDate !== toDate;

                            if (hasDateFilter) {
                              // Date filter is active - show total size without color coding
                              return (
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  sx={{
                                    color: theme.palette.secondary.main,
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                  }}
                                >
                                  {formatDataSize(size)}
                                </Typography>
                              );
                            }

                            // No date filter - compare total size with threshold
                            // Calculate threshold - aggregate for parent clients
                            let threshold = 0;
                            let thresholdDisplay = '';

                            if (PARENT_PATTERNS.includes(clientName) || (showParentClientsOnly && isParentClient(clientName))) {
                              const childClients = getChildClients(clientName);

                              // If no children, use this client's own threshold
                              if (childClients.length === 0 && clientLicenses[clientName]?.license_per_day) {
                                threshold = parseFloat(clientLicenses[clientName].license_per_day);
                                thresholdDisplay = clientLicenses[clientName].license_per_day;
                              } else {
                                // Start with parent's own threshold if available
                                if (clientLicenses[clientName]?.license_per_day) {
                                  threshold += parseFloat(clientLicenses[clientName].license_per_day);
                                }

                                // Add thresholds from children
                                childClients.forEach(childName => {
                                  if (clientLicenses[childName]?.license_per_day) {
                                    threshold += parseFloat(clientLicenses[childName].license_per_day);
                                  }
                                });

                                thresholdDisplay = threshold > 0 ? threshold.toFixed(2) : '';
                              }
                            } else if (clientLicenses[clientName]?.license_per_day) {
                              // Regular client
                              threshold = parseFloat(clientLicenses[clientName].license_per_day);
                              thresholdDisplay = clientLicenses[clientName].license_per_day;
                            }

                            // Check if size exceeds threshold
                            if (threshold > 0 && size > threshold) {
                              return (
                                <Tooltip
                                  title={`Exceeds threshold of ${thresholdDisplay} GB`}
                                  arrow
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                    <WarningIcon
                                      fontSize="small"
                                      sx={{
                                        color: theme.palette.error.main,
                                        animation: 'pulse 1.5s infinite',
                                        '@keyframes pulse': {
                                          '0%': { opacity: 0.7 },
                                          '50%': { opacity: 1 },
                                          '100%': { opacity: 0.7 },
                                        },
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                      sx={{ color: theme.palette.error.main }}
                                    >
                                      {formatDataSize(size)}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              );
                            } else {
                              return (
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  sx={{
                                    color: theme.palette.secondary.main,
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                  }}
                                >
                                  {formatDataSize(size)}
                                </Typography>
                              );
                            }
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{
                              color: theme.palette.info.main,
                            }}
                          >
                            {(() => {
                              // For parent patterns like DICT, aggregate thresholds from child clients
                              if (PARENT_PATTERNS.includes(clientName) || (showParentClientsOnly && isParentClient(clientName))) {
                                const childClients = getChildClients(clientName);

                                // If no children, show this client's own threshold
                                if (childClients.length === 0) {
                                  return clientLicenses[clientName]?.license_per_day || '-';
                                }

                                // Calculate total threshold from all child clients
                                let totalThreshold = 0;
                                let childCount = 0;

                                // Start with parent's own threshold if available
                                if (clientLicenses[clientName]?.license_per_day) {
                                  totalThreshold += parseFloat(clientLicenses[clientName].license_per_day);
                                  childCount++;
                                }

                                // Add thresholds from children
                                childClients.forEach(childName => {
                                  if (clientLicenses[childName]?.license_per_day) {
                                    const childThreshold = parseFloat(clientLicenses[childName].license_per_day);
                                    totalThreshold += childThreshold;
                                    childCount++;
                                    console.log(`Child ${childName} has threshold: ${childThreshold}`);
                                  }
                                });

                                console.log(`Total threshold for ${clientName}: ${totalThreshold.toFixed(2)} from ${childCount} clients with thresholds`);
                                return childCount > 0 ? totalThreshold.toFixed(2) : '-';
                              }

                              // Regular client - show its threshold
                              return clientLicenses[clientName]?.license_per_day || '-';
                            })()}
                          </Typography>
                        </TableCell>
                        {fromDate && toDate && fromDate !== toDate && (
                          <TableCell align="right">
                            {/* Calculate average as total GB size divided by total days */}
                            {(() => {
                              // Calculate total days between fromDate and toDate
                              let totalDays = 1; // Default to 1 day if no date range

                              if (fromDate && toDate) {
                                const from = new Date(fromDate);
                                const to = new Date(toDate);
                                const diffTime = Math.abs(to - from);
                                totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
                              }

                              // Calculate average GB per day
                              const averagePerDay = size / totalDays;

                              // Determine color based on comparison with threshold
                              let color = theme.palette.success.main;

                              // Calculate threshold - aggregate for parent clients
                              let threshold = 0;

                              if (PARENT_PATTERNS.includes(clientName) || (showParentClientsOnly && isParentClient(clientName))) {
                                const childClients = getChildClients(clientName);

                                // If no children, use this client's own threshold
                                if (childClients.length === 0 && clientLicenses[clientName]?.license_per_day) {
                                  threshold = parseFloat(clientLicenses[clientName].license_per_day);
                                } else {
                                  // Start with parent's own threshold if available
                                  if (clientLicenses[clientName]?.license_per_day) {
                                    threshold += parseFloat(clientLicenses[clientName].license_per_day);
                                  }

                                  // Add thresholds from children
                                  childClients.forEach(childName => {
                                    if (clientLicenses[childName]?.license_per_day) {
                                      threshold += parseFloat(clientLicenses[childName].license_per_day);
                                    }
                                  });
                                }
                              } else if (clientLicenses[clientName]?.license_per_day) {
                                // Regular client
                                threshold = parseFloat(clientLicenses[clientName].license_per_day);
                              }

                              // Compare with threshold
                              if (threshold > 0) {
                                if (averagePerDay > threshold) {
                                  color = theme.palette.error.main;
                                } else if (averagePerDay > threshold * 0.8) {
                                  color = theme.palette.warning.main;
                                }
                              }

                              return (
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  sx={{ color }}
                                >
                                  {formatDataSize(averagePerDay)}
                                </Typography>
                              );
                            })()}
                          </TableCell>
                        )}
                        <TableCell align="right">
                          {clientLicenses[clientName] && clientLicenses[clientName].end_date ? (
                            <Tooltip title={formatDateTime(clientLicenses[clientName].end_date, true)}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  sx={{
                                    color: (() => {
                                      const { isExpiring, isExpired } = checkLicenseExpiration(clientName);
                                      if (isExpired) return theme.palette.error.main;
                                      if (isExpiring) return theme.palette.warning.main;
                                      return theme.palette.success.main;
                                    })(),
                                  }}
                                >
                                  {formatDateTime(clientLicenses[clientName].end_date, false)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" justifyContent="flex-end" gap={1}>
                            <Tooltip title="Download CSV">
                              <IconButton
                                onClick={() => handleOpenDownloadConfirm(clientName)}
                                size="small"
                                sx={{
                                  color: theme.palette.primary.main,
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  borderRadius: '10px',
                                  padding: '7px',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                  }
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="More Actions">
                              <IconButton
                                onClick={e => {
                                  setAnchorEl(e.currentTarget);
                                  setSelectedClient(clientName);
                                }}
                                size="small"
                                sx={{
                                  color: theme.palette.primary.main,
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                                  '&:hover': {
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                                  },
                                  width: 32,
                                  height: 32,
                                  boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Details Row */}
                      <TableRow
                        sx={{
                          display: expandedClient === clientName ? 'table-row' : 'none',
                          background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
                        }}
                      >
                        <TableCell colSpan={(fromDate && toDate && fromDate !== toDate) ? 7 : 6} sx={{ p: 3 }}>
                          <Box sx={{ pb: 2 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight="medium"
                              sx={{
                                color: theme.palette.primary.main,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <BusinessIcon fontSize="small" />
                              Client Details: {renderClientName(clientName)}
                            </Typography>
                            <Divider sx={{
                              mt: 1,
                              mb: 2,
                              background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.2)}, transparent)`,
                              height: '2px',
                              border: 'none',
                            }} />

                            {loadingDetails ? (
                              <Box display="flex" justifyContent="center" sx={{ py: 3 }}>
                                <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
                              </Box>
                            ) : clientDetails[clientName] ? (
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  {/* Data Size Threshold Chart */}
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: '16px',
                                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                      backgroundColor: theme.palette.background.paper,
                                      height: '100%',
                                      boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.04)}`,
                                      position: 'relative',
                                      overflow: 'hidden',
                                      '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                      }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 2
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '8px',
                                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                          }}
                                        >
                                          <StorageIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                        </Box>
                                        <Typography
                                          variant="subtitle1"
                                          sx={{
                                            fontWeight: 600,
                                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                          }}
                                        >
                                          Data Size vs Threshold
                                        </Typography>
                                      </Box>
                                    </Box>

                                    {clientDetails[clientName]?.logs ? (
                                      <Box sx={{ height: 200, position: 'relative' }}>
                                        {(() => {
                                          // Get initial total size and threshold data
                                          let totalSize = 0;

                                          // For consolidated clients like DICT, sum up all child client sizes
                                          if (clientDetails[clientName]?.logs?.is_aggregated) {
                                            console.log(`Calculating total size for aggregated client ${clientName}`);

                                            // Get all products for this client
                                            const products = clientDetails[clientName]?.logs?.by_product || [];

                                            // Sum up all product sizes
                                            totalSize = products.reduce((sum, product) => sum + (product.size_gb || 0), 0);
                                            console.log(`Aggregated total size from products: ${totalSize.toFixed(2)} GB`);
                                          } else {
                                            // Regular client - sum all by_client rows for this logical customer (case variants)
                                            const bc = clientDetails[clientName]?.logs?.by_client;
                                            totalSize = sumByClientSizeForCanonicalName(bc, clientName);
                                          }

                                          // Calculate threshold - aggregate for parent clients
                                          let threshold = 0;

                                          if (PARENT_PATTERNS.includes(clientName) || (showParentClientsOnly && isParentClient(clientName))) {
                                            const childClients = getChildClients(clientName);

                                            // If no children, use this client's own threshold
                                            if (childClients.length === 0 && clientLicenses[clientName]?.license_per_day) {
                                              threshold = parseFloat(clientLicenses[clientName].license_per_day);
                                            } else {
                                              // Start with parent's own threshold if available
                                              if (clientLicenses[clientName]?.license_per_day) {
                                                threshold += parseFloat(clientLicenses[clientName].license_per_day);
                                              }

                                              // Add thresholds from children
                                              childClients.forEach(childName => {
                                                if (clientLicenses[childName]?.license_per_day) {
                                                  threshold += parseFloat(clientLicenses[childName].license_per_day);
                                                }
                                              });
                                            }
                                          } else if (clientLicenses[clientName]?.license_per_day) {
                                            threshold = parseFloat(clientLicenses[clientName].license_per_day);
                                          }

                                          // Check if threshold is set
                                          const hasThreshold = threshold > 0;

                                          // For date range view, show a bar chart
                                          if (fromDate && toDate && fromDate !== toDate) {
                                            // Get dates in range
                                            const dateRange = getDatesInRange(fromDate, toDate);

                                            // Log data structure for debugging
                                            //console.log("Client details for chart:", clientName, clientDetails[clientName]);
                                            //console.log("Client cost data:", clientCostData[clientName]);

                                            // Daily data array to populate the chart
                                            let dailyData = [];

                                            // Create a daily data object to aggregate data
                                            const aggregatedDailyData = {};

                                            // First try to get daily data from clientCostData which has the most reliable daily breakdown
                                            if (clientCostData[clientName]?.daily_data && Object.keys(clientCostData[clientName].daily_data).length > 0) {
                                              console.log(`Using clientCostData daily_data for chart for ${clientName}`);

                                              // Add parent client data
                                              Object.entries(clientCostData[clientName].daily_data)
                                                .filter(([date]) => date >= fromDate && date <= toDate)
                                                .forEach(([date, dayData]) => {
                                                  aggregatedDailyData[date] = {
                                                    size_gb: dayData.client_rsyslog_size || 0
                                                  };
                                                });

                                              // For parent clients, add data from all child clients
                                              const isParent = PARENT_PATTERNS.includes(clientName);
                                              if (isParent && clients && clients.length) {
                                                console.log(`Adding child client data for chart for parent ${clientName}`);

                                                // Find child clients
                                                const childClients = clients.filter(client => client && typeof client === 'string' ? client.startsWith(clientName) : client && client.name && client.name.startsWith(clientName));

                                                console.log(`Found ${childClients.length} child clients for chart data`);

                                                // Process each child client
                                                childClients.forEach(childName => {
                                                  // Skip if we don't have cost data for this child
                                                  if (!clientCostData[childName]?.daily_data) {
                                                    console.log(`No daily data available for child client ${childName} for chart`);
                                                    return;
                                                  }

                                                  console.log(`Adding daily data from child client ${childName} for chart`);

                                                  // Add child's daily data to aggregated data
                                                  Object.entries(clientCostData[childName].daily_data)
                                                    .filter(([date]) => date >= fromDate && date <= toDate)
                                                    .forEach(([date, data]) => {
                                                      if (!aggregatedDailyData[date]) {
                                                        aggregatedDailyData[date] = { size_gb: 0 };
                                                      }
                                                      const childSize = data.client_rsyslog_size || 0;
                                                      aggregatedDailyData[date].size_gb += childSize;
                                                      console.log(`Added ${childSize.toFixed(2)} GB from ${childName} for date ${date} to chart data`);
                                                    });
                                                });
                                              }

                                              // Convert aggregated data to array format for the chart
                                              dailyData = Object.entries(aggregatedDailyData)
                                                .map(([date, data]) => ({
                                                  date,
                                                  size_gb: data.size_gb
                                                }))
                                                .sort((a, b) => a.date.localeCompare(b.date));

                                              console.log(`Prepared dailyData for chart for ${clientName}:`, dailyData);
                                            }
                                            // If no daily data in clientCostData, try other sources
                                            else {
                                              console.log(`No daily_data in clientCostData for ${clientName}, checking alternative sources`);

                                              // Get client logs data from the API response
                                              // We'll need to check multiple possible data structures
                                              const byDateData = clientDetails[clientName]?.logs?.by_date || [];

                                              // If no by_date data exists, try to get it from daily data in costs
                                              const costData = clientDetails[clientName]?.costs?.debug?.daily_data || {};

                                              // Get daily data or create empty placeholders
                                              dailyData = dateRange.map(date => {
                                                // First try to find in by_date
                                                const byDateEntry = byDateData.find(d => d.date === date);
                                                if (byDateEntry) {
                                                  return {
                                                    date,
                                                    size_gb: byDateEntry.size_gb || 0
                                                  };
                                                }

                                                // Otherwise check cost data
                                                const costDataEntry = costData[date];
                                                if (costDataEntry) {
                                                  return {
                                                    date,
                                                    size_gb: costDataEntry.client_rsyslog_size || 0
                                                  };
                                                }

                                                // Default to 0
                                                return {
                                                  date,
                                                  size_gb: 0
                                                };
                                              });

                                              console.log(`Prepared dailyData from alternative sources for ${clientName}:`, dailyData);
                                            }

                                            // If we still don't have any data with size > 0, check if we have data in the Daily Data Breakdown table
                                            if (dailyData.every(day => day.size_gb === 0) && clientCostData[clientName]?.debug?.daily_data) {
                                              console.log(`All sizes are 0 for ${clientName}, trying debug.daily_data`);
                                              dailyData = Object.entries(clientCostData[clientName].debug.daily_data)
                                                .filter(([date]) => date >= fromDate && date <= toDate)
                                                .map(([date, dayData]) => ({
                                                  date,
                                                  size_gb: dayData.client_rsyslog_size || 0
                                                }))
                                                .sort((a, b) => a.date.localeCompare(b.date));

                                              console.log(`Prepared dailyData from debug.daily_data for ${clientName}:`, dailyData);
                                            }

                                            // Make sure we have at least one data point
                                            if (dailyData.length === 0) {
                                              dailyData = [{
                                                date: fromDate || new Date().toISOString().split('T')[0],
                                                size_gb: 0
                                              }];
                                            }

                                            // Ensure dates are properly formatted for display
                                            const formattedLabels = dailyData.map(d => {
                                              // Convert YYYY-MM-DD to DD-MM-YYYY for better readability
                                              const parts = d.date.split('-');
                                              if (parts.length === 3) {
                                                return `${parts[2]}-${parts[1]}-${parts[0].substring(2)}`;
                                              }
                                              return d.date;
                                            });

                                            return (
                                              <Bar
                                                data={{
                                                  labels: formattedLabels,
                                                  datasets: [
                                                    {
                                                      label: "Daily Data Size (GB)",
                                                      data: dailyData.map(d => d.size_gb),
                                                      backgroundColor: !hasThreshold
                                                        ? Array(dailyData.length).fill(alpha(theme.palette.info.main, 0.7))
                                                        : dailyData.map(d =>
                                                          d.size_gb > threshold
                                                            ? alpha(theme.palette.error.main, 0.7)
                                                            : d.size_gb > threshold * 0.8
                                                              ? alpha(theme.palette.warning.main, 0.7)
                                                              : alpha(theme.palette.primary.main, 0.7)
                                                        ),
                                                      borderColor: !hasThreshold
                                                        ? Array(dailyData.length).fill(theme.palette.info.main)
                                                        : dailyData.map(d =>
                                                          d.size_gb > threshold
                                                            ? theme.palette.error.main
                                                            : d.size_gb > threshold * 0.8
                                                              ? theme.palette.warning.main
                                                              : theme.palette.primary.main
                                                        ),
                                                      borderWidth: 1,
                                                      borderRadius: 4,
                                                    },
                                                    {
                                                      label: !hasThreshold ? "No Threshold Set" : "Threshold",
                                                      data: !hasThreshold ? [] : Array(dailyData.length).fill(threshold),
                                                      type: 'line',
                                                      borderColor: alpha(theme.palette.error.main, 0.8),
                                                      borderWidth: 2,
                                                      borderDash: [5, 5],
                                                      pointRadius: 0,
                                                      fill: false,
                                                      hidden: !hasThreshold
                                                    }
                                                  ]
                                                }}
                                                options={{
                                                  responsive: true,
                                                  maintainAspectRatio: false,
                                                  plugins: {
                                                    title: {
                                                      display: true,
                                                      text: 'Daily Data Usage vs Threshold',
                                                      font: { size: 14, weight: 'bold' },
                                                      color: theme.palette.text.primary,
                                                      padding: { bottom: 15 }
                                                    },
                                                    legend: {
                                                      position: 'top',
                                                      align: 'end',
                                                      labels: {
                                                        boxWidth: 12,
                                                        font: { size: 11 },
                                                        usePointStyle: true,
                                                        pointStyle: 'circle',
                                                      },
                                                    },
                                                    tooltip: {
                                                      backgroundColor: theme.palette.background.paper,
                                                      titleColor: theme.palette.text.primary,
                                                      bodyColor: theme.palette.text.secondary,
                                                      borderColor: alpha(theme.palette.divider, 0.2),
                                                      borderWidth: 1,
                                                      padding: 10,
                                                      cornerRadius: 8,
                                                      boxPadding: 4,
                                                      usePointStyle: true,
                                                      callbacks: {
                                                        title: function (context) {
                                                          // Build the title from the original YYYY-MM-DD date; the axis label
                                                          // is DD-MM-YY, which new Date() can't parse (-> NaN-NaN-NaN).
                                                          const point = dailyData[context[0].dataIndex];
                                                          const parts = point?.date?.split('-');
                                                          if (parts && parts.length === 3) {
                                                            return `${parts[2]}-${parts[1]}-${parts[0]}`;
                                                          }
                                                          return context[0].label;
                                                        },
                                                        label: function (context) {
                                                          const value = context.raw;
                                                          return context.dataset.label + ': ' + formatDataSize(value);
                                                        },
                                                        labelPointStyle: function () {
                                                          return {
                                                            pointStyle: 'circle',
                                                            rotation: 0
                                                          };
                                                        }
                                                      }
                                                    }
                                                  },
                                                  interaction: {
                                                    mode: 'index',
                                                    intersect: false,
                                                  },
                                                  hover: {
                                                    mode: 'index',
                                                    intersect: false
                                                  },
                                                  scales: {
                                                    x: {
                                                      grid: {
                                                        display: false
                                                      },
                                                      ticks: {
                                                        font: { size: 10 },
                                                        maxRotation: 45,
                                                        minRotation: 45
                                                      },
                                                      title: {
                                                        display: true,
                                                        text: 'Date',
                                                        font: { size: 12 },
                                                        color: theme.palette.text.secondary
                                                      }
                                                    },
                                                    y: {
                                                      beginAtZero: true,
                                                      grid: {
                                                        color: alpha(theme.palette.divider, 0.08)
                                                      },
                                                      ticks: {
                                                        font: { size: 10 },
                                                        callback: function (value) {
                                                          return value + ' GB';
                                                        }
                                                      },
                                                      title: {
                                                        display: true,
                                                        text: 'Data Size (GB)',
                                                        font: { size: 12 },
                                                        color: theme.palette.text.secondary
                                                      }
                                                    }
                                                  },
                                                  animation: {
                                                    duration: 1000,
                                                    easing: 'easeOutQuart'
                                                  }
                                                }}
                                              />
                                            );
                                          } else {
                                            // For single date view, show a gauge-like visual

                                            // Get the most accurate size data for a single day
                                            let singleDaySize = 0;
                                            const currentDate = fromDate || new Date().toISOString().split('T')[0];

                                            console.log(`Getting single day size for ${clientName} on ${currentDate}`);

                                            // Check if this is a parent client
                                            const isParent = PARENT_PATTERNS.includes(clientName);

                                            // Try to get the size from clientCostData for more accuracy - this has most up-to-date data
                                            if (clientCostData[clientName]) {
                                              console.log(`Using clientCostData for single day view for ${clientName}:`, currentDate);

                                              // If we have daily data and it contains the current date
                                              if (clientCostData[clientName].daily_data && clientCostData[clientName].daily_data[currentDate]) {
                                                singleDaySize = clientCostData[clientName].daily_data[currentDate].client_rsyslog_size || 0;
                                                console.log(`Using daily data size for ${clientName}: ${singleDaySize.toFixed(2)} GB`);
                                              }
                                              // If we have debug daily data
                                              else if (clientCostData[clientName].debug?.daily_data &&
                                                clientCostData[clientName].debug.daily_data[currentDate]) {
                                                singleDaySize = clientCostData[clientName].debug.daily_data[currentDate].client_rsyslog_size || 0;
                                                console.log(`Using debug daily data size for ${clientName}: ${singleDaySize.toFixed(2)} GB`);
                                              }
                                              // If we have a specific day's data
                                              else if (clientCostData[clientName].rsyslog_size_gb) {
                                                singleDaySize = clientCostData[clientName].rsyslog_size_gb;
                                                console.log(`Using rsyslog_size_gb for ${clientName}: ${singleDaySize.toFixed(2)} GB`);
                                              }
                                            }

                                            // For parent clients, add data from all child clients
                                            if (isParent && clients && clients.length) {
                                              console.log(`Adding child client data for single day view for parent ${clientName}`);

                                              // Find child clients
                                              const childClients = clients.filter(client => client && typeof client === 'string' ? client.startsWith(clientName) : client && client.name && client.name.startsWith(clientName));

                                              console.log(`Found ${childClients.length} child clients for single day view`);

                                              // Process each child client
                                              childClients.forEach(childName => {
                                                // Skip if we don't have cost data for this child
                                                if (!clientCostData[childName]) {
                                                  console.log(`No cost data available for child client ${childName} for single day view`);
                                                  return;
                                                }

                                                let childSize = 0;

                                                // Try to get the child's size from daily data
                                                if (clientCostData[childName].daily_data &&
                                                  clientCostData[childName].daily_data[currentDate]) {
                                                  childSize = clientCostData[childName].daily_data[currentDate].client_rsyslog_size || 0;
                                                }
                                                // Try debug daily data
                                                else if (clientCostData[childName].debug?.daily_data &&
                                                  clientCostData[childName].debug.daily_data[currentDate]) {
                                                  childSize = clientCostData[childName].debug.daily_data[currentDate].client_rsyslog_size || 0;
                                                }
                                                // Try rsyslog_size_gb
                                                else if (clientCostData[childName].rsyslog_size_gb) {
                                                  childSize = clientCostData[childName].rsyslog_size_gb;
                                                }

                                                if (childSize > 0) {
                                                  console.log(`Adding ${childSize.toFixed(2)} GB from child ${childName} for single day view`);
                                                  singleDaySize += childSize;
                                                }
                                              });
                                            }

                                            // If we still don't have data, try to get it from clientDetails or clientLogs
                                            if (singleDaySize === 0) {
                                              // Try clientDetails first (expanded view data)
                                              if (clientDetails[clientName]?.logs?.by_client && clientDetails[clientName].logs.by_client.length > 0) {
                                                singleDaySize = sumByClientSizeForCanonicalName(clientDetails[clientName].logs.by_client, clientName);
                                                console.log(`Using client details by_client size for ${clientName}: ${singleDaySize.toFixed(2)} GB`);
                                              }
                                              // Then try clientLogs (table row data)
                                              else if (clientLogs?.by_client) {
                                                const agg = sumByClientSizeForCanonicalName(clientLogs.by_client, clientName);
                                                if (agg > 0) {
                                                  singleDaySize = agg;
                                                  console.log(`Using clientLogs by_client size for ${clientName}: ${singleDaySize.toFixed(2)} GB`);
                                                }
                                              }
                                            }

                                            // As a last resort, use totalSize
                                            if (singleDaySize === 0) {
                                              singleDaySize = totalSize;
                                              console.log(`Using fallback totalSize for ${clientName}: ${singleDaySize.toFixed(2)} GB`);
                                            }

                                            console.log(`Final single day size for ${clientName}: ${singleDaySize.toFixed(2)} GB`);

                                            // Calculate percentage only if threshold is set
                                            // hasThreshold is already defined at the top level
                                            const percentOfThreshold = hasThreshold ? (singleDaySize / threshold) * 100 : 0;
                                            const isOverThreshold = hasThreshold && percentOfThreshold > 100;
                                            const isNearThreshold = hasThreshold && percentOfThreshold > 80;

                                            // Colors based on threshold status or default to info for no threshold
                                            const gaugeColor = !hasThreshold
                                              ? theme.palette.info.main
                                              : isOverThreshold
                                                ? theme.palette.error.main
                                                : isNearThreshold
                                                  ? theme.palette.warning.main
                                                  : theme.palette.success.main;

                                            // Update totalSize with the aggregated singleDaySize
                                            totalSize = singleDaySize;

                                            return (
                                              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', alignItems: 'center', pt: 1 }}>
                                                {/* Title and status chip */}
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 1 }}>
                                                  <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                      fontWeight: 600,
                                                      color: theme.palette.text.primary,
                                                      textAlign: 'center'
                                                    }}
                                                  >
                                                    Data Usage vs Threshold
                                                  </Typography>

                                                  {/* Threshold Exceeded indicator */}
                                                  {isOverThreshold && (
                                                    <Box sx={{
                                                      mb: 1,
                                                      p: 0.75,
                                                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                                                      borderRadius: '4px',
                                                      border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 0.5,
                                                      justifyContent: 'center'
                                                    }}>
                                                      <WarningIcon sx={{ color: theme.palette.error.main, fontSize: '0.9rem' }} />
                                                      <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 'bold', fontSize: '0.75rem' }}>
                                                        Threshold Exceeded by {formatDataSize(totalSize - threshold)} ({(percentOfThreshold - 100).toFixed(0)}%)
                                                      </Typography>
                                                    </Box>
                                                  )}

                                                  {/* Threshold and Current Size labels above the gauge */}
                                                  <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    width: '100%',
                                                    px: 1
                                                  }}>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                                      Current Size
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                                      Threshold
                                                    </Typography>
                                                  </Box>

                                                  {/* Status chip */}
                                                  <Chip
                                                    size="small"
                                                    label={
                                                      !hasThreshold
                                                        ? "No Threshold Set"
                                                        : isOverThreshold
                                                          ? `Threshold Exceeded by ${formatDataSize(totalSize - threshold)}`
                                                          : isNearThreshold
                                                            ? "Near Threshold"
                                                            : "Within Limit"
                                                    }
                                                    sx={{
                                                      backgroundColor: isOverThreshold
                                                        ? alpha(theme.palette.error.main, 0.1)
                                                        : isNearThreshold
                                                          ? alpha(theme.palette.warning.main, 0.1)
                                                          : alpha(theme.palette.success.main, 0.1),
                                                      color: isOverThreshold
                                                        ? theme.palette.error.main
                                                        : isNearThreshold
                                                          ? theme.palette.warning.main
                                                          : theme.palette.success.main,
                                                      fontWeight: 'medium',
                                                      fontSize: '0.7rem',
                                                      borderRadius: '6px',
                                                      border: `1px solid ${isOverThreshold
                                                        ? alpha(theme.palette.error.main, 0.2)
                                                        : isNearThreshold
                                                          ? alpha(theme.palette.warning.main, 0.2)
                                                          : alpha(theme.palette.success.main, 0.2)
                                                        }`,
                                                    }}
                                                  />
                                                </Box>

                                                {/* Gauge visualization */}
                                                <Box sx={{ position: 'relative', width: 180, height: 180 }}>
                                                  {/* Background circle with gradient rim */}
                                                  <Box
                                                    sx={{
                                                      position: 'absolute',
                                                      top: 0,
                                                      left: 0,
                                                      width: '100%',
                                                      height: '100%',
                                                      borderRadius: '50%',
                                                      background: `radial-gradient(circle, ${alpha(theme.palette.background.paper, 0.9)} 65%, ${alpha(theme.palette.divider, 0.15)} 66%)`,
                                                      boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.07)}`,
                                                    }}
                                                  />

                                                  {/* Gauge markers: 0%, 25%, 50%, 75%, 100% - flipped right to left */}
                                                  {[0, 25, 50, 75, 100].map((markerValue) => {
                                                    // Convert percentage to angle with left = 0% and right = 100%
                                                    const angle = (markerValue / 100) * 180;
                                                    // Calculate position using trig (offset from center)
                                                    const radian = (angle - 90) * (Math.PI / 180);
                                                    // Flip the X coordinate to match gauge orientation
                                                    const x = 180 - (Math.cos(radian) * 85 + 90); // 85 = radius, 90 = center
                                                    const y = Math.sin(radian) * 85 + 90;

                                                    return (
                                                      <React.Fragment key={markerValue}>
                                                        {/* Marker tick */}
                                                        <Box
                                                          sx={{
                                                            position: 'absolute',
                                                            left: x,
                                                            top: y,
                                                            width: markerValue % 50 === 0 ? 3 : 2,
                                                            height: markerValue % 50 === 0 ? 10 : 6,
                                                            backgroundColor: markerValue > 80 ? theme.palette.error.main :
                                                              markerValue > 50 ? theme.palette.warning.main :
                                                                alpha(theme.palette.text.secondary, 0.4),
                                                            transformOrigin: 'center top',
                                                            transform: `rotate(${180 - angle}deg)`,
                                                            borderRadius: '1px',
                                                            zIndex: 1,
                                                          }}
                                                        />

                                                        {/* Marker label (only show for 0, 50, 100) */}
                                                        {markerValue % 50 === 0 && (
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              position: 'absolute',
                                                              left: x,
                                                              top: y + 12,
                                                              transform: 'translateX(-50%)',
                                                              fontSize: '0.65rem',
                                                              color: markerValue === 100 ? theme.palette.error.main :
                                                                markerValue === 50 ? theme.palette.warning.main :
                                                                  alpha(theme.palette.text.secondary, 0.7),
                                                              fontWeight: markerValue === 100 ? 'bold' : 'normal',
                                                            }}
                                                          >
                                                            {markerValue}%
                                                          </Typography>
                                                        )}
                                                      </React.Fragment>
                                                    );
                                                  })}

                                                  {/* Progress indicator with gradient and animation - right to left */}
                                                  <Box
                                                    sx={{
                                                      position: 'absolute',
                                                      top: 5,
                                                      left: 5,
                                                      width: 170,
                                                      height: 170,
                                                      borderRadius: '50%',
                                                      transform: 'scaleX(-1)', // Flip horizontally
                                                      background: `conic-gradient(
                                                        ${gaugeColor} 0deg ${!hasThreshold ? 90 : Math.min(percentOfThreshold * 1.8, 180)}deg, 
                                                        ${alpha(theme.palette.background.default, 0.2)} ${!hasThreshold ? 90 : Math.min(percentOfThreshold * 1.8, 180)}deg 180deg,
                                                        transparent 180deg 360deg
                                                      )`,
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      transition: 'all 0.5s ease-out',
                                                      animation: 'gauge-fill 1.5s ease-out',
                                                      overflow: 'hidden',
                                                      boxShadow: `0 0 10px ${alpha(gaugeColor, 0.2)} inset`,
                                                      clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
                                                      '&::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: -1,
                                                        left: -1,
                                                        right: -1,
                                                        bottom: -1,
                                                        borderRadius: '50%',
                                                        background: 'transparent',
                                                        border: `2px solid ${alpha(gaugeColor, 0.3)}`,
                                                      }
                                                    }}
                                                  />



                                                  {/* Inner circle with text */}
                                                  <Box
                                                    sx={{
                                                      position: 'absolute',
                                                      top: 20,
                                                      left: 20,
                                                      width: 140,
                                                      height: 140,
                                                      borderRadius: '50%',
                                                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                                      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                                      '&::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: 5,
                                                        left: 5,
                                                        right: 5,
                                                        bottom: 5,
                                                        borderRadius: '50%',
                                                        border: `1px solid ${alpha(gaugeColor, 0.1)}`,
                                                      },
                                                      zIndex: 2
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="h4"
                                                      fontWeight="bold"
                                                      sx={{
                                                        color: gaugeColor,
                                                        textShadow: `0 2px 4px ${alpha(gaugeColor, 0.15)}`,
                                                        mb: 0
                                                      }}
                                                    >
                                                      {!hasThreshold ? "50%" : `${percentOfThreshold.toFixed(0)}%`}
                                                    </Typography>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        color: alpha(theme.palette.text.secondary, 0.8),
                                                        fontWeight: 500,
                                                        letterSpacing: '0.5px',
                                                        mt: 0.5
                                                      }}
                                                    >
                                                      {!hasThreshold ? "data usage" : "of threshold"}
                                                    </Typography>

                                                    {/* Values inside gauge */}
                                                    <Box sx={{
                                                      position: 'absolute',
                                                      top: '100%',
                                                      left: 0,
                                                      right: 0,
                                                      display: 'flex',
                                                      justifyContent: 'space-between',
                                                      mt: 0.5,
                                                      px: 1
                                                    }}>
                                                      <Box>
                                                        <Typography
                                                          variant="subtitle2"
                                                          sx={{
                                                            fontWeight: 'bold',
                                                            color: gaugeColor,
                                                            fontSize: '0.85rem'
                                                          }}
                                                        >
                                                          {formatDataSize(totalSize)}
                                                        </Typography>
                                                      </Box>
                                                      <Box>
                                                        <Typography
                                                          variant="subtitle2"
                                                          sx={{
                                                            fontWeight: 'bold',
                                                            color: theme.palette.primary.main,
                                                            fontSize: '0.85rem'
                                                          }}
                                                        >
                                                          {!hasThreshold ? "Not Set" : formatDataSize(threshold)}
                                                        </Typography>
                                                      </Box>
                                                    </Box>

                                                    {/* Status indicator */}
                                                    <Box
                                                      sx={{
                                                        mt: 1,
                                                        px: 1.5,
                                                        py: 0.3,
                                                        borderRadius: '12px',
                                                        backgroundColor: alpha(gaugeColor, 0.1),
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                      }}
                                                    >
                                                      <Box
                                                        sx={{
                                                          width: 8,
                                                          height: 8,
                                                          borderRadius: '50%',
                                                          backgroundColor: gaugeColor,
                                                          boxShadow: `0 0 4px ${gaugeColor}`,
                                                        }}
                                                      />
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          color: gaugeColor,
                                                          fontWeight: 'bold',
                                                          fontSize: '0.65rem',
                                                        }}
                                                      >
                                                        {!hasThreshold
                                                          ? 'DATA USAGE'
                                                          : isOverThreshold
                                                            ? `EXCEEDED +${(percentOfThreshold - 100).toFixed(0)}%`
                                                            : isNearThreshold
                                                              ? 'WARNING'
                                                              : 'NORMAL'}
                                                      </Typography>
                                                    </Box>
                                                  </Box>
                                                </Box>

                                                {/* Data info */}
                                                <Box sx={{
                                                  mt: 2.5,
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                                  width: '100%',
                                                  px: 2,
                                                  gap: 2
                                                }}>
                                                  <Box sx={{
                                                    textAlign: 'center',
                                                    flex: 1,
                                                    p: 1.5,
                                                    borderRadius: '8px',
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                  }}>
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                      sx={{ fontSize: '0.75rem', mb: 0.5 }}
                                                    >
                                                      Threshold
                                                    </Typography>
                                                    <Typography
                                                      variant="subtitle1"
                                                      fontWeight="bold"
                                                      sx={{
                                                        color: theme.palette.primary.main,
                                                        fontSize: '1.1rem'
                                                      }}
                                                    >
                                                      {!hasThreshold ? "Not Set" : formatDataSize(threshold)}
                                                    </Typography>
                                                  </Box>

                                                  <Box sx={{
                                                    textAlign: 'center',
                                                    flex: 1,
                                                    p: 1.5,
                                                    borderRadius: '8px',
                                                    backgroundColor: alpha(gaugeColor, 0.05),
                                                    border: `1px solid ${alpha(gaugeColor, 0.1)}`,
                                                  }}>
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                      sx={{ fontSize: '0.75rem', mb: 0.5 }}
                                                    >
                                                      Current Size
                                                    </Typography>
                                                    <Typography
                                                      variant="subtitle1"
                                                      fontWeight="bold"
                                                      sx={{
                                                        color: gaugeColor,
                                                        fontSize: '1.1rem'
                                                      }}
                                                    >
                                                      {formatDataSize(singleDaySize)}
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              </Box>
                                            );
                                          }
                                        })()}
                                      </Box>
                                    ) : (
                                      <Box sx={{ textAlign: 'center', p: 2 }}>
                                        <InfoIcon sx={{ color: alpha(theme.palette.text.secondary, 0.5), fontSize: 36, mb: 1 }} />
                                        <Typography color="text.secondary">
                                          {!clientLicenses[clientName]?.license_per_day
                                            ? "No threshold configured"
                                            : "No data available"}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  {/* Client Products Table */}
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: '16px',
                                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                      backgroundColor: theme.palette.background.paper,
                                      height: '100%',
                                      boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.04)}`,
                                      position: 'relative',
                                      overflow: 'hidden',
                                      '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                      }
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <DisplaySettingsIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                      Client Products
                                      <Chip
                                        label={products}
                                        size="small"
                                        sx={{
                                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                          color: theme.palette.primary.main,
                                          fontWeight: 'bold',
                                          borderRadius: '6px',
                                          height: 20,
                                          fontSize: '0.7rem',
                                        }}
                                      />
                                    </Typography>

                                    {clientDetails[clientName]?.logs?.by_product && clientDetails[clientName].logs.by_product.length > 0 ? (
                                      <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Product</TableCell>
                                              <TableCell align="right">Size</TableCell>
                                              {clientDetails[clientName]?.logs?.is_aggregated && (
                                                <TableCell>Child Client</TableCell>
                                              )}
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(() => {
                                              // Get the client's products (exclude "unknown" / empty) and sort them in descending order by size
                                              const sortedProducts = [...clientDetails[clientName].logs.by_product]
                                                .filter(product => !isExcludedProduct(product))
                                                .sort((a, b) => b.size_gb - a.size_gb);

                                              // Calculate total size for percentage calculation
                                              const totalProductSize = sortedProducts.reduce((sum, product) =>
                                                sum + (product.size_gb || 0), 0);

                                              return sortedProducts.map((product, idx) => {
                                                // Use the direct per-product size from the products API.
                                                // hosts.py now correctly sums the latest-per-day value for each
                                                // (date, product, hostname, device_id), so no scaling is needed.
                                                const normalizedSize = product.size_gb || 0;

                                                // Calculate percentage
                                                const percentage = totalProductSize > 0
                                                  ? (product.size_gb / totalProductSize) * 100
                                                  : 0;

                                                // Get color based on percentage
                                                const getPercentColor = (pct) => {
                                                  if (pct >= 50) return theme.palette.error.main;
                                                  if (pct >= 25) return theme.palette.warning.main;
                                                  if (pct >= 10) return theme.palette.info.main;
                                                  return theme.palette.success.main;
                                                };

                                                return (
                                                  <TableRow key={idx} sx={{
                                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                                                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.07)}`
                                                  }}>
                                                    <TableCell>
                                                      <Box
                                                        sx={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: 1,
                                                          cursor: 'pointer',
                                                          '&:hover': {
                                                            textDecoration: 'underline',
                                                            color: theme.palette.primary.main
                                                          }
                                                        }}
                                                        onClick={() => handleOpenProductDevicePopup(clientName, product.product)}
                                                      >
                                                        <Box
                                                          sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            backgroundColor: getColor(idx, 0.8)
                                                          }}
                                                        />
                                                        <Typography variant="body2" fontWeight={500}>
                                                          {product.product || 'Unknown'}
                                                        </Typography>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Typography variant="body2" fontWeight="medium" sx={{ color: getColor(idx, 1) }}>
                                                        {(() => {
                                                          // If size is 0 but we know this is a child client with data, use the original product size
                                                          if (normalizedSize === 0 && product.size_gb > 0) {
                                                            console.log(`Fixing zero size for ${product.client_name}/${product.product}: using original size ${product.size_gb}`);
                                                            return formatDataSize(product.size_gb);
                                                          }
                                                          return formatDataSize(normalizedSize);
                                                        })()}
                                                      </Typography>
                                                    </TableCell>

                                                    {clientDetails[clientName]?.logs?.is_aggregated && (
                                                      <TableCell>
                                                        <Typography
                                                          variant="body2"
                                                          sx={{
                                                            color: theme.palette.info.main,
                                                            fontWeight: 'medium',
                                                            fontSize: '0.8rem'
                                                          }}
                                                        >
                                                          {product.client_name}
                                                        </Typography>
                                                      </TableCell>
                                                    )}
                                                  </TableRow>
                                                );
                                              });
                                            })()}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    ) : (
                                      <Box sx={{ textAlign: 'center', p: 2 }}>
                                        <InfoIcon sx={{ color: alpha(theme.palette.text.secondary, 0.5), fontSize: 36, mb: 1 }} />
                                        <Typography color="text.secondary">No product data available</Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Grid>

                                {/* Cost Analysis Section */}
                                <Grid item xs={12}>
                                  <Box
                                    sx={{
                                      mt: 2,
                                      p: 2,
                                      borderRadius: '16px',
                                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                      backgroundColor: theme.palette.background.paper,
                                      background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.6)}, ${alpha(theme.palette.background.paper, 0.9)})`,
                                      boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.04)}`,
                                      position: 'relative',
                                      overflow: 'hidden',
                                      '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                      }
                                    }}
                                  >
                                    <PermissionWidget permission={WIDGET_PERMISSIONS['client-cost-analysis']} fallback={null}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography
                                          variant="subtitle2"
                                          gutterBottom
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 0
                                          }}
                                        >
                                          <MonetizationOnIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                          Client Cost Analysis
                                        </Typography>
                                        {clientCostData[clientName] && fromDate && toDate && (
                                          <Tooltip title="Data aggregated over date range">
                                            <Chip
                                              size="small"
                                              icon={<CalendarIcon fontSize="small" />}
                                              label={`${fromDate} to ${toDate}`}
                                              sx={{
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                color: theme.palette.primary.main,
                                                fontWeight: 'medium',
                                                fontSize: '0.7rem',
                                              }}
                                            />
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </PermissionWidget>
                                    {/* <PermissionWidget permission={WIDGET_PERMISSIONS['client-cost-analysis']} fallback={null}>    */}
                                    <Grid container spacing={2}>
                                      <PermissionWidget permission={WIDGET_PERMISSIONS['cost-per-gb']} fallback={null}>
                                        <Grid item xs={12} sm={6} md={4}>
                                          <Box sx={{
                                            p: 2,
                                            borderRadius: '12px',
                                            backgroundColor: alpha(theme.palette.warning.main, 0.05),
                                            border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                                          }}>
                                            <Typography variant="body2" color="text.secondary">Cost Per GB</Typography>
                                            <Typography variant="h6" fontWeight="medium" sx={{ color: theme.palette.warning.main }}>
                                              ₹{clientCostData[clientName]?.cost_per_gb.toFixed(2) || "0.00"}
                                            </Typography>
                                            {fromDate && toDate && (
                                              <Typography variant="caption" color="text.secondary">
                                                Average across date range
                                              </Typography>
                                            )}
                                          </Box>
                                        </Grid>
                                      </PermissionWidget>
                                      <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{
                                          p: 2,
                                          borderRadius: '12px',
                                          backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                                        }}>
                                          <Typography variant="body2" color="text.secondary">Total Client Data</Typography>
                                          <Typography variant="h6" fontWeight="medium" color="secondary.main">
                                            {(() => {
                                              // For consolidated clients, calculate total from products
                                              if (clientDetails[clientName]?.logs?.is_aggregated) {
                                                const products = clientDetails[clientName]?.logs?.by_product || [];
                                                const totalSize = products.reduce((sum, product) => sum + (product.size_gb || 0), 0);
                                                return formatDataSize(totalSize);
                                              }

                                              // Regular client - use cost data if available
                                              return formatDataSize(clientCostData[clientName]?.rsyslog_size_gb || 0);
                                            })()}
                                          </Typography>
                                          {fromDate && toDate && clientCostData[clientName]?.daily_data && (
                                            <Typography variant="caption" color="text.secondary">
                                              {`Aggregated over ${Object.keys(clientCostData[clientName].daily_data).length} days`}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Grid>
                                      <PermissionWidget permission={WIDGET_PERMISSIONS['total-cost']} fallback={null}>
                                        <Grid item xs={12} sm={6} md={4}>
                                          <Box sx={{
                                            p: 2,
                                            borderRadius: '12px',
                                            backgroundColor: alpha(theme.palette.success.main, 0.05),
                                            border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                                          }}>
                                            <Typography variant="body2" color="text.secondary">Total Cost</Typography>
                                            <Typography variant="h6" fontWeight="medium" sx={{ color: theme.palette.success.main }}>
                                              {(() => {
                                                // For consolidated clients, calculate cost based on size and cost per GB
                                                if (clientDetails[clientName]?.logs?.is_aggregated) {
                                                  const products = clientDetails[clientName]?.logs?.by_product || [];
                                                  const totalSize = products.reduce((sum, product) => sum + (product.size_gb || 0), 0);
                                                  const costPerGB = clientCostData[clientName]?.cost_per_gb || 0;
                                                  const totalCost = totalSize * costPerGB;
                                                  return `₹${totalCost.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                                                }

                                                // Regular client - use cost data
                                                return `₹${clientCostData[clientName]?.total_cost.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || "0"}`;
                                              })()}
                                            </Typography>
                                            {fromDate && toDate && (
                                              <Typography variant="caption" color="text.secondary">
                                                Across entire date range
                                              </Typography>
                                            )}
                                          </Box>
                                        </Grid>
                                      </PermissionWidget>

                                      {/* Add daily breakdown if available */}
                                      {fromDate && toDate && (
                                        <Grid item xs={12}>
                                          <Box sx={{
                                            mt: 2,
                                            p: 2,
                                            borderRadius: '12px',
                                            backgroundColor: alpha(theme.palette.info.main, 0.05),
                                            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                                          }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                              Daily Data Breakdown
                                            </Typography>
                                            <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell align="right">Size</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {(() => {
                                                    // Get date range
                                                    const startDate = new Date(fromDate);
                                                    const endDate = new Date(toDate);
                                                    const dateRange = [];

                                                    // Create array of dates in range
                                                    let currentDate = new Date(startDate);
                                                    while (currentDate <= endDate) {
                                                      dateRange.push(new Date(currentDate).toISOString().split('T')[0]);
                                                      currentDate.setDate(currentDate.getDate() + 1);
                                                    }

                                                    // Get daily data from client details if available
                                                    const dailyData = {};

                                                    // Check if this is a parent client from PARENT_PATTERNS
                                                    const isParent = PARENT_PATTERNS.includes(clientName);

                                                    // For consolidated clients, calculate from products
                                                    if (clientDetails[clientName]?.logs?.is_aggregated &&
                                                      clientDetails[clientName]?.logs?.by_product) {

                                                      const products = clientDetails[clientName].logs.by_product;
                                                      console.log(`Processing ${products.length} products for daily data`);

                                                      // Group products by date
                                                      products.forEach(product => {
                                                        const productDate = product.date || product.log_date;
                                                        if (productDate) {
                                                          if (!dailyData[productDate]) {
                                                            dailyData[productDate] = { size_gb: 0 };
                                                          }
                                                          dailyData[productDate].size_gb += (product.size_gb || 0);
                                                        }
                                                      });

                                                      console.log(`Generated daily data for ${Object.keys(dailyData).length} days:`, dailyData);
                                                    }

                                                    // by_product rows carry no per-day date, so aggregated clients (e.g. IFFCO)
                                                    // end up empty above; fall back to the same cost data the chart uses.
                                                    if (Object.keys(dailyData).length === 0 && clientCostData[clientName]?.daily_data) {
                                                      Object.entries(clientCostData[clientName].daily_data).forEach(([date, data]) => {
                                                        dailyData[date] = { size_gb: data.client_rsyslog_size || 0 };
                                                      });
                                                    }

                                                    // For parent clients, ALWAYS add data from all child clients
                                                    // regardless of whether we have data for the parent itself
                                                    if (isParent && clients && clients.length) {
                                                      // Find child clients directly without using getChildClients
                                                      const childClients = clients.filter(client => client && typeof client === 'string' ? client.startsWith(clientName) : client && client.name && client.name.startsWith(clientName));

                                                      console.log(`Found ${childClients.length} child clients for ${clientName}`);

                                                      // Process each child client
                                                      childClients.forEach(childName => {
                                                        // Skip if we don't have cost data for this child
                                                        if (!clientCostData[childName]?.daily_data) {
                                                          console.log(`No daily data available for child client ${childName}`);
                                                          return;
                                                        }

                                                        console.log(`Adding daily data from child client ${childName}`);

                                                        // Add child's daily data to parent's data
                                                        Object.entries(clientCostData[childName].daily_data).forEach(([date, data]) => {
                                                          if (!dailyData[date]) {
                                                            dailyData[date] = { size_gb: 0 };
                                                          }
                                                          const childSize = data.client_rsyslog_size || 0;
                                                          dailyData[date].size_gb += childSize;
                                                          console.log(`Added ${childSize.toFixed(2)} GB from ${childName} for date ${date}`);
                                                        });
                                                      });

                                                      console.log(`Final daily data for ${clientName} after adding child data:`, dailyData);
                                                    }

                                                    // Sort dates in descending order (newest first)
                                                    const sortedDates = [...dateRange].sort((a, b) => new Date(b) - new Date(a));

                                                    // For debugging
                                                    if (isParent) {
                                                      console.log(`Rendering daily data for parent ${clientName}:`, dailyData);
                                                    }

                                                    // Return table rows for each date in range
                                                    return sortedDates.map(date => {
                                                      const sizeValue = dailyData[date] ? dailyData[date].size_gb : 0;

                                                      return (
                                                        <TableRow key={date}>
                                                          <TableCell>{date}</TableCell>
                                                          <TableCell align="right">
                                                            {formatDataSize(sizeValue)}
                                                          </TableCell>
                                                        </TableRow>
                                                      );
                                                    });
                                                  })()}
                                                </TableBody>
                                              </Table>
                                            </Box>
                                          </Box>
                                        </Grid>
                                      )}

                                      {/* Cost Breakdown Section - Now wrapped with permission check */}
                                      <PermissionWidget permission={WIDGET_PERMISSIONS['client-cost-breakdown']} fallback={null}>
                                        <Grid item xs={12}>
                                          <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <MoneyIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                              Cost Breakdown
                                              <Tooltip title="All costs that contribute to total production cost">
                                                <InfoIcon fontSize="small" sx={{ color: alpha(theme.palette.info.main, 0.7), cursor: 'help' }} />
                                              </Tooltip>
                                            </Typography>

                                            {/* Display explanation of how costs are calculated */}
                                            <Box sx={{ mb: 2, p: 2, borderRadius: '12px', backgroundColor: alpha(theme.palette.background.paper, 0.8), border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                                              <Typography variant="body2" color="text.secondary">
                                                <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1, color: theme.palette.info.main }} />
                                                Cost breakdown shows the client's portion of each cost component.
                                                These costs are calculated based on the client's data size and the cost per GB.
                                              </Typography>
                                            </Box>

                                            <Grid container spacing={2}>
                                              {/* Compute Cost */}
                                              <PermissionWidget permission={WIDGET_PERMISSIONS['compute-cost']} fallback={null}>
                                                <Grid item xs={12} md={4}>
                                                  <Box sx={{
                                                    p: 2,
                                                    borderRadius: '12px',
                                                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                                                    border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                                                  }}>
                                                    <Typography variant="body2" color="text.secondary">Compute Cost</Typography>
                                                    <Typography variant="h6" fontWeight="medium" sx={{ color: theme.palette.info.main }}>
                                                      {(() => {
                                                        // Get client-specific compute cost
                                                        const totalRsyslogSize = clientCostData[clientName]?.debug?.total_rsyslog_size || 0;
                                                        const clientRsyslogSize = clientCostData[clientName]?.rsyslog_size_gb || 0;
                                                        let computeCost = 0;

                                                        if (clientCostData[clientName]?.debug?.daily_data) {
                                                          computeCost = Object.values(clientCostData[clientName].debug.daily_data)
                                                            .reduce((sum, day) => sum + (day.oci_costs?.compute_cost || 0), 0);
                                                        } else {
                                                          computeCost = clientCostData[clientName]?.cost_details?.compute_cost || 0;
                                                        }

                                                        // Calculate client portion
                                                        const computeCostPerGB = totalRsyslogSize > 0.1 ? computeCost / totalRsyslogSize : computeCost / 100;
                                                        const clientComputeCost = clientRsyslogSize * computeCostPerGB;

                                                        // For parent clients, also include child costs
                                                        let totalClientComputeCost = clientComputeCost;
                                                        const isParent = PARENT_PATTERNS.includes(clientName);

                                                        if (isParent) {
                                                          const childClients = clients.filter(client => client && typeof client === 'string' ? client.startsWith(clientName) : client && client.name && client.name.startsWith(clientName));
                                                          childClients.forEach(childName => {
                                                            const childSize = sumByClientSizeForCanonicalName(clientLogs?.by_client, childName);
                                                            if (childSize > 0) {
                                                              totalClientComputeCost += childSize * computeCostPerGB;
                                                            }
                                                          });
                                                        }

                                                        return `₹${totalClientComputeCost.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                                                      })()}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                      Client portion of compute costs
                                                    </Typography>
                                                  </Box>
                                                </Grid>
                                              </PermissionWidget>

                                              {/* Boot Volume Cost */}
                                              <PermissionWidget permission={WIDGET_PERMISSIONS['boot-volume-cost']} fallback={null}>
                                                <Grid item xs={12} md={4}>
                                                  <Box sx={{
                                                    p: 2,
                                                    borderRadius: '12px',
                                                    backgroundColor: alpha(theme.palette.warning.main, 0.05),
                                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                                                  }}>
                                                    <Typography variant="body2" color="text.secondary">Boot Volume Cost</Typography>
                                                    <Typography variant="h6" fontWeight="medium" sx={{ color: theme.palette.warning.main }}>
                                                      {(() => {
                                                        // Get client-specific boot volume cost
                                                        const totalRsyslogSize = clientCostData[clientName]?.debug?.total_rsyslog_size || 0;
                                                        const clientRsyslogSize = clientCostData[clientName]?.rsyslog_size_gb || 0;
                                                        let bootVolumeCost = 0;

                                                        if (clientCostData[clientName]?.debug?.daily_data) {
                                                          bootVolumeCost = Object.values(clientCostData[clientName].debug.daily_data)
                                                            .reduce((sum, day) => sum + (day.oci_costs?.boot_volume_cost || 0), 0);
                                                        } else {
                                                          bootVolumeCost = clientCostData[clientName]?.cost_details?.boot_volume_cost || 0;
                                                        }

                                                        // Calculate client portion
                                                        const bootVolumeCostPerGB = totalRsyslogSize > 0.1 ? bootVolumeCost / totalRsyslogSize : bootVolumeCost / 100;
                                                        const clientBootVolumeCost = clientRsyslogSize * bootVolumeCostPerGB;

                                                        // For parent clients, also include child costs
                                                        let totalClientBootVolumeCost = clientBootVolumeCost;
                                                        const isParent = PARENT_PATTERNS.includes(clientName);

                                                        if (isParent) {
                                                          const childClients = clients.filter(client => client && typeof client === 'string' ? client.startsWith(clientName) : client && client.name && client.name.startsWith(clientName));
                                                          childClients.forEach(childName => {
                                                            const childSize = sumByClientSizeForCanonicalName(clientLogs?.by_client, childName);
                                                            if (childSize > 0) {
                                                              totalClientBootVolumeCost += childSize * bootVolumeCostPerGB;
                                                            }
                                                          });
                                                        }

                                                        return `₹${totalClientBootVolumeCost.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                                                      })()}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                      Client portion of boot volume costs
                                                    </Typography>
                                                  </Box>
                                                </Grid>
                                              </PermissionWidget>

                                              {/* Block Volume Cost */}
                                              <PermissionWidget permission={WIDGET_PERMISSIONS['block-volume-cost']} fallback={null}>
                                                <Grid item xs={12} md={4}>
                                                  <Box sx={{
                                                    p: 2,
                                                    borderRadius: '12px',
                                                    backgroundColor: alpha(theme.palette.error.main, 0.05),
                                                    border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                                                  }}>
                                                    <Typography variant="body2" color="text.secondary">Block Volume Cost</Typography>
                                                    <Typography variant="h6" fontWeight="medium" sx={{ color: theme.palette.error.main }}>
                                                      {(() => {
                                                        // Get client-specific block volume cost
                                                        const totalRsyslogSize = clientCostData[clientName]?.debug?.total_rsyslog_size || 0;
                                                        const clientRsyslogSize = clientCostData[clientName]?.rsyslog_size_gb || 0;
                                                        let blockVolumeCost = 0;

                                                        if (clientCostData[clientName]?.debug?.daily_data) {
                                                          blockVolumeCost = Object.values(clientCostData[clientName].debug.daily_data)
                                                            .reduce((sum, day) => sum + (day.oci_costs?.block_volume_cost || 0), 0);
                                                        } else {
                                                          blockVolumeCost = clientCostData[clientName]?.cost_details?.block_volume_cost || 0;
                                                        }

                                                        // Calculate client portion
                                                        const blockVolumeCostPerGB = totalRsyslogSize > 0.1 ? blockVolumeCost / totalRsyslogSize : blockVolumeCost / 100;
                                                        const clientBlockVolumeCost = clientRsyslogSize * blockVolumeCostPerGB;

                                                        // For parent clients, also include child costs
                                                        let totalClientBlockVolumeCost = clientBlockVolumeCost;
                                                        const isParent = PARENT_PATTERNS.includes(clientName);

                                                        if (isParent) {
                                                          const childClients = clients.filter(client => client && typeof client === 'string' ? client.startsWith(clientName) : client && client.name && client.name.startsWith(clientName));
                                                          childClients.forEach(childName => {
                                                            const childSize = sumByClientSizeForCanonicalName(clientLogs?.by_client, childName);
                                                            if (childSize > 0) {
                                                              totalClientBlockVolumeCost += childSize * blockVolumeCostPerGB;
                                                            }
                                                          });
                                                        }

                                                        return `₹${totalClientBlockVolumeCost.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                                                      })()}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                      Client portion of block volume costs
                                                    </Typography>
                                                  </Box>
                                                </Grid>
                                              </PermissionWidget>
                                            </Grid>
                                          </Box>
                                        </Grid>
                                      </PermissionWidget>
                                    </Grid>
                                    {/* </PermissionWidget> */}
                                  </Box>
                                </Grid>
                              </Grid>
                            ) : (
                              <Box sx={{ textAlign: 'center', p: 2 }}>
                                <InfoIcon sx={{ color: alpha(theme.palette.text.secondary, 0.5), fontSize: 36, mb: 1 }} />
                                <Typography color="text.secondary">No details available for this client</Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Keep your existing dialogs and snackbar */}
        <Snackbar
          open={showError}
          autoHideDuration={6000}
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseError}
            severity="error"
            sx={{ width: '100%' }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>

        {/* More Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          sx={{
            '& .MuiPaper-root': {
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
              borderRadius: '12px',
              mt: 0.5,
            }
          }}
        >
          <MenuItem onClick={handleOpenLicenseDialog} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <AddIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
            </ListItemIcon>
            <ListItemText primary="Add License Details" />
          </MenuItem>
          <MenuItem onClick={handleOpenViewDetailsDialog} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <InfoIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
            </ListItemIcon>
            <ListItemText primary="View Details" />
          </MenuItem>
        </Menu>

        {/* Add License Dialog */}
        <Dialog
          open={openLicenseDialog}
          onClose={handleCloseLicenseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <DialogTitle sx={{
            pb: 1,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold',
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              <LocalOfferIcon />
              License Details: {renderClientName(selectedClient)}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {loadingLicense ? (
              <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Start Date"
                    type="date"
                    fullWidth
                    value={licenseData.startDate}
                    onChange={(e) => handleLicenseDataChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: '10px' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="End Date"
                    type="date"
                    fullWidth
                    value={licenseData.endDate}
                    onChange={(e) => handleLicenseDataChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: '10px' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Associated Log Data (GB)"
                    type="number"
                    fullWidth
                    value={licenseData.associatedLogData}
                    onChange={(e) => handleLicenseDataChange('associatedLogData', e.target.value)}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: '10px' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <StorageIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Threshold GB/Day"
                    type="number"
                    fullWidth
                    value={licenseData.licensePerDay}
                    onChange={(e) => handleLicenseDataChange('licensePerDay', e.target.value)}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: '10px' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Online Retention (months)"
                    type="number"
                    fullWidth
                    value={licenseData.onlineRetention}
                    onChange={(e) => handleLicenseDataChange('onlineRetention', e.target.value)}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: '10px' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <StorageIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Offline Retention (months)"
                    type="number"
                    fullWidth
                    value={licenseData.offlineRetention}
                    onChange={(e) => handleLicenseDataChange('offlineRetention', e.target.value)}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: '10px' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <StorageIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Devices to Integrate"
                    type="number"
                    fullWidth
                    value={licenseData.devicesToIntegrate}
                    onChange={(e) => handleLicenseDataChange('devicesToIntegrate', e.target.value)}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: '10px' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <DevicesIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel sx={{ 
                      '&.Mui-focused': { 
                        color: theme.palette.primary.main 
                      } 
                    }}>
                      Engagement Type
                    </InputLabel>
                    <Select
                      value={licenseData.engagementType || ''}
                      onChange={(e) => handleLicenseDataChange('engagementType', e.target.value)}
                      label="Engagement Type"
                      sx={{ 
                        borderRadius: '10px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.divider,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                      startAdornment={
                        <InputAdornment position="start" sx={{ ml: 1 }}>
                          <BusinessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Trend Micro Direct">Trend Micro Direct</MenuItem>
                      <MenuItem value="Trend Micro Indirect">Trend Micro Indirect</MenuItem>
                      <MenuItem value="BO Direct">BO Direct</MenuItem>
                      <MenuItem value="BO Indirect">BO Indirect</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel sx={{ 
                      '&.Mui-focused': { 
                        color: theme.palette.primary.main 
                      } 
                    }}>
                      Engagement Model
                    </InputLabel>
                    <Select
                      value={licenseData.engagementModel || ''}
                      onChange={(e) => handleLicenseDataChange('engagementModel', e.target.value)}
                      label="Engagement Model"
                      sx={{ 
                        borderRadius: '10px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.divider,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                      startAdornment={
                        <InputAdornment position="start" sx={{ ml: 1 }}>
                          <BusinessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="MXDR">MXDR</MenuItem>
                      <MenuItem value="Third party">Third party</MenuItem>
                      <MenuItem value="Both">Both</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCloseLicenseDialog}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveLicenseData}
              variant="contained"
              disabled={savingLicense || loadingLicense}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                px: 3,
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.8),
                },
              }}
            >
              {savingLicense ? 'Saving...' : 'Save License Details'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog
          open={openViewDetailsDialog}
          onClose={handleCloseViewDetailsDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <DialogTitle sx={{
            pb: 1,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold',
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon />
              Client Details: {renderClientName(selectedClient)}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {loadingLicense ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3} sx={{ mt: 0 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    License Information
                  </Typography>
                  <Box sx={{
                    borderRadius: '12px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    p: 2,
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Start Date</Typography>
                        <Typography variant="body1">{formatDateTime(licenseData.startDate, true)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">End Date</Typography>
                        <Typography variant="body1">{formatDateTime(licenseData.endDate, true)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Associated Log Data</Typography>
                        <Typography variant="body1">{licenseData.associatedLogData || "Not specified"}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Threshold GB/Day</Typography>
                        <Typography variant="body1">{licenseData.licensePerDay || "Not specified"}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Online Retention(Months)</Typography>
                        <Typography variant="body1">{licenseData.onlineRetention || "Not specified"}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Offline Retention(Months)</Typography>
                        <Typography variant="body1">{licenseData.offlineRetention || "Not specified"}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Devices to Integrate</Typography>
                        <Typography variant="body1">{licenseData.devicesToIntegrate || "Not specified"}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Engagement Type</Typography>
                        <Typography variant="body1">
                          {licenseData.engagementType || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Engagement Model</Typography>
                        <Typography variant="body1">
                          {licenseData.engagementModel || "Not specified"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCloseViewDetailsDialog}
              variant="contained"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                px: 3,
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.8),
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={showSuccess}
          autoHideDuration={6000}
          onClose={() => setShowSuccess(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowSuccess(false)}
            severity="success"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {successMessage}
          </Alert>
        </Snackbar>

        {/* Download confirmation dialog */}
        <Dialog
          open={downloadConfirmOpen}
          onClose={handleCloseDownloadConfirm}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              padding: '8px'
            }
          }}
        >
          <DialogTitle sx={{
            fontWeight: 600,
            fontSize: '1.25rem',
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: 2
          }}>
            Download Options
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {downloadTarget ?
                `Please select the download format for ${getFullClientName(downloadTarget)}:` :
                'Please select the download format for all clients:'}
            </Typography>

            <Stack spacing={2}>
              <Box sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '12px',
                p: 2,
                mb: 1,
                cursor: 'pointer',
                bgcolor: downloadType === 'product' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                borderColor: downloadType === 'product' ? theme.palette.primary.main : theme.palette.divider,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                }
              }}
                onClick={() => setDownloadType('product')}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}>
                    <LocalOfferIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500}>Product-wise</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Download data organized by product for each client
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '12px',
                p: 2,
                cursor: 'pointer',
                bgcolor: downloadType === 'client' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                borderColor: downloadType === 'client' ? theme.palette.primary.main : theme.palette.divider,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                }
              }}
                onClick={() => setDownloadType('client')}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.main
                  }}>
                    <DeviceHubIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500}>Client-wise</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Download consolidated data by client without product breakdown
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              variant="outlined"
              onClick={handleCloseDownloadConfirm}
              sx={{ borderRadius: '10px' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={processDownload}
              startIcon={<DownloadIcon />}
              sx={{
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              Download
            </Button>
          </DialogActions>
        </Dialog>

        {/* Product Device IDs Popup */}
        <Dialog
          open={productDevicePopup.open}
          onClose={handleCloseProductDevicePopup}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 2
          }}>
            <DevicesIcon sx={{ color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Device IDs for {productDevicePopup.productName}
                {productDevicePopup.deviceData && productDevicePopup.deviceData.length > 0 && (
                  <Chip
                    label={productDevicePopup.deviceData.length}
                    size="small"
                    sx={{
                      ml: 1,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                      height: 20,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Client: {getFullClientName(productDevicePopup.clientName)}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {productDevicePopup.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : productDevicePopup.deviceData.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Device ID</TableCell>
                        <TableCell align="right">Size</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productDevicePopup.deviceData.map((device, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.07)}`
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: getColor(index, 0.8)
                                }}
                              />
                              <Typography variant="body2" fontWeight="medium">
                                {device.device_id}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium" sx={{ color: getColor(index, 1) }}>
                              {formatDataSize(device.total_size_gb)}
                            </Typography>
                          </TableCell>

                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Summary footer */}
                {productDevicePopup.deviceData.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      Total Size:
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ color: theme.palette.primary.main }}
                    >
                      {formatDataSize(productDevicePopup.deviceData.reduce((sum, device) => sum + (device.total_size_gb || 0), 0))}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <InfoIcon sx={{ color: alpha(theme.palette.text.secondary, 0.5), fontSize: 48, mb: 1 }} />
                <Typography color="text.secondary">No device IDs found for this product</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, p: 2 }}>
            <Button
              onClick={handleCloseProductDevicePopup}
              variant="contained"
              startIcon={<CloseIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 'medium'
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* RSYSLOG Data Detail Popup */}
      <Dialog
        open={rsyslogDetailPopup.open}
        onClose={handleCloseRsyslogDetailPopup}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
            backgroundColor: '#1e2732', // Darker background matching app theme
            backgroundImage: `linear-gradient(145deg, ${alpha('#1e2732', 0.97)} 0%, ${alpha('#141b24', 0.95)} 100%)`,
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StorageIcon sx={{ color: theme.palette.primary.light, mr: 1.5 }} />
            <Typography variant="h6" fontWeight="medium" sx={{ color: 'white' }}>RSYSLOG Data Details</Typography>
          </Box>
          <IconButton onClick={handleCloseRsyslogDetailPopup} size="small" sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {fromDate && toDate && fromDate !== toDate &&
            totalProductionCostData.daily_rsyslog_data &&
            totalProductionCostData.daily_rsyslog_data.length > 1 ? (
            <Box sx={{ position: 'relative', height: 400, width: '100%' }}>
              {/* Header with title and info card side by side */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ color: 'white' }}>
                  Daily RSYSLOG Size Distribution
                </Typography>

                {/* Total RSYSLOG Size Info Box - Positioned at top */}
                <Card
                  elevation={3}
                  sx={{
                    width: 350,
                    borderRadius: '12px',
                    backgroundColor: '#141b24',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="medium" gutterBottom sx={{ color: theme.palette.grey[300] }}>
                      Total RSYSLOG Size
                    </Typography>
                    <Typography variant="h4" color="primary" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {formatDataSize(totalProductionCostData.rsyslog_size)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, color: theme.palette.grey[400] }}>
                      {fromDate && toDate ?
                        `From ${fromDate} to ${toDate}` :
                        "Today only"}
                    </Typography>

                    {totalProductionCostData.cost_per_gb > 0 && (
                      <>
                        <Divider sx={{ my: 0.1, borderColor: alpha(theme.palette.divider, 0.1) }} />
                        {/* <Typography variant="caption" sx={{ color: theme.palette.grey[400] }}>
                        Cost Per GB: <strong style={{ color: theme.palette.grey[200] }}>₹{totalProductionCostData.cost_per_gb.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                      </Typography> */}
                        {/* <Typography variant="caption" sx={{ display: 'block', color: theme.palette.grey[400] }}>
                        Total Cost: <strong style={{ color: theme.palette.grey[200] }}>₹{(totalProductionCostData.rsyslog_size * totalProductionCostData.cost_per_gb).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                      </Typography> */}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Box>

              {/* Main Chart - Full width and height */}
              <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
                <Bar
                  data={{
                    labels: totalProductionCostData.daily_rsyslog_data.map(item => item.date),
                    datasets: [
                      {
                        label: 'RSYSLOG Size (GB)',
                        data: totalProductionCostData.daily_rsyslog_data.map(item => item.size_gb),
                        backgroundColor: theme.palette.primary.main,
                        borderColor: theme.palette.primary.dark,
                        borderWidth: 1,
                        borderRadius: 4,
                        barThickness: 'flex',
                        maxBarThickness: 30,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          font: {
                            weight: 'bold'
                          },
                          color: 'white'
                        }
                      },
                      tooltip: {
                        backgroundColor: alpha(theme.palette.common.black, 0.85),
                        titleFont: {
                          size: 14,
                          weight: 'bold'
                        },
                        bodyFont: {
                          size: 13
                        },
                        padding: 12,
                        cornerRadius: 8,
                        callbacks: {
                          title: (tooltipItems) => {
                            return `Date: ${tooltipItems[0].label}`;
                          },
                          label: (tooltipItem) => {
                            return `Size: ${formatDataSize(tooltipItem.raw)}`;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false,
                          color: alpha(theme.palette.grey[700], 0.2)
                        },
                        ticks: {
                          maxRotation: 0,
                          autoSkip: true,
                          maxTicksLimit: 20,
                          color: theme.palette.grey[400]
                        }
                      },
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Size (GB)',
                          font: {
                            weight: 'bold'
                          },
                          color: 'white'
                        },
                        grid: {
                          color: alpha(theme.palette.grey[700], 0.2)
                        },
                        ticks: {
                          color: theme.palette.grey[400]
                        }
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300 }}>
              <DateRangeIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.5), mb: 2 }} />
              <Typography variant="h6" fontWeight="medium" align="center" sx={{ color: 'white' }}>
                Select a date range to view daily distribution
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, color: theme.palette.grey[400] }}>
                Current data: {formatDataSize(totalProductionCostData.rsyslog_size)} {fromDate && toDate ? `(${fromDate} to ${toDate})` : "(today only)"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
          <Button onClick={handleCloseRsyslogDetailPopup} variant="outlined" sx={{
            color: 'white',
            borderColor: alpha('#ffffff', 0.3),
            '&:hover': {
              borderColor: 'white',
              backgroundColor: alpha('#ffffff', 0.1)
            }
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Production Cost Detail Popup */}
      <Dialog
        open={productionCostDetailPopup.open}
        onClose={handleCloseProductionCostDetailPopup}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
            backgroundColor: '#1e2732', // Darker background matching app theme
            backgroundImage: `linear-gradient(145deg, ${alpha('#1e2732', 0.97)} 0%, ${alpha('#141b24', 0.95)} 100%)`,
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MoneyIcon sx={{ color: theme.palette.success.light, mr: 1.5 }} />
            <Typography variant="h6" fontWeight="medium" sx={{ color: 'white' }}>Production Cost Details</Typography>
          </Box>
          <IconButton onClick={handleCloseProductionCostDetailPopup} size="small" sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {fromDate && toDate && fromDate !== toDate &&
            totalProductionCostData.daily_data &&
            Object.keys(totalProductionCostData.daily_data).length > 1 ? (
            <Box sx={{ position: 'relative', height: 400, width: '100%' }}>
              {/* Header with title and info card side by side */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ color: 'white' }}>
                  Daily Cost Distribution
                </Typography>

                {/* Total Production Cost Info Box - Positioned at top */}
                <Card
                  elevation={3}
                  sx={{
                    width: 450,
                    borderRadius: '12px',
                    backgroundColor: '#141b24',
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.15)}`,
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="medium" gutterBottom sx={{ color: theme.palette.grey[300] }}>
                      Total Production Cost
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h4" color="success.main" fontWeight="bold" sx={{ mb: 0.5 }}>
                          ₹{totalProductionCostData.production_cost.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', color: theme.palette.grey[400] }}>
                          {fromDate && toDate ?
                            `From ${fromDate} to ${toDate}` :
                            "Today only"}
                        </Typography>
                      </Box>

                      {totalProductionCostData.cost_details && (
                        <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                          <Typography variant="caption" sx={{ display: 'block', color: theme.palette.grey[400] }}>
                            Compute: <strong style={{ color: theme.palette.grey[200] }}>₹{totalProductionCostData.cost_details.compute_cost.toLocaleString('en-IN')}</strong>
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: theme.palette.grey[400] }}>
                            Boot Vol: <strong style={{ color: theme.palette.grey[200] }}>₹{totalProductionCostData.cost_details.boot_volume_cost.toLocaleString('en-IN')}</strong>
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: theme.palette.grey[400] }}>
                            Block Vol: <strong style={{ color: theme.palette.grey[200] }}>₹{totalProductionCostData.cost_details.block_volume_cost.toLocaleString('en-IN')}</strong>
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {/* Main Chart - Full width and height */}
              <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
                <Bar
                  data={{
                    labels: Object.keys(totalProductionCostData.daily_data).sort(),
                    datasets: [
                      {
                        label: 'Compute Cost',
                        data: Object.keys(totalProductionCostData.daily_data).sort().map(date =>
                          totalProductionCostData.daily_data[date].compute_cost || 0
                        ),
                        backgroundColor: theme.palette.info.main,
                        borderColor: theme.palette.info.dark,
                        borderWidth: 1,
                        borderRadius: 4,
                        stack: 'stack0',
                      },
                      {
                        label: 'Boot Volume Cost',
                        data: Object.keys(totalProductionCostData.daily_data).sort().map(date =>
                          totalProductionCostData.daily_data[date].boot_volume_cost || 0
                        ),
                        backgroundColor: theme.palette.warning.main,
                        borderColor: theme.palette.warning.dark,
                        borderWidth: 1,
                        borderRadius: 4,
                        stack: 'stack0',
                      },
                      {
                        label: 'Block Volume Cost',
                        data: Object.keys(totalProductionCostData.daily_data).sort().map(date =>
                          totalProductionCostData.daily_data[date].block_volume_cost || 0
                        ),
                        backgroundColor: theme.palette.error.main,
                        borderColor: theme.palette.error.dark,
                        borderWidth: 1,
                        borderRadius: 4,
                        stack: 'stack0',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          font: {
                            weight: 'bold'
                          },
                          color: 'white'
                        }
                      },
                      tooltip: {
                        backgroundColor: alpha(theme.palette.common.black, 0.85),
                        titleFont: {
                          size: 14,
                          weight: 'bold'
                        },
                        bodyFont: {
                          size: 13
                        },
                        padding: 12,
                        cornerRadius: 8,
                        callbacks: {
                          title: (tooltipItems) => {
                            return `Date: ${tooltipItems[0].label}`;
                          },
                          label: (tooltipItem) => {
                            return `${tooltipItem.dataset.label}: ₹${tooltipItem.raw.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false,
                          color: alpha(theme.palette.grey[700], 0.2)
                        },
                        ticks: {
                          maxRotation: 0,
                          autoSkip: true,
                          maxTicksLimit: 20,
                          color: theme.palette.grey[400]
                        }
                      },
                      y: {
                        beginAtZero: true,
                        stacked: true,
                        title: {
                          display: true,
                          text: 'Cost (₹)',
                          font: {
                            weight: 'bold'
                          },
                          color: 'white'
                        },
                        grid: {
                          color: alpha(theme.palette.grey[700], 0.2)
                        },
                        ticks: {
                          color: theme.palette.grey[400]
                        }
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300 }}>
              <DateRangeIcon sx={{ fontSize: 60, color: alpha(theme.palette.success.main, 0.5), mb: 2 }} />
              <Typography variant="h6" fontWeight="medium" align="center" sx={{ color: 'white' }}>
                Select a date range to view daily cost distribution
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, color: theme.palette.grey[400] }}>
                Current cost: ₹{totalProductionCostData.production_cost.toLocaleString('en-IN', { maximumFractionDigits: 2 })} {fromDate && toDate ? `(${fromDate} to ${toDate})` : "(today only)"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
          <Button onClick={handleCloseProductionCostDetailPopup} variant="outlined" sx={{
            color: 'white',
            borderColor: alpha('#ffffff', 0.3),
            '&:hover': {
              borderColor: 'white',
              backgroundColor: alpha('#ffffff', 0.1)
            }
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>


    </LoadingOverlay>
  );
}
